import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type HubConnection,
} from "@microsoft/signalr";
import * as THREE from "three";
import { mountFriends } from "./friends";
import { DestructionEffects } from "./destructionEffects";
import { BuildingDamage, buildingDamageStage } from "./buildingDamage";
import { ServerRate, SnapshotMotion } from "./snapshotTiming";
import { createSoldier, createOnager, createBombardCannon, animateUnitModel } from "./unitVisuals";
import { createBuildingVisual, createDefensiveRange, createStoneFortification } from "./buildingVisuals";
import {
  AGE_COSTS,
  AGE_NAMES,
  BUILDING_DEFS,
  NAVIGATION_CELL_SIZE,
  RESOURCE_LABELS,
  SIM_TICK_RATE,
  SNAPSHOT_RATE,
  TERRAIN_TILE_SIZE,
  UNIT_DEFS,
  VISION_CELL_SIZE,
  formatCost,
  hasResources,
} from "./shared/config";
import type {
  BuildingState,
  BuildingType,
  ChatMessage,
  CommandPayload,
  GameEvent,
  GameSnapshot,
  LobbyState,
  MapType,
  MatchMilestone,
  MatchReport,
  MatchTimelinePoint,
  OperationResult,
  ResourceKind,
  ResourceNodeState,
  TerrainTileState,
  UnitState,
  UnitStance,
  UnitType,
  Vec2,
} from "./shared/types";
import "./style.css";

type RenderState = UnitState | BuildingState | ResourceNodeState;

const MENU_MUSIC_STORAGE_KEY = "throne-and-timber-menu-music";

function savedMenuMusicEnabled(): boolean {
  try {
    return localStorage.getItem(MENU_MUSIC_STORAGE_KEY) !== "off";
  } catch {
    return true;
  }
}

const SOUND_FILES = {
  ageUp: "/assets/audio/aoe2/age-up.wav",
  ambience: "/assets/audio/ambience-woodland.mp3",
  archer: "/assets/audio/aoe2/arrow-shot.wav",
  build: "/assets/audio/aoe2/briton-build.wav",
  cancel: "/assets/audio/ui-cancel.mp3",
  cannon: ["/assets/audio/aoe2/cannon-fire-1.wav", "/assets/audio/aoe2/cannon-fire-2.wav"],
  selectOnager: "/assets/audio/aoe/stonethrower_select1.wav",
  click: "/assets/audio/aoe2/click.wav",
  error: "/assets/audio/aoe2/error.wav",
  marketBuy: "/assets/audio/aoe2/market-buy.wav",
  marketSell: "/assets/audio/aoe2/market-sell.wav",
  defeat: "/assets/audio/defeat.mp3",
  gatherFood: "/assets/audio/aoe2/briton-forage.wav",
  gatherFarm: "/assets/audio/aoe2/briton-farm.wav",
  gatherGold: "/assets/audio/aoe2/briton-mine.wav",
  gatherStone: "/assets/audio/aoe2/briton-mine.wav",
  gatherWood: "/assets/audio/aoe2/briton-chop.wav",
  garrison: "/assets/audio/garrison.mp3",
  melee: "/assets/audio/aoe2/melee-clash.wav",
  move: [1, 2, 3].map(i => `/assets/audio/aoe2/briton-worker-move-${i}.wav`),
  moveSoldier: [1, 2, 3].map(i => `/assets/audio/aoe2/briton-soldier-move-${i}.wav`),
  moveSiege: "/assets/audio/aoe2/siege-move.wav",
  attackOrder: [1, 2].map(i => `/assets/audio/aoe2/briton-soldier-attack-${i}.wav`),
  rally: "/assets/audio/aoe2/rally.wav",
  repair: "/assets/audio/aoe2/briton-repair.wav",
  selectVillager: [1, 2, 3].map(i => `/assets/audio/aoe2/briton-worker-select-${i}.wav`),
  selectSoldier: [1, 2, 3].map(i => `/assets/audio/aoe2/briton-soldier-select-${i}.wav`),
  selectCavalry: "/assets/audio/aoe/cavalry_select1.wav",
  selectSiege: "/assets/audio/aoe2/select-cannon.wav",
  selectTown: "/assets/audio/aoe2/select-town.wav",
  selectCamp: "/assets/audio/aoe2/select-camp.wav",
  selectHouse: "/assets/audio/aoe/building_house.wav",
  selectBarracks: "/assets/audio/aoe2/select-barracks.wav",
  selectRange: "/assets/audio/aoe/building_archeryrange.wav",
  selectStable: "/assets/audio/aoe2/select-stable.wav",
  selectTower: "/assets/audio/aoe/building_tower.wav",
  selectWall: "/assets/audio/aoe/building_wall.wav",
  selectFarm: "/assets/audio/aoe/building_farm.wav",
  selectMarket: "/assets/audio/aoe/building_market.wav",
  select: "/assets/audio/ui-select.mp3",
  trainCavalry: "/assets/audio/train-cavalry.mp3",
  trainSoldier: "/assets/audio/aoe/soldier_train1.wav",
  trainVillager: "/assets/audio/aoe/villager_train1.wav",
  underAttack: "/assets/audio/under-attack.mp3",
  ungarrison: "/assets/audio/ungarrison.mp3",
  victory: "/assets/audio/victory.mp3",
} as const;

type SoundKey = keyof typeof SOUND_FILES;
const SELECTION_SOUNDS: Partial<Record<UnitType | BuildingType, SoundKey>> = {
  villager: "selectVillager", spearman: "selectSoldier", archer: "selectSoldier",
  skirmisher: "selectSoldier", royal_guard: "selectSoldier",
  knight: "selectCavalry", scout: "selectCavalry", bombard_cannon: "selectSiege", onager: "selectOnager",
  town_center: "selectTown", camp: "selectCamp", house: "selectHouse",
  barracks: "selectBarracks", archery_range: "selectRange", stable: "selectStable",
  tower: "selectTower", wall: "selectWall", gate: "selectWall", farm: "selectFarm", market: "selectMarket",
};

class SoundDesign {
  private context?: AudioContext;
  private master?: GainNode;
  private muted = false;
  private readonly buffers = new Map<SoundKey, AudioBuffer[]>();
  private readonly sampleVariant = new Map<SoundKey, number>();
  private readonly lastSampleTime = new Map<SoundKey, number>();
  private acknowledgement?: AudioBufferSourceNode;
  private preloadPromise?: Promise<void>;
  private ambienceSource?: AudioBufferSourceNode;
  private ambienceGain?: GainNode;
  private menuActive = true;
  private menuMusicEnabled = savedMenuMusicEnabled();

  get isMuted(): boolean {
    return this.muted;
  }

  get isMenuMusicEnabled(): boolean {
    return this.menuMusicEnabled;
  }

  async unlock(): Promise<void> {
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.gain.value = this.muted ? 0 : 0.34;
      this.master.connect(this.context.destination);
    }
    if (this.context.state === "suspended") await this.context.resume();
    this.preloadPromise ??= this.preloadSamples();
    void this.preloadPromise;
  }

  toggle(): boolean {
    this.muted = !this.muted;
    if (this.master && this.context) {
      this.master.gain.setTargetAtTime(
        this.muted ? 0 : 0.34,
        this.context.currentTime,
        0.015,
      );
    }
    return this.muted;
  }

  setMenuMusicEnabled(enabled: boolean): void {
    this.menuMusicEnabled = enabled;
    try {
      localStorage.setItem(MENU_MUSIC_STORAGE_KEY, enabled ? "on" : "off");
    } catch {
      // The current tab still honours the setting if storage is unavailable.
    }
    if (enabled) this.startAmbience();
    this.updateAmbienceGain();
  }

  setMenuActive(active: boolean): void {
    this.menuActive = active;
    if (active && this.menuMusicEnabled) this.startAmbience();
    this.updateAmbienceGain();
  }

  select(type?: UnitType | BuildingType): void {
    if (this.sample(type ? SELECTION_SOUNDS[type] ?? "select" : "select", 0.42)) return;
    this.tone(520, 0.055, "triangle", 0.18, 660);
  }

  click(): void {
    if (this.sample("click", 0.34)) return;
    this.tone(430, 0.04, "triangle", 0.1, 520);
  }

  death(): void {
    // Original synthesised falling cry and impact, respecting the master mute.
    this.tone(240, .48, "sawtooth", .09, 65);
    this.tone(460, .3, "triangle", .035, 110);
    this.noise(.22, .08, 650);
  }

  cancel(): void {
    if (this.sample("cancel", 0.42)) return;
    this.tone(240, 0.09, "sawtooth", 0.1, 150);
  }

  move(type?: UnitType): void {
    const key = !type || type === "villager" ? "move" : type === "bombard_cannon" || type === "onager" ? "moveSiege" : "moveSoldier";
    if (this.sample(key, 0.42)) return;
    this.tone(240, 0.09, "sine", 0.18, 180);
  }

  gather(kind: ResourceKind): void {
    const sample = {
      food: "gatherFood",
      wood: "gatherWood",
      gold: "gatherGold",
      stone: "gatherStone",
    }[kind] as SoundKey;
    if (this.sample(sample, 0.4)) return;
    const pitch = { food: 460, wood: 260, gold: 760, stone: 610 }[kind];
    this.tone(pitch, 0.055, "square", 0.12, pitch * 0.76);
    window.setTimeout(
      () => this.tone(pitch * 1.16, 0.045, "triangle", 0.09, pitch),
      55,
    );
  }

  build(): void {
    if (this.sample("build", 0.44)) return;
    this.noise(0.09, 0.1, 450);
    this.tone(115, 0.12, "triangle", 0.2, 78);
  }

  farm(): void { if (!this.sample("gatherFarm", .4)) this.gather("food"); }

  attackOrder(): void { if (!this.sample("attackOrder", .42)) this.click(); }

  trade(direction: "buy" | "sell"): void {
    if (!this.sample(direction === "buy" ? "marketBuy" : "marketSell", .42)) this.click();
  }

  repair(): void {
    if (this.sample("repair", 0.4)) return;
    this.build();
  }

  train(type: UnitType): void {
    const sample: SoundKey =
      type === "villager"
        ? "trainVillager"
        : type === "scout" || type === "knight"
          ? "trainCavalry"
          : "trainSoldier";
    if (this.sample(sample, 0.46)) return;
    this.complete();
  }

  ageUp(): void {
    if (this.sample("ageUp", 0.58)) return;
    this.chord([262, 330, 392, 523], 0.52, 0.1);
  }

  complete(): void {
    this.chord([392, 494, 587], 0.28, 0.1);
  }

  warning(): void {
    if (this.sample("error", .42)) return;
    this.tone(210, 0.16, "sawtooth", 0.14, 120);
  }

  combat(ranged = false): void {
    if (this.sample(ranged ? "archer" : "melee", 0.4)) return;
    this.noise(0.07, 0.08, 1000);
    this.tone(145, 0.08, "square", 0.1, 95);
  }

  cannon(): void {
    if (this.sample("cannon", 0.58)) return;
    this.noise(0.24, 0.18, 480);
    this.tone(72, 0.32, "sawtooth", 0.2, 38);
  }

  onager(impact = false): void {
    // Timber/rope release and a rough stone impact, distinct from gunpowder.
    this.noise(impact ? .24 : .12, impact ? .14 : .08, impact ? 700 : 1700);
    this.tone(impact ? 95 : 220, impact ? .18 : .22, "triangle", .09, impact ? 40 : 85);
  }

  rally(): void { if (!this.sample("rally", .4)) this.click(); }

  garrison(): void {
    if (this.sample("garrison", 0.42)) return;
    this.tone(180, 0.13, "triangle", 0.12, 260);
  }

  ungarrison(): void {
    if (this.sample("ungarrison", 0.42)) return;
    this.tone(310, 0.11, "triangle", 0.12, 190);
  }

  stance(): void {
    this.tone(330, 0.08, "square", 0.08, 440);
    window.setTimeout(() => this.tone(520, 0.07, "triangle", 0.07), 55);
  }

  warDeclaration(): void {
    if (this.sample("underAttack", 0.72)) return;
    this.noise(0.16, 0.06, 620);
    this.tone(196, 0.58, "sawtooth", 0.13, 147);
    window.setTimeout(
      () => this.tone(220, 0.72, "sawtooth", 0.15, 165),
      460,
    );
  }

  victory(): void {
    if (this.sample("victory", 0.68)) return;
    this.chord([262, 330, 392, 523], 0.7, 0.11);
  }

  defeat(): void {
    if (this.sample("defeat", 0.62)) return;
    this.chord([220, 185, 147], 0.62, 0.1);
  }

  private async preloadSamples(): Promise<void> {
    if (!this.context) return;
    const context = this.context;
    const downloads = new Map<string, Promise<AudioBuffer>>();
    const load = (url: string) => {
      let pending = downloads.get(url);
      if (!pending) {
        pending = fetch(url).then(async response => {
          if (!response.ok) throw new Error(`Audio unavailable: ${url}`);
          return context.decodeAudioData(await response.arrayBuffer());
        });
        downloads.set(url, pending);
      }
      return pending;
    };
    await Promise.all(
      (Object.entries(SOUND_FILES) as [SoundKey, string | readonly string[]][]).map(
        async ([key, urls]) => {
          const results = await Promise.allSettled((typeof urls === "string" ? [urls] : urls).map(load));
          const buffers = results.flatMap(result => result.status === "fulfilled" ? [result.value] : []);
          // One unavailable variant must not silence the other variants.
          if (buffers.length) this.buffers.set(key, buffers);
        },
      ),
    );
    this.startAmbience();
  }

  private startAmbience(): void {
    if (
      this.ambienceSource ||
      !this.context ||
      !this.master ||
      !this.menuMusicEnabled
    ) {
      return;
    }
    const buffer = this.buffers.get("ambience")?.[0];
    if (!buffer) return;
    const source = this.context.createBufferSource();
    const gain = this.context.createGain();
    source.buffer = buffer;
    source.loop = true;
    gain.gain.value = this.menuActive ? 0.04 : 0;
    source.connect(gain);
    gain.connect(this.master);
    source.addEventListener("ended", () => {
      if (this.ambienceSource === source) {
        this.ambienceSource = undefined;
        this.ambienceGain = undefined;
      }
    });
    source.start();
    this.ambienceSource = source;
    this.ambienceGain = gain;
  }

  private updateAmbienceGain(): void {
    if (!this.ambienceGain || !this.context) return;
    this.ambienceGain.gain.setTargetAtTime(
      this.menuMusicEnabled && this.menuActive ? 0.04 : 0,
      this.context.currentTime,
      0.08,
    );
  }

  private sample(key: SoundKey, volume: number, loop = false): boolean {
    if (!this.context || !this.master) return false;
    const variants = this.buffers.get(key);
    if (!variants?.length) return false;
    const now = this.context.currentTime;
    if (now - (this.lastSampleTime.get(key) ?? -1) < .14) return true;
    this.lastSampleTime.set(key, now);
    const variant = (this.sampleVariant.get(key) ?? -1) + 1;
    this.sampleVariant.set(key, variant % variants.length);
    const source = this.context.createBufferSource();
    const gain = this.context.createGain();
    source.buffer = variants[variant % variants.length];
    source.loop = loop;
    gain.gain.value = volume;
    source.connect(gain);
    gain.connect(this.master);
    if (key.startsWith("select") || key.startsWith("move") || key.startsWith("gather") || key === "repair" || key === "build" || key === "attackOrder") {
      this.acknowledgement?.stop();
      this.acknowledgement = source;
      source.addEventListener("ended", () => { if (this.acknowledgement === source) this.acknowledgement = undefined; });
    }
    source.addEventListener("ended", () => { source.disconnect(); gain.disconnect(); });
    source.start();
    return true;
  }

  private tone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    endFrequency = frequency,
  ): void {
    if (!this.context || !this.master || this.muted) return;
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(
      Math.max(20, endFrequency),
      now + duration,
    );
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(volume, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(this.master);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  }

  private chord(frequencies: number[], duration: number, volume: number): void {
    frequencies.forEach((frequency, index) =>
      window.setTimeout(
        () => this.tone(frequency, duration, "triangle", volume, frequency * 1.01),
        index * 42,
      ),
    );
  }

  private noise(duration: number, volume: number, cutoff: number): void {
    if (!this.context || !this.master || this.muted) return;
    const frameCount = Math.ceil(this.context.sampleRate * duration);
    const buffer = this.context.createBuffer(1, frameCount, this.context.sampleRate);
    const channel = buffer.getChannelData(0);
    for (let index = 0; index < frameCount; index += 1) {
      channel[index] = (Math.random() * 2 - 1) * (1 - index / frameCount);
    }
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    source.buffer = buffer;
    filter.type = "lowpass";
    filter.frequency.value = cutoff;
    gain.gain.value = volume;
    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    source.start();
  }
}

const soundDesign = new SoundDesign();

const app = document.querySelector<HTMLDivElement>("#app")!;
app.innerHTML = `
  <main id="menu-screen" class="screen menu-screen">
    <section id="account-panel" class="account-panel" aria-label="Commander account">
      <div class="account-avatar">G</div>
      <div>
        <small>GUEST COMMANDER</small>
        <div class="commander-name-line">
          <strong id="commander-name-display">Commander</strong>
          <button id="commander-name-edit" class="name-edit-button" type="button" aria-label="Edit commander name" title="Edit commander name">✎</button>
        </div>
        <span>Sign in to enter ranked rooms and retain your ratings.</span>
      </div>
      <div class="account-actions">
        <button type="button" data-open-friends>Friends</button>
        <a class="provider-button steam" id="steam-signin" href="/auth/steam/start">Steam</a>
        <a class="provider-button discord hidden" id="discord-signin" href="/auth/discord/start">Discord</a>
      </div>
    </section>
    <section class="hero-panel">
      <h1>Throne<br /><span>Dominion</span></h1>
      <p class="lead">Your seat at the war council awaits.</p>
      <div class="menu-form landing-actions">
        <button id="host-button" class="primary-button menu-host-button" aria-label="Host a match"><span class="menu-plaque">Host a match<small>Raise your banner</small></span></button>
        <button id="browse-games" aria-label="Browse games"><span class="menu-plaque">Browse games<small>Find your rivals</small></span></button>
        <button id="spectate-code" aria-label="Spectate by room code"><span class="menu-plaque">Spectate<small>Watch a battle · room code</small></span></button>
        <button id="standings-button" class="menu-standings-button" type="button" aria-label="Commander standings"><span class="menu-plaque">Commander standings<small>The book of deeds</small></span></button>
        <div class="join-row">
          <label for="room-code">Invited to a war council?</label>
          <input id="room-code" maxlength="5" placeholder="ROOM CODE" aria-label="Room code" />
          <button id="join-button">Join</button>
        </div>
      </div>
    </section>
    <footer class="chronicle-footer">
      <p id="connection-status" class="status-line" role="status">Connecting to realm server…</p>
      <span class="chronicle-edition">THRONE DOMINION</span>
      ${/Electron\//.test(navigator.userAgent) ? "" : '<a class="desktop-download" href="https://github.com/Damen57/throne-dominion-downloads/releases/download/desktop-latest/Throne-Dominion-Setup.exe" title="Windows 10/11 · 64-bit · Dedicated hardware-accelerated client">Download for Windows</a>'}
      <button id="menu-music-toggle" class="menu-music-toggle" type="button" aria-pressed="true">♪ MENU MUSIC: ON</button>
    </footer>
    <section id="browser-panel" class="menu-modal hidden" aria-label="Server browser">
      <div class="menu-modal-card"><header><h2 id="browser-title">Current games</h2><button id="browser-close" aria-label="Close server browser">×</button></header>
        <div class="setup-modal-body"><p id="browser-description">Public games on this server. Invite-only games require a room code.</p>
        <form id="spectate-form" class="hidden">
          <label for="spectate-room-code">Room code</label>
          <input id="spectate-room-code" placeholder="Enter a room code" maxlength="5" autocomplete="off" required />
          <button id="spectate-confirm" type="submit">Watch match</button>
        </form>
        <p id="browser-error" role="status" aria-live="polite"></p>
        <button id="browser-refresh">Refresh</button><div id="browser-list" aria-live="polite"></div></div>
      </div>
    </section>
    <section id="host-setup-panel" class="menu-modal hidden" aria-label="Host match setup">
      <div class="menu-modal-card setup-modal-card">
        <header>
          <div><small>RAISE YOUR BANNER</small><h2>Host a match</h2></div>
          <button id="host-setup-close" type="button" aria-label="Close match setup">×</button>
        </header>
        <div class="setup-modal-body">
          <p>Choose the rules for this lobby. You can invite friends or add an AI rival from the war council.</p>
          <label>
            Lobby type
            <select id="lobby-mode">
              <option value="unranked">Unranked · Casual rating</option>
              <option value="ranked">Ranked · Sign-in required</option>
            </select>
          </label>
          <label>
            Victory condition
            <select id="victory-condition"><option value="conquest">Conquest</option><option value="town_centres">Destroy Town Centres</option><option value="hill">King of the Hill</option></select>
          </label>
          <p id="victory-description">Eliminate all enemy units and unit-producing buildings; other buildings do not count.</p>
          <label>
            Battlefield
            <select id="map-type">
              <option value="random">Random map</option>
              <option value="open">Open Frontier</option>
              <option value="forest">Timber Pass</option>
              <option value="arena">Crown Arena</option>
            </select>
          </label>
          <label>Who can join?<select id="lobby-access"><option value="public">Public · listed in server browser</option><option value="invite">Invite only · share room code or link</option></select></label>
          <label><input type="checkbox" id="allow-spectators" checked /> Allow live spectators (full map view)</label>
          <button id="host-confirm-button" class="primary-button">Create war council</button>
        </div>
      </div>
    </section>
    <section id="commander-name-panel" class="menu-modal hidden" aria-label="Edit commander name">
      <div class="menu-modal-card name-modal-card">
        <header>
          <div><small>COMMANDER PROFILE</small><h2>Choose your name</h2></div>
          <button id="commander-name-close" type="button" aria-label="Close name editor">×</button>
        </header>
        <div class="setup-modal-body">
          <label>
            Display name
            <input id="player-name" maxlength="20" value="Commander" autocomplete="nickname" />
          </label>
          <p>This name appears in lobbies, matches, chat, and public standings.</p>
          <button id="commander-name-save" class="primary-button">Save commander name</button>
        </div>
      </div>
    </section>
    <section id="standings-panel" class="menu-modal hidden" aria-label="Global standings">
      <div class="menu-modal-card">
        <header>
          <div><small>GLOBAL RECORDS</small><h2>Commander standings</h2></div>
          <button id="standings-close" type="button" aria-label="Close standings">×</button>
        </header>
        <div class="standings-tabs">
          <button type="button" class="active" data-mode="ranked">Ranked</button>
          <button type="button" data-mode="unranked">Unranked</button>
          <button type="button" data-mode="recent">My matches</button>
          <button type="button" data-mode="ai">Vs AI</button>
        </div>
        <div id="standings-list" class="standings-list">Loading records…</div>
      </div>
    </section>
  </main>

  <main id="lobby-screen" class="screen lobby-screen hidden">
    <section class="lobby-card">
      <div class="eyebrow">War council</div>
      <h1>Room <button id="copy-code" class="room-code"></button></h1>
      <p><strong id="lobby-mode-label">UNRANKED</strong> · <strong id="lobby-map-label">RANDOM MAP</strong> · Click the room code to copy a friend invite link.</p>
      <div id="lobby-players" class="lobby-players"></div>
      <div class="lobby-actions">
        <button type="button" data-open-friends>Friends</button>
        <button id="leave-lobby">Leave lobby</button>
        <button id="ready-lobby">Ready</button>
        <button id="add-bot-button">Add AI rival</button>
        <button id="start-button" class="primary-button" disabled>Begin match</button>
      </div>
      <p id="lobby-status" class="status-line">Waiting for another commander…</p>
    </section>
  </main>

  <main id="game-screen" class="game-screen hidden">
    <div id="viewport"></div>
    <header class="topbar">
      <div class="age-panel">
        <span id="age-number">I</span>
        <div class="age-copy">
          <small>CURRENT ERA</small>
          <strong id="age-name">Settlement Age</strong>
          <div id="age-progress" class="age-progress hidden">
            <i id="age-progress-fill"></i>
            <span id="age-progress-label"></span>
          </div>
        </div>
      </div>
      <button id="idle-villager-button" class="idle-villager-button" disabled>
        <span>V</span>
        <div><small>IDLE VILLAGERS</small><strong id="idle-villager-count">0</strong></div>
      </button>
      <div id="resources" class="resources"></div>
      <div class="match-meta">
        <span id="room-indicator">ROOM</span>
        <div class="performance-readout" aria-label="Match performance">
          <span id="match-time-indicator">00:00</span>
          <span id="client-fps-indicator">-- FPS</span>
          <span id="server-rate-indicator">-- SIM</span>
          <span id="ping-indicator">-- ms</span>
        </div>
        <div class="match-controls">
          <button id="pause-toggle" class="pause-toggle" aria-label="Pause match">⏸ PAUSE <kbd>P</kbd></button>
          <button id="sound-toggle" class="sound-toggle" aria-label="Toggle sound">SOUND ON</button>
        </div>
      </div>
      <div id="victory-status" style="position:absolute;top:90px;left:50%;transform:translateX(-50%);padding:8px 12px;background:#161b22dd;color:#efd694;pointer-events:none;text-align:center"></div>
      <nav id="global-production" class="global-production" aria-label="Unit production queue"></nav>
    </header>
    <section id="scoreboard" class="scoreboard" aria-label="Player scores"></section>
    <button id="match-summary-button" class="match-summary-button hidden" type="button">
      MATCH SUMMARY
    </button>
    <div id="event-feed" class="event-feed"></div>
    <div id="selection-box" class="selection-box hidden"></div>
    <div id="placement-hint" class="placement-hint hidden">Place building · Esc to cancel</div>
    <div id="world-tooltip" class="world-tooltip hidden"></div>
    <div id="combat-cursor" class="context-cursor combat hidden">⚔</div>
    <div id="work-cursor" class="context-cursor work hidden">🔨</div>
    <div id="garrison-cursor" class="context-cursor garrison hidden">♜</div>
    <button id="attack-alert" class="attack-alert hidden">
      <span>⚠</span>
      <div><small>YOUR FORCES</small><strong>UNDER ATTACK</strong></div>
      <em>CLICK TO VIEW</em>
    </button>
    <aside class="objectives">
      <div class="eyebrow">Strategic objective</div>
      <strong id="objective-title">Conquest</strong>
      <span id="objective-description">Eliminate all enemy units and unit-producing buildings.</span>
    </aside>
    <section id="control-hotbar" class="control-hotbar" aria-label="Control groups">
      ${Array.from(
        { length: 9 },
        (_, index) => `
          <button class="control-slot empty" data-group="${index + 1}" title="Ctrl+${index + 1} assigns the current selection">
            <kbd>${index + 1}</kbd>
            <span>—</span>
            <small>EMPTY</small>
          </button>`,
      ).join("")}
    </section>
    <section id="chat-panel" class="chat-panel" aria-label="War chat">
      <header>
        <strong>WAR CHAT</strong>
        <span>ENTER TO CHAT</span>
        <button id="taunt-toggle" type="button">TAUNTS</button>
        <button id="chat-minimize" type="button" aria-label="Minimize chat" title="Minimize chat">−</button>
      </header>
      <div id="chat-log" class="chat-log" aria-live="polite">
        <div class="chat-empty">No messages yet.</div>
      </div>
      <div id="taunt-menu" class="taunt-menu hidden">
        ${[
          "Yes.",
          "No.",
          "Food, please.",
          "Wood, please.",
          "Gold, please.",
          "Stone, please.",
          "Attack now!",
          "You have chosen war.",
          "Good game.",
        ]
          .map(
            (taunt, index) =>
              `<button type="button" data-taunt="${index + 1}"><kbd>${index + 1}</kbd>${taunt}</button>`,
          )
          .join("")}
      </div>
      <form id="chat-form" class="chat-form">
        <input id="chat-input" maxlength="140" autocomplete="off" placeholder="Message your rival…" aria-label="Chat message" />
        <button type="submit">SEND</button>
      </form>
    </section>
    <section class="bottom-hud">
      <canvas id="minimap" width="190" height="190" aria-label="Minimap"></canvas>
      <div id="command-panel" class="command-panel"></div>
      <div id="selection-info" class="selection-info"></div>
    </section>
    <div id="pause-overlay" class="pause-overlay hidden" aria-live="polite">
      <div>
        <small>MATCH PAUSED</small>
        <strong id="pause-owner">The battle is suspended.</strong>
        <span>Pan and inspect freely · Press P to resume</span>
      </div>
    </div>
    <div id="game-menu-overlay" class="game-menu-overlay hidden" aria-modal="true" role="dialog" aria-labelledby="game-menu-title">
      <div class="game-menu-card">
        <div class="eyebrow">War council</div>
        <h2 id="game-menu-title">Battle menu</h2>
        <p id="game-menu-status">The match continues while this menu is open.</p>
        <div class="game-menu-actions">
          <button id="game-menu-resume" class="primary-button">Resume battle <kbd>ESC</kbd></button>
          <button id="game-menu-sound">Sound: on</button>
          <button id="game-menu-resign" class="danger-button">Resign match</button>
          <button id="game-menu-exit">Exit to main menu</button>
        </div>
      </div>
    </div>
    <dialog id="delete-confirmation" class="game-menu-card delete-confirmation" aria-labelledby="delete-title" aria-describedby="delete-description delete-live-note">
      <form method="dialog">
        <div class="eyebrow">Remove from the battlefield</div>
        <h2 id="delete-title">Delete selection?</h2>
        <p id="delete-description"></p>
        <p class="delete-warning">This cannot be undone. No resources will be refunded.</p>
        <div class="game-menu-actions delete-confirmation-actions">
          <button value="cancel" autofocus>Keep selection <kbd>ESC</kbd></button>
          <button value="delete" class="danger-button">Delete permanently</button>
        </div>
        <small id="delete-live-note">The match continues while you decide.</small>
      </form>
    </dialog>
    <div id="end-screen" class="end-screen hidden">
      <div class="end-card">
        <div class="eyebrow">The banners fall</div>
        <h2 id="end-title">Victory</h2>
        <p id="end-copy"></p>
        <section id="match-summary" class="match-summary"></section>
        <div class="end-actions">
          <button id="review-battlefield">Review battlefield</button>
          <button id="return-to-council" class="primary-button">Return to council</button>
          <button type="button" data-open-friends data-recent>Recent players</button>
        </div>
      </div>
    </div>
  </main>
`;

const menuScreen = element("menu-screen");
const lobbyScreen = element("lobby-screen");
const gameScreen = element("game-screen");
const statusLine = element("connection-status");
const lobbyStatus = element("lobby-status");
const playerNameInput = inputElement("player-name");
const roomCodeInput = inputElement("room-code");
const lobbyModeSelect =
  document.querySelector<HTMLSelectElement>("#lobby-mode")!;
const mapTypeSelect =
  document.querySelector<HTMLSelectElement>("#map-type")!;
const accountPanel = element("account-panel");
const hostSetupPanel = element("host-setup-panel");
const hostSetupClose = buttonElement("host-setup-close");
const hostConfirmButton = buttonElement("host-confirm-button");
const commanderNamePanel = element("commander-name-panel");
const commanderNameClose = buttonElement("commander-name-close");
const commanderNameSave = buttonElement("commander-name-save");
const steamSignin =
  document.querySelector<HTMLAnchorElement>("#steam-signin")!;
const discordSignin =
  document.querySelector<HTMLAnchorElement>("#discord-signin")!;
const lobbyModeLabel = element("lobby-mode-label");
const lobbyMapLabel = element("lobby-map-label");
const standingsPanel = element("standings-panel");
const standingsList = element("standings-list");
const standingsButton = buttonElement("standings-button");
const standingsClose = buttonElement("standings-close");
const hostButton = buttonElement("host-button");
const joinButton = buttonElement("join-button");
const addBotButton = buttonElement("add-bot-button");
const startButton = buttonElement("start-button");
const copyCodeButton = buttonElement("copy-code");
const selectionInfo = element("selection-info");
const commandPanel = element("command-panel");
const commandHelp = document.createElement("div");
commandHelp.className = "command-help hidden";
commandHelp.id = "command-help";
commandHelp.setAttribute("role", "tooltip");
document.body.append(commandHelp);
function showCommandHelp(event: Event): void {
  const button = (event.target as HTMLElement).closest<HTMLElement>("[data-help]");
  if (!button) { commandHelp.classList.add("hidden"); return; }
  commandHelp.textContent = button.dataset.help ?? "";
  button.setAttribute("aria-describedby", commandHelp.id);
  commandHelp.classList.remove("hidden");
  const rect = button.getBoundingClientRect();
  commandHelp.style.left = `${Math.max(8, Math.min(rect.left, innerWidth - commandHelp.offsetWidth - 8))}px`;
  commandHelp.style.top = `${Math.max(8, rect.top - commandHelp.offsetHeight - 10)}px`;
}
commandPanel.addEventListener("pointermove", showCommandHelp);
commandPanel.addEventListener("focusin", showCommandHelp);
for (const event of ["pointerleave", "focusout", "pointerdown"]) {
  commandPanel.addEventListener(event, () => commandHelp.classList.add("hidden"));
}
const selectionBox = element("selection-box");
const placementHint = element("placement-hint");
const worldTooltip = element("world-tooltip");
const combatCursor = element("combat-cursor");
const workCursor = element("work-cursor");
const garrisonCursor = element("garrison-cursor");
const attackAlert = buttonElement("attack-alert");
const controlHotbar = element("control-hotbar");
const viewport = element("viewport");
const minimap = document.querySelector<HTMLCanvasElement>("#minimap")!;
const menuMusicToggle = buttonElement("menu-music-toggle");
const soundToggle = buttonElement("sound-toggle");
const pauseToggle = buttonElement("pause-toggle");
const pauseOverlay = element("pause-overlay");
const gameMenuOverlay = element("game-menu-overlay");
const deleteConfirmation = document.querySelector<HTMLDialogElement>("#delete-confirmation")!;

function requestDeleteConfirmation(dialog: HTMLDialogElement, label: string): Promise<boolean> {
  if (dialog.open) return Promise.resolve(false);
  dialog.querySelector("#delete-description")!.textContent = `Permanently remove ${label}?`;
  dialog.returnValue = "cancel";
  return new Promise(resolve => {
    dialog.addEventListener("close", () => resolve(dialog.returnValue === "delete"), { once: true });
    dialog.showModal();
  });
}
const gameMenuResume = buttonElement("game-menu-resume");
const gameMenuSound = buttonElement("game-menu-sound");
const gameMenuResign = buttonElement("game-menu-resign");
const gameMenuExit = buttonElement("game-menu-exit");
const returnToCouncilButton = buttonElement("return-to-council");
const reviewBattlefieldButton = buttonElement("review-battlefield");
const matchSummaryButton = buttonElement("match-summary-button");
const idleVillagerButton = buttonElement("idle-villager-button");
const chatForm = document.querySelector<HTMLFormElement>("#chat-form")!;
const chatInput = inputElement("chat-input");
const chatLog = element("chat-log");
const tauntToggle = buttonElement("taunt-toggle");
const chatMinimize = buttonElement("chat-minimize");
const tauntMenu = element("taunt-menu");

document.addEventListener(
  "pointerdown",
  () => {
    void soundDesign.unlock();
  },
  { once: true },
);
document.addEventListener("pointerdown", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
    "button",
  );
  if (button && !button.disabled) soundDesign.click();
});
function updateMenuMusicToggle(): void {
  const enabled = soundDesign.isMenuMusicEnabled;
  menuMusicToggle.textContent = enabled
    ? "♪ MENU MUSIC: ON"
    : "♪ MENU MUSIC: OFF";
  menuMusicToggle.setAttribute("aria-pressed", String(enabled));
  menuMusicToggle.classList.toggle("muted", !enabled);
}
menuMusicToggle.addEventListener("click", () => {
  soundDesign.setMenuMusicEnabled(!soundDesign.isMenuMusicEnabled);
  updateMenuMusicToggle();
});
updateMenuMusicToggle();
soundToggle.addEventListener("click", () => {
  const muted = soundDesign.toggle();
  soundToggle.textContent = muted ? "SOUND OFF" : "SOUND ON";
  soundToggle.classList.toggle("muted", muted);
  gameMenuSound.textContent = muted ? "Sound: off" : "Sound: on";
});
pauseToggle.addEventListener("click", () => {
  void togglePause();
});
reviewBattlefieldButton.addEventListener("click", () => {
  element("end-screen").classList.add("hidden");
  matchSummaryButton.classList.remove("hidden");
});
matchSummaryButton.addEventListener("click", () => {
  if (!latestSnapshot?.matchReport) return;
  renderMatchSummary(latestSnapshot.matchReport);
  gameMenuOverlay.classList.add("hidden");
  matchSummaryButton.classList.add("hidden");
  element("end-screen").classList.remove("hidden");
});
function setGameMenuOpen(open: boolean): void {
  if (!latestSnapshot) return;
  gameMenuOverlay.classList.toggle("hidden", !open);
  if (!open) return;
  const finished = latestSnapshot.phase === "finished";
  element("game-menu-title").textContent = matchUnavailable ? "Match ended" : finished
    ? "Battlefield review"
    : "Battle menu";
  element("game-menu-status").textContent = matchUnavailable ? "This match is no longer available. The server may have restarted. You can return to the menu." : finished
    ? "Review the final battlefield or return to the main council."
    : latestSnapshot.isPaused
      ? "The match is currently paused."
      : "The match continues while this menu is open.";
  gameMenuResume.innerHTML = finished
    ? "Return to battlefield <kbd>ESC</kbd>"
    : "Resume battle <kbd>ESC</kbd>";
  gameMenuResign.classList.toggle("hidden", finished || matchUnavailable);
  gameMenuResume.classList.toggle("hidden", matchUnavailable);
  gameMenuSound.textContent = soundDesign.isMuted ? "Sound: off" : "Sound: on";
}

async function resignCurrentMatch(): Promise<boolean> {
  if (
    matchUnavailable || !latestSnapshot || latestSnapshot.isSpectator ||
    latestSnapshot.phase !== "playing" ||
    connection.state !== HubConnectionState.Connected
  ) {
    return true;
  }
  const result = await connection.invoke<OperationResult>("Command", {
    type: "resign",
  } satisfies CommandPayload);
  if (!result.ok && result.error === "Match not found.") {
    markMatchUnavailable();
    return true;
  }
  if (!result.ok) {
    showEvent(result.error ?? "Could not resign from this match.", "warning");
  }
  return result.ok;
}

gameMenuResume.addEventListener("click", () => setGameMenuOpen(false));
gameMenuSound.addEventListener("click", () => soundToggle.click());
gameMenuResign.addEventListener("click", async () => {
  gameMenuResign.disabled = true;
  const resigned = await resignCurrentMatch();
  gameMenuResign.disabled = false;
  if (resigned && !matchUnavailable) setGameMenuOpen(false);
});
gameMenuExit.addEventListener("click", async () => {
  gameMenuExit.disabled = true;
  if (!await resignCurrentMatch()) {
    gameMenuExit.disabled = false;
    return;
  }
  sessionStorage.removeItem(activeRoomSessionKey);
  if (!matchUnavailable && connection.state === HubConnectionState.Connected)
    void connection.invoke("LeaveRoom").catch(() => {});
  sessionStorage.removeItem("spectating-room");
  location.assign("/");
});
idleVillagerButton.addEventListener("pointerdown", (event) => {
  if (event.button !== 0 || idleVillagerButton.disabled) return;
  event.preventDefault();
  gameView?.selectNextIdleVillager();
});
chatForm.addEventListener("submit", async (event) => {
  event.preventDefault();
  const message = chatInput.value.trim();
  if (
    !message ||
    !connectionReady ||
    connection.state !== HubConnectionState.Connected
  ) return;
  const result = await connection.invoke<OperationResult>("SendChat", message);
  if (result.ok) chatInput.value = "";
  else showEvent(result.error ?? "Chat message failed.", "warning");
});
tauntToggle.addEventListener("click", () => {
  tauntMenu.classList.toggle("hidden");
  tauntToggle.classList.toggle(
    "active",
    !tauntMenu.classList.contains("hidden"),
  );
});
chatMinimize.addEventListener("click", () => {
  const minimized = element("chat-panel").classList.toggle("minimized");
  chatMinimize.textContent = minimized ? "+" : "−";
  chatMinimize.setAttribute(
    "aria-label",
    minimized ? "Expand chat" : "Minimize chat",
  );
  chatMinimize.title = minimized ? "Expand chat" : "Minimize chat";
});
tauntMenu.addEventListener("click", async (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
    "button[data-taunt]",
  );
  if (
    !button ||
    !connectionReady ||
    connection.state !== HubConnectionState.Connected
  ) return;
  const result = await connection.invoke<OperationResult>(
    "SendTaunt",
    Number(button.dataset.taunt),
  );
  if (!result.ok) showEvent(result.error ?? "Taunt failed.", "warning");
});
window.addEventListener("keydown", (event) => {
  if (deleteConfirmation.open) return;
  if (gameScreen.classList.contains("hidden")) return;
  const typing =
    document.activeElement instanceof HTMLInputElement ||
    document.activeElement instanceof HTMLTextAreaElement;
  if (event.key === "Escape") {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (document.activeElement === chatInput) {
      chatInput.blur();
      return;
    }
    setGameMenuOpen(gameMenuOverlay.classList.contains("hidden"));
  } else if (event.key.toLowerCase() === "p" && !typing) {
    event.preventDefault();
    event.stopImmediatePropagation();
    void togglePause();
  } else if (event.key === "Enter" && document.activeElement !== chatInput) {
    event.preventDefault();
    chatInput.focus();
  }
});

const hubUrl = import.meta.env.PROD
  ? "/gameHub"
  : `${location.protocol}//${location.hostname}:3000/gameHub`;
const serverOrigin = import.meta.env.PROD
  ? ""
  : `${location.protocol}//${location.hostname}:3000`;
steamSignin.href = `${serverOrigin}/auth/steam/start`;
discordSignin.href = `${serverOrigin}/auth/discord/start`;

interface AccountSummary {
  id: string;
  provider: "steam" | "discord";
  providers: ("steam" | "discord")[];
  displayName: string;
  avatarUrl?: string;
  rankedElo: number;
  unrankedElo: number;
  rankedWins: number;
  rankedLosses: number;
  unrankedWins: number;
  unrankedLosses: number;
  isPublic: boolean;
}

interface AccountResponse {
  authenticated: boolean;
  account?: AccountSummary;
  providers: { steam: boolean; discord: boolean };
}

let accountState: AccountResponse | undefined;

function renderSignedInAccount(account: AccountSummary): void {
  const linkedProviders = account.providers?.length
    ? account.providers
    : [account.provider];
  const linkButtons = [
    accountState?.providers.steam && !linkedProviders.includes("steam")
      ? `<a class="provider-button steam" href="${serverOrigin}/auth/steam/start?link=true">Link Steam</a>`
      : "",
    accountState?.providers.discord && !linkedProviders.includes("discord")
      ? `<a class="provider-button discord" href="${serverOrigin}/auth/discord/start?link=true">Link Discord</a>`
      : "",
  ].join("");
  accountPanel.innerHTML = `
    ${account.avatarUrl
      ? `<img src="${escapeHtml(account.avatarUrl)}" alt="" />`
      : `<div class="account-avatar">${account.provider === "steam" ? "S" : "D"}</div>`}
    <div>
      <small>COMMANDER PROFILE</small>
      <div class="commander-name-line">
        <strong id="commander-name-display">${escapeHtml(account.displayName)}</strong>
        <button id="commander-name-edit" class="name-edit-button" type="button" aria-label="Edit commander name" title="Edit commander name">✎</button>
      </div>
      <span>Ranked ${account.rankedElo} · Unranked ${account.unrankedElo}</span>
      <span class="account-provider-list">${linkedProviders
        .map((provider) => `<b>${provider.toUpperCase()}</b>`)
        .join("")}</span>
    </div>
    <div class="account-actions signed-in">
      <button type="button" data-open-friends>Friends</button>
      ${linkButtons}
      <button id="account-visibility" type="button" title="Control whether you appear in global standings">${account.isPublic ? "Public profile" : "Private profile"}</button>
      <button id="logout-button" type="button">Sign out</button>
    </div>
    <div class="account-presence"><label><input type="checkbox" data-appear-offline disabled> Appear offline</label><span data-presence-status role="status"></span></div>`;
  document.dispatchEvent(new Event("commander-account-changed"));
}

async function loadAccount(): Promise<void> {
  try {
    const response = await fetch(`${serverOrigin}/api/account`, {
      credentials: "include",
    });
    if (!response.ok) return;
    accountState = await response.json() as AccountResponse;
    discordSignin.classList.toggle(
      "hidden",
      !accountState.providers.discord,
    );
    const rankedOption = lobbyModeSelect.querySelector<HTMLOptionElement>(
      'option[value="ranked"]',
    );
    if (rankedOption) rankedOption.disabled = !accountState.authenticated;
    if (accountState.account) {
      const account = accountState.account;
      playerNameInput.value = account.displayName;
      playerNameInput.readOnly = false;
      renderSignedInAccount(account);
    }
  } catch {
    // Guest play remains available when the account service is offline.
  }
}

accountPanel.addEventListener("click", async (event) => {
  const target = event.target as HTMLElement;
  const editName = target.closest("#commander-name-edit");
  if (editName) {
    commanderNamePanel.classList.remove("hidden");
    playerNameInput.focus();
    playerNameInput.select();
    return;
  }
  const visibility = target.closest("#account-visibility");
  if (visibility && accountState?.account) {
    const response = await fetch(`${serverOrigin}/api/account/visibility`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ isPublic: !accountState.account.isPublic }),
    });
    if (response.ok) {
      accountState.account = await response.json() as AccountSummary;
      renderSignedInAccount(accountState.account);
    }
    return;
  }
  const button = target.closest("#logout-button");
  if (!button) return;
  await fetch(`${serverOrigin}/auth/logout`, {
    method: "POST",
    credentials: "include",
  });
  location.reload();
});

async function saveCommanderName(): Promise<void> {
  const displayName = playerNameInput.value.trim();
  if (displayName.length < 2) {
    showMenuError("Commander names must contain at least 2 characters.");
    return;
  }
  playerNameInput.value = displayName;
  if (accountState?.account) {
    commanderNameSave.disabled = true;
    try {
      const response = await fetch(`${serverOrigin}/api/account/display-name`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ displayName }),
      });
      if (!response.ok) {
        showMenuError("That commander name could not be saved.");
        return;
      }
      accountState.account = await response.json() as AccountSummary;
      playerNameInput.value = accountState.account.displayName;
      renderSignedInAccount(accountState.account);
    } finally {
      commanderNameSave.disabled = false;
    }
  } else {
    const display = accountPanel.querySelector("#commander-name-display");
    if (display) display.textContent = displayName;
  }
  commanderNamePanel.classList.add("hidden");
  statusLine.textContent = "Commander name saved.";
  statusLine.className = "status-line online";
}

commanderNameSave.addEventListener("click", () => void saveCommanderName());
commanderNameClose.addEventListener(
  "click",
  () => commanderNamePanel.classList.add("hidden"),
);
commanderNamePanel.addEventListener("click", (event) => {
  if (event.target === commanderNamePanel)
    commanderNamePanel.classList.add("hidden");
});
playerNameInput.addEventListener("keydown", (event) => {
  if (event.key === "Enter") {
    event.preventDefault();
    void saveCommanderName();
  } else if (event.key === "Escape") {
    commanderNamePanel.classList.add("hidden");
  }
});

const mapLabels: Record<string, string> = {
  open: "Open Frontier",
  forest: "Timber Pass",
  arena: "Crown Arena",
  random: "Random map",
};

function formatMatchTime(totalSeconds: number): string {
  const wholeSeconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(wholeSeconds / 60);
  const seconds = wholeSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

let historyAccountId: string | undefined;
let historyAccountName = "";
let historyReports: MatchReport[] = [];
let standingsRequest = 0;
const historyDialog = document.createElement("dialog");
historyDialog.className = "history-report-dialog";
historyDialog.setAttribute("aria-label", "Post-match statistics");
historyDialog.innerHTML = `<button type="button" class="history-report-close" aria-label="Close match report">Close ×</button><section id="history-report-content" class="match-summary"></section>`;
document.body.append(historyDialog);
historyDialog.querySelector("button")!.addEventListener("click", () => historyDialog.close());

async function loadStandings(
  mode: "ranked" | "unranked" | "recent" | "ai",
): Promise<void> {
  const request = ++standingsRequest;
  historyReports = [];
  standingsList.innerHTML = `<div class="standings-empty">Loading records…</div>`;
  if (mode === "recent" || mode === "ai") {
    if (!historyAccountId && !accountState?.authenticated) {
      standingsList.innerHTML = `<div class="standings-empty">Sign in to retain and review your completed matches.</div>`;
      return;
    }
    const endpoint = historyAccountId ? `/api/leaderboard/${encodeURIComponent(historyAccountId)}/matches` : "/api/matches/recent";
    const response = await fetch(`${serverOrigin}${endpoint}?ai=${mode === "ai"}`, {
      credentials: "include",
    });
    if (request !== standingsRequest) return;
    if (!response.ok) {
      standingsList.innerHTML = `<div class="standings-empty">Match history is unavailable.</div>`;
      return;
    }
    const matches = await response.json() as Array<{
      opponentName: string;
      won: boolean;
      report: MatchReport;
    }>;
    if (request !== standingsRequest) return;
    historyReports = matches.map(match => match.report);
    const heading = historyAccountId ? `<div class="history-owner">${escapeHtml(historyAccountName)} · ${mode === "ai" ? "Vs AI" : "Human matches"} <button type="button" data-own-history>My history</button></div>` : "";
    standingsList.innerHTML = heading + (matches.length
      ? matches.map((match, index) => `
          <button type="button" data-report-index="${index}" class="recent-match ${match.won ? "won" : "lost"}" title="View full post-match report">
            <b>${match.won ? "VICTORY" : "DEFEAT"}</b>
            <div><strong>vs ${escapeHtml(match.opponentName)}</strong><span>${mapLabels[match.report.mapType]} · ${formatMatchTime(match.report.durationSeconds)}</span></div>
            <small>${new Date(match.report.finishedAt).toLocaleDateString()}</small>
          </button>`).join("")
      : `<div class="standings-empty">No ${mode === "ai" ? "AI" : "human"} matches recorded yet.</div>`);
    return;
  }
  const response = await fetch(`${serverOrigin}/api/leaderboard?mode=${mode}`);
  if (request !== standingsRequest) return;
  if (!response.ok) {
    standingsList.innerHTML = `<div class="standings-empty">Standings are unavailable.</div>`;
    return;
  }
  const accounts = await response.json() as AccountSummary[];
  if (request !== standingsRequest) return;
  standingsList.innerHTML = accounts.length
    ? accounts.map((account, index) => {
        const elo = mode === "ranked" ? account.rankedElo : account.unrankedElo;
        const wins = mode === "ranked" ? account.rankedWins : account.unrankedWins;
        const losses = mode === "ranked" ? account.rankedLosses : account.unrankedLosses;
        return `<button type="button" class="standing-row" data-history-account="${escapeHtml(account.id)}" data-history-name="${escapeHtml(account.displayName)}" title="View match history">
          <b>${index + 1}</b>
          ${account.avatarUrl
            ? `<img src="${escapeHtml(account.avatarUrl)}" alt="" />`
            : `<i>${account.provider === "steam" ? "S" : "D"}</i>`}
          <div><strong>${escapeHtml(account.displayName)}</strong><span>${wins}W · ${losses}L</span></div>
          <em>${elo} ELO</em>
        </button>`;
      }).join("")
    : `<div class="standings-empty">No public commanders have played this ladder yet.</div>`;
}

standingsButton.addEventListener("click", () => {
  historyAccountId = undefined;
  standingsPanel.classList.remove("hidden");
  void loadStandings("ranked");
});
standingsClose.addEventListener(
  "click",
  () => standingsPanel.classList.add("hidden"),
);
standingsPanel.addEventListener("click", (event) => {
  const target = event.target as HTMLElement;
  const report = target.closest<HTMLElement>("[data-report-index]");
  if (report) {
    const selected = historyReports[Number(report.dataset.reportIndex)];
    if (selected) { renderMatchSummary(selected, "history-report-content"); historyDialog.showModal(); }
    return;
  }
  const account = target.closest<HTMLElement>("[data-history-account]");
  if (account || target.closest("[data-own-history]")) {
    historyAccountId = account?.dataset.historyAccount;
    historyAccountName = account?.dataset.historyName ?? "";
    standingsPanel.querySelectorAll<HTMLElement>("[data-mode]").forEach(tab => tab.classList.toggle("active", tab.dataset.mode === "recent"));
    void loadStandings("recent");
    return;
  }
  if (event.target === standingsPanel) standingsPanel.classList.add("hidden");
  const tab = (event.target as HTMLElement).closest<HTMLButtonElement>(
    "[data-mode]",
  );
  if (!tab) return;
  standingsPanel.querySelectorAll("[data-mode]").forEach((candidate) =>
    candidate.classList.toggle("active", candidate === tab));
  if (tab.dataset.mode === "ranked" || tab.dataset.mode === "unranked") historyAccountId = undefined;
  void loadStandings(tab.dataset.mode as "ranked" | "unranked" | "recent" | "ai");
});

document.querySelectorAll<HTMLAnchorElement>(".provider-button").forEach((link) => {
  link.href = `${serverOrigin}${new URL(link.href).pathname}`;
});
void loadAccount();

const invitedRoomCode = new URLSearchParams(location.search)
  .get("join")
  ?.toUpperCase()
  .replace(/[^A-Z0-9]/g, "")
  .slice(0, 5);
if (invitedRoomCode) roomCodeInput.value = invitedRoomCode;

const playerSessionKey = "throne-and-timber-player-session";
let playerSessionId = sessionStorage.getItem(playerSessionKey);
if (!playerSessionId) {
  playerSessionId = `player-${crypto.randomUUID()}`;
  sessionStorage.setItem(playerSessionKey, playerSessionId);
}
const connection = new HubConnectionBuilder()
  .withUrl(hubUrl)
  .withAutomaticReconnect([0, 1000, 2500, 5000, 10000])
  .configureLogging(LogLevel.Warning)
  .build();

let lobby: LobbyState | undefined;
let latestSnapshot: GameSnapshot | undefined;
let gameView: GameView | undefined;
let ping = 0;
const serverRate = new ServerRate();
let lastSnapshotReceivedAt: number | undefined;
let notifiedEvents = new Set<string>();
let announcedOutcome: "victory" | "defeat" | undefined;
let pauseRequestPending = false;
const activeRoomSessionKey = "throne-and-timber-active-room";
let activeRoomCode =
  sessionStorage.getItem(activeRoomSessionKey) ?? undefined;
let connectionReady = false;
let matchUnavailable = false;
function markMatchUnavailable(): void {
  matchUnavailable = true;
  sessionStorage.removeItem(activeRoomSessionKey);
  sessionStorage.removeItem("spectating-room");
  activeRoomCode = undefined;
  connectionReady = true;
  setGameMenuOpen(true);
}
const friendsPanel = mountFriends(connection, {
  signedIn: () => !!accountState?.authenticated,
  playing: () => !gameScreen.classList.contains("hidden") && latestSnapshot?.phase === "playing",
  playerId: () => playerSessionId!,
  joined: result => {
    if (result.code && result.resumeToken) sessionStorage.setItem(`room-token:${result.code}`, result.resumeToken);
  },
  serverOrigin,
});

returnToCouncilButton.addEventListener("click", () => {
  sessionStorage.removeItem(activeRoomSessionKey);
  location.assign("/");
});

async function resumeActiveRoom(): Promise<boolean> {
  if (!activeRoomCode) return true;
  if (sessionStorage.getItem("spectating-room") === activeRoomCode) {
    const result = await connection.invoke<OperationResult>("Spectate", activeRoomCode);
    if (!result.ok) markMatchUnavailable();
    return result.ok;
  }
  const result = await connection.invoke<OperationResult>(
    "ResumeSession",
    activeRoomCode,
    playerSessionId,
    sessionStorage.getItem(`room-token:${activeRoomCode}`) ?? "",
  );
  if (result.ok) return true;
  markMatchUnavailable();
  showEvent(result.error ?? "The match could not be resumed.", "warning");
  return false;
}

async function togglePause(): Promise<void> {
  if (
    pauseRequestPending ||
    !latestSnapshot ||
    latestSnapshot.phase !== "playing" ||
    !connectionReady ||
    connection.state !== HubConnectionState.Connected
  ) {
    return;
  }
  pauseRequestPending = true;
  pauseToggle.disabled = true;
  try {
    const result = await connection.invoke<OperationResult>("TogglePause");
    if (!result.ok) {
      showEvent(result.error ?? "Could not change pause state.", "warning");
    }
  } catch (error) {
    showEvent(`Pause failed: ${String(error)}`, "warning");
  } finally {
    pauseRequestPending = false;
    pauseToggle.disabled = false;
  }
}

connection.on("Lobby", (state: LobbyState) => {
  sessionStorage.removeItem("spectating-room");
  activeRoomCode = state.code;
  sessionStorage.setItem(activeRoomSessionKey, state.code);
  lobby = state;
  showLobby(state);
});
connection.on("Snapshot", (snapshot: GameSnapshot) => {
  matchUnavailable = false;
  if (latestSnapshot?.roomCode === snapshot.roomCode &&
      (snapshot.tick < latestSnapshot.tick || snapshot.serverTime < latestSnapshot.serverTime)) return;
  activeRoomCode = snapshot.roomCode;
  sessionStorage.setItem(activeRoomSessionKey, snapshot.roomCode);
  latestSnapshot = snapshot;
  document.body.classList.toggle("spectating", !!snapshot.isSpectator);
  if (snapshot.isSpectator) sessionStorage.setItem("spectating-room", snapshot.roomCode);
  if (!gameView) {
    soundDesign.setMenuActive(false);
    menuScreen.classList.add("hidden");
    lobbyScreen.classList.add("hidden");
    gameScreen.classList.remove("hidden");
    gameView = new GameView(viewport, connection, snapshot);
  }
  gameView.applySnapshot(snapshot);
  updateHud(snapshot);
});
connection.on("Chat", (message: ChatMessage) => {
  const empty = chatLog.querySelector(".chat-empty");
  if (empty) empty.remove();
  const row = document.createElement("div");
  row.className = `chat-message${message.isTaunt ? " taunt" : ""}`;
  row.innerHTML = `
    <i style="--player-color:${escapeHtml(message.playerColor)}"></i>
    <div>
      <strong>${escapeHtml(message.playerName)}</strong>
      <span>${message.isTaunt ? "TAUNT" : "CHAT"}</span>
      <p>${escapeHtml(message.message)}</p>
    </div>`;
  chatLog.append(row);
  while (chatLog.children.length > 30) chatLog.firstElementChild?.remove();
  chatLog.scrollTop = chatLog.scrollHeight;
});
connection.on("ErrorMessage", showMenuError);
connection.onreconnecting(() => {
  connectionReady = false;
  statusLine.textContent = "Connection interrupted. Reconnecting…";
  statusLine.className = "status-line warning";
});
connection.onreconnected(async () => {
  connectionReady = false;
  if (activeRoomCode) {
    try {
      if (!await resumeActiveRoom()) {
        statusLine.textContent = "Match session expired.";
        statusLine.className = "status-line warning";
        return;
      }
    } catch (error) {
      showEvent(`Could not resume match: ${String(error)}`, "warning");
      return;
    }
  }
  connectionReady = true;
  statusLine.textContent = "Realm server online.";
  statusLine.className = "status-line online";
});
connection.onclose(() => {
  connectionReady = false;
  statusLine.textContent = "Connection lost. Reconnecting…";
  statusLine.className = "status-line warning";
});
connection
  .start()
  .then(async () => {
    void friendsPanel.refresh();
    try {
      connectionReady = await resumeActiveRoom();
      statusLine.textContent = connectionReady
        ? "Realm server online."
        : "Previous match session expired.";
      statusLine.className = connectionReady
        ? "status-line online"
        : "status-line warning";
    } catch (error) {
      connectionReady = false;
      showMenuError(`Could not resume the previous match: ${String(error)}`);
    }
  })
  .catch((error: unknown) => {
    showMenuError(`Could not reach the C# realm server: ${String(error)}`);
  });

const victoryLabels = { conquest: "Conquest", town_centres: "Destroy Town Centres", hill: "King of the Hill" };
const victoryDescriptions = { conquest: "Eliminate all enemy units and unit-producing buildings; other buildings do not count.", town_centres: "Destroy every enemy Town Centre.", hill: "Hold the central landmark for 3 minutes; enemies pause the timer, losing control resets it." };
const victorySelect = element("victory-condition") as HTMLSelectElement;
function updateVictoryChoice(): void {
  victorySelect.disabled = lobbyModeSelect.value === "ranked";
  if (victorySelect.disabled) victorySelect.value = "conquest";
  element("victory-description").textContent = victoryDescriptions[victorySelect.value as keyof typeof victoryDescriptions];
}
victorySelect.addEventListener("change", updateVictoryChoice);
lobbyModeSelect.addEventListener("change", updateVictoryChoice);

hostButton.addEventListener("click", () => {
  updateVictoryChoice();
  hostSetupPanel.classList.remove("hidden");
});
hostSetupClose.addEventListener(
  "click",
  () => hostSetupPanel.classList.add("hidden"),
);
hostSetupPanel.addEventListener("click", (event) => {
  if (event.target === hostSetupPanel) hostSetupPanel.classList.add("hidden");
});
hostConfirmButton.addEventListener("click", async () => {
  hostConfirmButton.disabled = true;
  try {
    const result = await connection.invoke<OperationResult>(
      "CreateConfiguredLobby",
      playerNameInput.value,
      playerSessionId,
      lobbyModeSelect.value,
      mapTypeSelect.value,
      (element("lobby-access") as HTMLSelectElement).value === "public",
      (element("allow-spectators") as HTMLInputElement).checked,
      (element("victory-condition") as HTMLSelectElement).value,
    );
    hostConfirmButton.disabled = false;
    if (result.ok && result.code && result.resumeToken) sessionStorage.setItem(`room-token:${result.code}`, result.resumeToken);
    if (!result.ok) {
      showMenuError(result.error ?? "Could not create room.");
      return;
    }
    hostSetupPanel.classList.add("hidden");
  } catch (error) {
    hostConfirmButton.disabled = false;
    showMenuError(String(error));
  }
});

joinButton.addEventListener("click", async () => {
  joinButton.disabled = true;
  try {
    const result = await connection.invoke<OperationResult>(
      "JoinRoom",
      playerNameInput.value,
      roomCodeInput.value.toUpperCase(),
      playerSessionId,
    );
    joinButton.disabled = false;
    if (result.ok && result.code && result.resumeToken) sessionStorage.setItem(`room-token:${result.code}`, result.resumeToken);
    if (!result.ok) showMenuError(result.error ?? "Could not join room.");
  } catch (error) {
    joinButton.disabled = false;
    showMenuError(String(error));
  }
});

let browserLoading = false;
let browserSpectating = false;
async function refreshGames(): Promise<void> {
  if (browserLoading || connection.state !== HubConnectionState.Connected) return;
  browserLoading = true;
  try {
    const games = await connection.invoke<Array<{code: string; host: string; mapType: MapType; mode: string; victoryCondition: keyof typeof victoryLabels; players: number; maxPlayers: number; phase: string; allowSpectators: boolean; spectators: number}>>("ListGames");
    const listedGames = browserSpectating ? games.filter(game => game.phase === "playing" && game.allowSpectators) : games;
    element("browser-list").innerHTML = listedGames.length ? listedGames.map(game => `<article class="browser-game">
      <div><strong>${escapeHtml(game.host)} · ${escapeHtml(game.code)}</strong><small>${escapeHtml(mapLabels[game.mapType])} · ${escapeHtml(game.mode)} · ${victoryLabels[game.victoryCondition ?? "conquest"]} · ${game.players} players · ${game.phase === "lobby" ? "Waiting" : "In progress"}${game.spectators ? ` · ${game.spectators} watching` : ""}</small></div>
      ${game.phase === "lobby" ? `<button data-join="${escapeHtml(game.code)}" ${game.players >= game.maxPlayers ? "disabled" : ""}>${game.players >= game.maxPlayers ? "Full" : "Join"}</button>` : game.allowSpectators ? `<button data-watch="${escapeHtml(game.code)}">Spectate</button>` : "<span>Spectating off</span>"}
    </article>`).join("") : browserSpectating ? "<p>No public matches are available to watch right now. You can enter a room code above once that match has started and its host allows spectators.</p>" : "<p>No public games yet. Host a match to start one.</p>";
  } catch (error) { element("browser-list").textContent = `Could not load games: ${String(error)}`; }
  finally { browserLoading = false; }
}
function openGameBrowser(spectating: boolean): void {
  browserSpectating = spectating;
  element("browser-title").textContent = spectating ? "Spectate a match" : "Current games";
  element("browser-description").textContent = spectating ? "Choose a live public match below, or enter a room code to watch an invite-only match." : "Public games on this server. Invite-only games require a room code.";
  element("spectate-form").classList.toggle("hidden", !spectating);
  element("browser-error").textContent = "";
  element("browser-list").textContent = "Loading games…";
  element("browser-panel").classList.remove("hidden");
  const codeInput = element("spectate-room-code") as HTMLInputElement;
  if (spectating) { codeInput.value = roomCodeInput.value.trim().toUpperCase(); codeInput.focus(); }
  else buttonElement("browser-refresh").focus();
  void refreshGames();
}
element("browse-games").addEventListener("click", () => openGameBrowser(false));
element("browser-close").addEventListener("click", () => {
  element("browser-panel").classList.add("hidden");
  buttonElement(browserSpectating ? "spectate-code" : "browse-games").focus();
});
element("browser-refresh").addEventListener("click", () => void refreshGames());
element("spectate-code").addEventListener("click", () => openGameBrowser(true));
element("spectate-form").addEventListener("submit", async event => {
  event.preventDefault();
  const code = (element("spectate-room-code") as HTMLInputElement).value.trim().toUpperCase();
  if (!code) { element("browser-error").textContent = "Enter a room code to watch a match."; return; }
  const confirm = buttonElement("spectate-confirm");
  if (confirm.disabled) return;
  confirm.disabled = true;
  element("browser-error").textContent = "";
  try {
    const result = await connection.invoke<OperationResult>("Spectate", code);
    if (result.ok) element("browser-panel").classList.add("hidden");
    else element("browser-error").textContent = result.error ?? "Could not spectate this game.";
  } catch { element("browser-error").textContent = "Could not connect. Please try again."; }
  finally { confirm.disabled = false; }
});
setInterval(() => { if (!element("browser-panel").classList.contains("hidden")) void refreshGames(); }, 5000);
element("browser-list").addEventListener("click", async (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>("button[data-join], button[data-watch]");
  if (!button) return;
  button.disabled = true;
  try {
    const result = button.dataset.watch
      ? await connection.invoke<OperationResult>("Spectate", button.dataset.watch)
      : await connection.invoke<OperationResult>("JoinRoom", playerNameInput.value, button.dataset.join, playerSessionId);
    if (result.ok && result.code && result.resumeToken) sessionStorage.setItem(`room-token:${result.code}`, result.resumeToken);
    if (result.ok) element("browser-panel").classList.add("hidden");
    else { element("browser-error").textContent = result.error ?? "Could not enter game. Refresh and try again."; }
  } catch (error) { element("browser-error").textContent = String(error); }
  finally { button.disabled = false; }
});
element("leave-lobby").addEventListener("click", async () => {
  try {
    await connection.invoke("LeaveRoom");
    sessionStorage.removeItem(activeRoomSessionKey);
    activeRoomCode = undefined; lobby = undefined;
    lobbyScreen.classList.add("hidden"); menuScreen.classList.remove("hidden");
  } catch (error) { lobbyStatus.textContent = String(error); }
});
element("ready-lobby").addEventListener("click", async () => {
  try { const result = await connection.invoke<OperationResult>("SetReady", !lobby?.players.find(p => p.id === playerSessionId)?.isReady);
    if (!result.ok) lobbyStatus.textContent = result.error ?? "Could not update ready status.";
  } catch (error) { lobbyStatus.textContent = String(error); }
});

roomCodeInput.addEventListener("input", () => {
  roomCodeInput.value = roomCodeInput.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
});

copyCodeButton.addEventListener("click", async () => {
  if (!lobby) return;
  const inviteUrl = new URL(location.origin);
  inviteUrl.searchParams.set("join", lobby.code);
  await navigator.clipboard.writeText(inviteUrl.toString());
  lobbyStatus.textContent = "Friend invite link copied.";
});

addBotButton.addEventListener("click", async () => {
  try {
    const result = await connection.invoke<OperationResult>("AddBot");
    if (!result.ok) lobbyStatus.textContent = result.error ?? "Could not add AI.";
  } catch (error) {
    lobbyStatus.textContent = String(error);
  }
});

startButton.addEventListener("click", async () => {
  startButton.disabled = true;
  lobbyStatus.textContent = "Preparing the frontier…";
  try {
    const result = await connection.invoke<OperationResult>("StartMatch");
    if (!result.ok) {
      startButton.disabled = false;
      lobbyStatus.textContent = result.error ?? "Could not begin.";
    }
  } catch (error) {
    startButton.disabled = false;
    lobbyStatus.textContent = String(error);
  }
});

setInterval(async () => {
  if (connection.state !== HubConnectionState.Connected) return;
  const sentAt = Date.now();
  try {
    const echo = await connection.invoke<number>("PingCheck", sentAt);
    ping = Date.now() - echo;
    const indicator = document.querySelector("#ping-indicator");
    if (indicator) indicator.textContent = `${ping} ms`;
  } catch {
    // The reconnect callbacks provide the visible status.
  }
}, 2000);

function showLobby(state: LobbyState): void {
  menuScreen.classList.add("hidden");
  lobbyScreen.classList.remove("hidden");
  copyCodeButton.textContent = state.code;
  lobbyModeLabel.textContent = state.mode.toUpperCase();
  lobbyModeLabel.textContent += state.isPublic ? " · PUBLIC" : " · INVITE ONLY";
  lobbyMapLabel.textContent = `${mapLabels[state.mapType]?.toUpperCase() ?? state.mapType.toUpperCase()} · ${victoryLabels[state.victoryCondition ?? "conquest"]}`;
  const players = element("lobby-players");
  players.innerHTML = state.players
    .map(
      (player, index) => `
        <article class="lobby-player">
          <span class="player-color" style="--player-color:${player.color}"></span>
          <span class="lobby-color-square" style="--player-color:${player.color}" title="${player.colorChoice === "random" ? "Random colour" : player.colorChoice === "#4ba3ff" ? "Blue" : "Red"}">
            ${player.colorChoice === "random" ? "?" : ""}
            ${player.id === playerSessionId ? `<select class="lobby-color" aria-label="Your colour">
              ${[["random", "? Random"], ["#4ba3ff", "Blue"], ["#f0645a", "Red"]].map(([value,label]) =>
                `<option value="${value}" ${player.colorChoice === value ? "selected" : ""} ${value !== "random" && state.players.some(p => p.id !== player.id && p.colorChoice === value) ? "disabled" : ""}>${label}</option>`).join("")}
            </select>` : ""}
          </span>
          <div>
            <small>HOUSE ${index + 1}</small>
            <strong>${escapeHtml(player.name)}</strong>
          </div>
          <span class="player-role">${player.isBot ? "AI" : `${player.isAuthenticated ? "✓ " : ""}${player.isHost ? "HOST" : player.isReady ? "READY" : "CHOOSING"}`}</span>

        </article>
      `,
    )
    .join("");
  players.querySelector<HTMLSelectElement>(".lobby-color")?.addEventListener("change", async (event) => {
    try { const result = await connection.invoke<OperationResult>("SetColor", (event.target as HTMLSelectElement).value);
      if (!result.ok) { showLobby(state); lobbyStatus.textContent = result.error ?? "Could not change colour."; }
    } catch (error) { lobbyStatus.textContent = String(error); }
  });
  const isHost = state.players.some(
    (player) => player.id === playerSessionId && player.isHost,
  );
  addBotButton.classList.toggle("hidden", !isHost || state.players.length >= state.maxPlayers);
  if (state.mode === "ranked") addBotButton.classList.add("hidden");
  startButton.classList.toggle("hidden", !isHost);
  startButton.disabled = !state.canStart;
  element("ready-lobby").classList.toggle("hidden", isHost);
  element("ready-lobby").textContent = state.players.find(p => p.id === playerSessionId)?.isReady ? "Not ready" : "Ready";
  lobbyStatus.textContent = state.canStart
    ? "Both houses are assembled."
    : "Waiting for commanders to join and ready up…";
}

function milestoneMarkerSymbol(milestone: MatchMilestone): string {
  if (milestone.kind === "age") {
    const ageIndex = AGE_NAMES.findIndex((name) => milestone.text.includes(name));
    return ["", "I", "II", "III", "IV"][Math.max(1, ageIndex)] ?? "I";
  }
  if (["battle", "combat", "decisive"].includes(milestone.kind)) return "⚔";
  if (milestone.kind === "victory") return "♛";
  if (milestone.kind === "defeat") return "×";
  return "•";
}

function renderTimelineChart(
  report: MatchReport,
  players: MatchReport["players"],
  metric: "score" | "population",
): string {
  const width = 760;
  const height = 190;
  const left = 42;
  const right = 14;
  const top = 18;
  const bottom = 28;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const duration = Math.max(
    1,
    report.durationSeconds,
    ...report.timeline.map((point) => point.second),
  );
  const valueFor = (point: MatchTimelinePoint, playerId: string) => {
    const sample = point.players.find((player) => player.playerId === playerId);
    if (!sample) return 0;
    return metric === "score"
      ? sample.score
      : sample.army + sample.villagers;
  };
  const maximum = Math.max(
    1,
    ...report.timeline.flatMap((point) =>
      players.map((player) => valueFor(point, player.playerId)),
    ),
  );
  const x = (second: number) => left + (second / duration) * plotWidth;
  const y = (value: number) => top + plotHeight - (value / maximum) * plotHeight;
  const horizontalGrid = Array.from({ length: 5 }, (_, index) => {
    const value = Math.round((maximum * (4 - index)) / 4);
    const lineY = top + (plotHeight * index) / 4;
    return `<line x1="${left}" y1="${lineY}" x2="${width - right}" y2="${lineY}" />
      <text x="${left - 7}" y="${lineY + 3}" text-anchor="end">${value.toLocaleString()}</text>`;
  }).join("");
  const timeLabels = Array.from({ length: 5 }, (_, index) => {
    const second = (duration * index) / 4;
    const lineX = x(second);
    return `<line x1="${lineX}" y1="${top}" x2="${lineX}" y2="${top + plotHeight}" />
      <text x="${lineX}" y="${height - 7}" text-anchor="middle">${formatMatchTime(second)}</text>`;
  }).join("");
  const milestoneMarkers = report.milestones
    .filter((milestone) =>
      ["age", "battle", "combat", "decisive", "victory", "defeat"].includes(
        milestone.kind,
      ),
    )
    .map((milestone) => {
      const markerX = x(milestone.second);
      const player = players.find(
        (candidate) => candidate.playerId === milestone.playerId,
      );
      const color = /^#[0-9a-f]{6}$/i.test(player?.playerColor ?? "")
        ? player!.playerColor
        : "#d9bd75";
      const symbol = milestoneMarkerSymbol(milestone);
      return `<line class="milestone-line" x1="${markerX}" y1="${top}" x2="${markerX}" y2="${top + plotHeight}" style="--marker-color:${color}" />
        <circle class="milestone-dot" cx="${markerX}" cy="${top + 7}" r="7" style="--marker-color:${color}">
          <title>${escapeHtml(milestone.text)} · ${formatMatchTime(milestone.second)}</title>
        </circle>
        <text class="milestone-symbol" x="${markerX}" y="${top + 10}" text-anchor="middle">${symbol}</text>`;
    })
    .join("");
  const series = players
    .map((player) => {
      const color = /^#[0-9a-f]{6}$/i.test(player.playerColor)
        ? player.playerColor
        : "#d9bd75";
      const points = report.timeline.map((point) => ({
        x: x(point.second),
        y: y(valueFor(point, player.playerId)),
      }));
      if (points.length === 1) {
        points.push({ x: x(duration), y: points[0].y });
      }
      const pointList = points.map((point) => `${point.x},${point.y}`).join(" ");
      const area = [
        `M ${points[0]?.x ?? left} ${top + plotHeight}`,
        ...points.map((point) => `L ${point.x} ${point.y}`),
        `L ${points.at(-1)?.x ?? width - right} ${top + plotHeight}`,
        "Z",
      ].join(" ");
      return `<path class="timeline-area" d="${area}" fill="${color}" />
        <polyline class="timeline-series" points="${pointList}" stroke="${color}">
          <title>${escapeHtml(player.playerName)} · ${metric === "score" ? "score" : "population"}</title>
        </polyline>`;
    })
    .join("");
  const legend = players
    .map(
      (player) =>
        `<span style="--player-color:${escapeHtml(player.playerColor)}"><i></i>${escapeHtml(player.playerName)}</span>`,
    )
    .join("");
  return `<article class="timeline-chart-card">
    <header><strong>${metric === "score" ? "Score trajectory" : "Population over time"}</strong><div>${legend}</div></header>
    <svg class="timeline-chart" viewBox="0 0 ${width} ${height}" role="img" aria-label="${metric === "score" ? "Score" : "Population"} timeline">
      <g class="timeline-grid">${horizontalGrid}${timeLabels}</g>
      ${milestoneMarkers}
      ${series}
    </svg>
  </article>`;
}

function renderMatchSummary(report: MatchReport, targetId = "match-summary"): void {
  const players = [...report.players].sort(
    (left, right) => Number(right.won) - Number(left.won),
  );
  const gatheredTotal = (player: MatchReport["players"][number]) =>
    Object.values(player.resourcesGathered).reduce((sum, value) => sum + value, 0);
  const timeline = report.timeline.slice(-8).map((point) => {
    const ordered = [...point.players].sort((left, right) => right.score - left.score);
    const leader = players.find((player) => player.playerId === ordered[0]?.playerId);
    const lead = ordered.length > 1 ? ordered[0].score - ordered[1].score : 0;
    return `<div class="timeline-point">
      <time>${formatMatchTime(point.second)}</time>
      <span style="--player-color:${leader?.playerColor ?? "#d9bd75"}"></span>
      <div><strong>${escapeHtml(leader?.playerName ?? "Even")}</strong><small>${lead > 0 ? `+${lead.toLocaleString()} score` : "Even score"}</small></div>
    </div>`;
  }).join("");
  const milestones = report.milestones.slice(-10).map((milestone) => {
    const player = players.find((candidate) => candidate.playerId === milestone.playerId);
    return `<li><time>${formatMatchTime(milestone.second)}</time><i style="--player-color:${player?.playerColor ?? "#d9bd75"}"></i><b class="milestone-log-symbol">${milestoneMarkerSymbol(milestone)}</b><span>${escapeHtml(milestone.text)}</span></li>`;
  }).join("");
  element(targetId).innerHTML = `
    <div class="summary-meta">${mapLabels[report.mapType]} · ${report.mode.toUpperCase()} · ${victoryLabels[report.victoryCondition ?? "town_centres"]} · ${formatMatchTime(report.durationSeconds)}</div>
    <div class="summary-grid">
      ${players.map((player) => `
        <article style="--player-color:${escapeHtml(player.playerColor)}">
          <header><span></span><strong>${escapeHtml(player.playerName)}</strong><b>${player.won ? "WINNER" : "DEFEATED"}</b></header>
          <div class="summary-score">${player.finalScore.toLocaleString()}<small>FINAL SCORE</small></div>
          <dl>
            <div><dt>Military score</dt><dd>${player.militaryScore.toLocaleString()}</dd></div>
            <div><dt>Economy score</dt><dd>${player.economyScore.toLocaleString()}</dd></div>
            <div><dt>Technology score</dt><dd>${player.technologyScore.toLocaleString()}</dd></div>
            <div><dt>Army kills / losses</dt><dd>${player.enemyUnitsKilled} / ${player.unitsLost}</dd></div>
            <div><dt>Buildings razed / lost</dt><dd>${player.enemyBuildingsDestroyed} / ${player.buildingsLost}</dd></div>
            <div><dt>Damage dealt</dt><dd>${Math.round(player.damageDealt).toLocaleString()}</dd></div>
            <div><dt>Resources gathered</dt><dd>${Math.round(gatheredTotal(player)).toLocaleString()}</dd></div>
            <div><dt>Peak population</dt><dd>${player.peakPopulation}</dd></div>
            <div><dt>Final age</dt><dd>${player.finalAge}</dd></div>
          </dl>
        </article>`).join("")}
    </div>
    <section class="visual-timeline">
      <div class="visual-timeline-heading">
        <div><small>VISUAL TIMELINE</small><strong>How the match shifted</strong></div>
        <span><b>I–IV</b> Age advancement · <b>⚔</b> Battle</span>
      </div>
      ${renderTimelineChart(report, players, "score")}
      ${renderTimelineChart(report, players, "population")}
    </section>
    <details class="summary-timeline">
      <summary>Timeline samples and event log</summary>
      <div class="timeline-strip">${timeline || "<span>No minute samples recorded.</span>"}</div>
      <ol>${milestones || "<li>No major milestones recorded.</li>"}</ol>
    </details>`;
}

function productionQueueMarkup(snapshot: GameSnapshot): string {
  return snapshot.buildings.filter(building => building.ownerId === snapshot.you.id).flatMap(building => {
    const groups = new Map<string, number>();
    for (const item of building.queue) if (item.key.startsWith("unit:")) groups.set(item.key, (groups.get(item.key) ?? 0) + 1);
    return [...groups].map(([key, count]) => {
      const type = key.slice(5) as UnitType;
      const active = building.queue[0]?.key === key ? building.queue[0] : undefined;
      const progress = active ? Math.max(0, Math.min(100, (1 - active.remaining / Math.max(.001, active.total)) * 100)) : 0;
      const status = active ? snapshot.isPaused ? "Paused" : `${Math.ceil(active.remaining)}s` : "Waiting";
      const label = `${UNIT_DEFS[type].label} ×${count} · ${BUILDING_DEFS[building.buildingType].label} · ${status} · Click to select building`;
      return `<button type="button" class="global-production-item${active ? " active" : ""}" data-production-building="${escapeHtml(building.id)}" title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}">
        <span class="production-icon" aria-hidden="true">${unitGlyph(type)}</span><b class="production-count">${count}</b>
        <small>${unitRoleCode(type)} · ${status}</small><i style="width:${progress}%"></i></button>`;
    });
  }).join("");
}

function buildingQueueMarkup(building: BuildingState): string {
  const groups = new Map<string, typeof building.queue>();
  for (const item of building.queue) {
    const group = groups.get(item.key) ?? [];
    group.push(item);
    groups.set(item.key, group);
  }
  return [...groups.values()].map(items => {
    const first = items[0], cancel = items[items.length - 1];
    const active = building.queue[0] === first;
    const progress = active ? Math.max(0, Math.min(100, (1 - first.remaining / Math.max(.001, first.total)) * 100)) : 0;
    const label = `${first.label} ×${items.length} · ${active ? `${Math.ceil(first.remaining)}s` : "Waiting"} · Click to cancel and refund one · Shift-click cancels up to five`;
    return `<button type="button" data-action="cancel-queue:${building.id}:${cancel.id}" title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}"><i style="width:${progress}%"></i><span aria-hidden="true">${first.key.startsWith("unit:") ? unitGlyph(first.key.slice(5) as UnitType) : "♛"}</span><b aria-hidden="true">${items.length}</b></button>`;
  }).join("");
}

function ownerColor(snapshot: GameSnapshot, ownerId: string): string {
  return snapshot.players.find(player => player.id === ownerId)?.color
    ?? (snapshot.you.id === ownerId ? snapshot.you.color : "#999999");
}

function resourceWorkerCounts(snapshot: GameSnapshot): Record<ResourceKind, number> {
  const counts = { food: 0, wood: 0, gold: 0, stone: 0 };
  for (const unit of snapshot.units) {
    if (unit.ownerId === snapshot.you.id && unit.unitType === "villager" && unit.assignedResource)
      counts[unit.assignedResource]++;
  }
  return counts;
}

function updateHud(snapshot: GameSnapshot): void {
  const condition = snapshot.victoryCondition ?? "conquest";
  const hill = snapshot.hill;
  const holder = snapshot.players.find(p => p.id === hill?.ownerId);
  element("victory-status").textContent = hill ? `⚑ King of the Hill · ${hill.contested ? "Contested · paused" : holder?.name ?? "Unclaimed"} · ${formatMatchTime(Math.ceil(hill.remainingSeconds))}` : victoryLabels[condition];
  element("victory-status").title = victoryDescriptions[condition];
  element("objective-title").textContent = victoryLabels[condition];
  element("objective-description").textContent = victoryDescriptions[condition];
  reconcileMarkup(element("global-production"), productionQueueMarkup(snapshot));
  const roman = ["", "I", "II", "III", "IV"];
  element("age-number").textContent = roman[snapshot.you.age];
  element("age-name").textContent = AGE_NAMES[snapshot.you.age];
  if (snapshot.isSpectator) { element("age-name").textContent = "Spectating · Full map"; element("age-number").textContent = "◉"; }
  const activeAgeUp = snapshot.buildings
    .filter(
      (building) =>
        building.ownerId === snapshot.you.id &&
        building.buildingType === "town_center",
    )
    .flatMap((building) => building.queue)
    .find((item) => item.key.startsWith("age:"));
  const ageProgress = element("age-progress");
  if (activeAgeUp) {
    const progress = THREE.MathUtils.clamp(
      1 - activeAgeUp.remaining / Math.max(activeAgeUp.total, 0.001),
      0,
      1,
    );
    element("age-progress-fill").style.width = `${progress * 100}%`;
    element("age-progress-label").textContent =
      `${activeAgeUp.label} · ${Math.ceil(activeAgeUp.remaining)}s`;
    ageProgress.classList.remove("hidden");
  } else {
    ageProgress.classList.add("hidden");
  }
  element("room-indicator").textContent = `ROOM ${snapshot.roomCode}`;
  element("match-time-indicator").textContent = formatMatchTime(
    snapshot.tick / SIM_TICK_RATE,
  );
  const receivedAt = performance.now();
  const simulationRate = serverRate.sample(snapshot);
  const arrivalGap = lastSnapshotReceivedAt === undefined ? 0 : receivedAt - lastSnapshotReceivedAt;
  const rateIndicator = element("server-rate-indicator");
  rateIndicator.textContent = snapshot.isPaused ? "PAUSED"
    : snapshot.phase === "finished" ? "FINISHED"
    : simulationRate === undefined ? "… SIM" : `${simulationRate.toFixed(1)} SIM`;
  rateIndicator.title = `Server tick: ${(snapshot.serverTickMilliseconds ?? 0).toFixed(1)} ms / ${1000 / SIM_TICK_RATE} ms budget; packet gap: ${arrivalGap.toFixed(0)} ms (normally ${1000 / SNAPSHOT_RATE} ms)`;
  rateIndicator.classList.toggle("warning", snapshot.phase === "playing" && !snapshot.isPaused &&
    simulationRate !== undefined && simulationRate < SIM_TICK_RATE * 0.82);
  lastSnapshotReceivedAt = receivedAt;
  pauseToggle.classList.toggle("paused", snapshot.isPaused);
  pauseToggle.setAttribute(
    "aria-label",
    snapshot.isPaused ? "Resume match" : "Pause match",
  );
  pauseToggle.innerHTML = snapshot.isPaused
    ? "▶ RESUME <kbd>P</kbd>"
    : "⏸ PAUSE <kbd>P</kbd>";
  pauseOverlay.classList.toggle("hidden", !snapshot.isPaused);
  element("pause-owner").textContent = snapshot.pausedBy
    ? `${snapshot.pausedBy} paused the battle.`
    : "The battle is suspended.";
  const idleVillagers = snapshot.units.filter(
    (unit) =>
      unit.ownerId === snapshot.you.id &&
      unit.unitType === "villager" &&
      unit.state === "idle",
  ).length;
  element("idle-villager-count").textContent = String(idleVillagers);
  idleVillagerButton.disabled = idleVillagers === 0;
  idleVillagerButton.title = idleVillagers
    ? `Select next idle villager (${idleVillagers} available)`
    : "No idle villagers";
  element("scoreboard").innerHTML = [...snapshot.players]
    .sort(
      (left, right) =>
        right.score - left.score ||
        (left.id === snapshot.you.id
          ? -1
          : right.id === snapshot.you.id
            ? 1
            : left.name.localeCompare(right.name)),
    )
    .map(
      (player, index) => `
        <div class="score-row${player.id === snapshot.you.id ? " you" : ""}${player.defeated ? " defeated" : ""}">
          <span class="score-rank">${index + 1}</span>
          <i style="--player-color:${escapeHtml(player.color)}"></i>
          <div>
            <small>${player.id === snapshot.you.id ? "YOU" : player.isBot ? "AI RIVAL" : "RIVAL"}</small>
            <strong>${escapeHtml(player.name)}</strong>
          </div>
          <b>${player.defeated ? "DEFEATED" : player.score.toLocaleString()}</b>
        </div>`,
    )
    .join("");
  const workerCounts = resourceWorkerCounts(snapshot);
  element("resources").innerHTML = (
    Object.keys(snapshot.you.resources) as ResourceKind[]
  )
    .map(
      (kind) => `
        <div class="resource ${kind}">
          <span>${resourceGlyph(kind)}</span>
          <div><small>${RESOURCE_LABELS[kind]}</small><strong>${Math.floor(snapshot.you.resources[kind])}</strong></div>
          <em class="resource-workers" title="${workerCounts[kind]} villagers assigned to ${RESOURCE_LABELS[kind].toLowerCase()} (includes travel and drop-off)" aria-label="${workerCounts[kind]} villagers assigned to ${RESOURCE_LABELS[kind].toLowerCase()}"><i aria-hidden="true">♟</i>${workerCounts[kind]}</em>
        </div>`,
    )
    .join("") +
    `<div class="resource population"><span>♟</span><div><small>Population</small><strong>${snapshot.you.population}/${snapshot.you.populationCap}</strong></div></div>`;

  for (const event of snapshot.events) {
    if (notifiedEvents.has(event.id)) continue;
    notifiedEvents.add(event.id);
    if (event.eventType === "under_attack") {
      gameView?.triggerAttackAlert(event);
    }
    showEvent(event.message, event.tone, event.eventType !== "under_attack",
      event.eventType === "no_route" ? () => gameView?.locateEventUnit(event) : undefined);
  }
  if (notifiedEvents.size > 120) {
    notifiedEvents = new Set(snapshot.events.map((event) => event.id));
  }
  if (snapshot.phase === "finished") {
    const won = snapshot.winnerId === snapshot.you.id;
    element("end-screen").classList.remove("hidden");
    element("end-title").textContent = snapshot.isSpectator ? "Match finished" : won ? "Victory" : "Defeat";
    element("end-copy").textContent = won
      ? "Your rival's seat has fallen. The frontier kneels."
      : "Your town centres are lost. The rival banner claims the valley.";
    if (snapshot.isSpectator) element("end-copy").textContent = `${snapshot.players.find(p => p.id === snapshot.winnerId)?.name ?? "The winner"} wins the match.`;
    const outcome = won ? "victory" : "defeat";
    if (announcedOutcome !== outcome) {
      announcedOutcome = outcome;
      if (snapshot.matchReport) renderMatchSummary(snapshot.matchReport);
      if (!snapshot.isSpectator) { if (won) soundDesign.victory(); else soundDesign.defeat(); }
    }
  }
}

function showEvent(
  message: string,
  tone: "info" | "warning" | "success",
  playSound = true,
  onClick?: () => void,
): void {
  if (playSound && tone === "warning") soundDesign.warning();
  if (playSound && tone === "success") {
    soundDesign.complete();
  }
  const feed = element("event-feed");
  const notification = document.createElement(onClick ? "button" : "div");
  notification.className = `event ${tone}`;
  notification.textContent = message;
  if (notification instanceof HTMLButtonElement) {
    notification.type = "button";
    notification.classList.add("event-action");
    notification.addEventListener("click", () => { onClick?.(); notification.remove(); });
  }
  feed.append(notification);
  window.setTimeout(() => notification.classList.add("leaving"), onClick ? 15000 : 4200);
  window.setTimeout(() => notification.remove(), onClick ? 15500 : 4700);
}

function showMenuError(message: string): void {
  statusLine.textContent = message;
  statusLine.className = "status-line warning";
}

class GameView {
  private readonly scene = new THREE.Scene();
  private readonly destructionEffects = new DestructionEffects(this.scene);
  private readonly buildingDamage = new Map<string, BuildingDamage>();
  private readonly visibleDamagedBuildings = new Set<string>();
  private readonly camera = new THREE.PerspectiveCamera(47, 1, 0.1, 300);
  private readonly renderer = new THREE.WebGLRenderer({
    antialias: true,
    powerPreference: "high-performance",
  });
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly ground: THREE.Mesh;
  private readonly entityObjects = new Map<string, THREE.Object3D>();
  private readonly entityStates = new Map<string, RenderState>();
  private readonly snapshotMotion = new SnapshotMotion();
  private readonly selectedIds = new Set<string>();
  private readonly controlGroups = new Map<number, string[]>();
  private readonly keys = new Set<string>();
  private readonly edgePanInput = new THREE.Vector2();
  private readonly cameraTarget = new THREE.Vector3(18, 0, 18);
  private readonly cameraForward = new THREE.Vector3(-0.7, 0, -0.72).normalize();
  private readonly cameraRight = new THREE.Vector3(0.72, 0, -0.7).normalize();
  private readonly cameraMove = new THREE.Vector3();
  private readonly exploredCells = new Set<string>();
  private readonly lastUnitStates = new Map<string, UnitState["state"]>();
  private readonly orderMarkers: {
    mesh: THREE.Mesh;
    startedAt: number;
  }[] = [];
  private lastCombatEventId = 0;
  private wallPreviewKey = "";
  private dragAdditive = false;
  private siegeTargetMode?: "ground" | "trees";
  private attackMoveMode = false;
  private garrisonMode = false;
  private orderFlags = new Map<string, THREE.Group>();
  private readonly projectiles: {
    mesh: THREE.Mesh;
    pulse?: THREE.Mesh;
    fragments?: THREE.InstancedMesh;
    start: THREE.Vector3;
    end: THREE.Vector3;
    startedAt: number;
    duration: number;
    splashRadius?: number;
  }[] = [];
  private cameraDistance = 42;
  private workerBuildMenu?: "eco" | "military";
  private placementType?: BuildingType;
  private placementGhost?: THREE.Group;
  private wallDragStart?: Vec2;
  private wallDragEnd?: Vec2;
  private wallLineGhosts: THREE.Group[] = [];
  private wallLinePoints: Vec2[] = [];
  private wallLineValid = false;
  private wallLineReason = "";
  private rallyPlacementBuildingId?: string;
  private rallyPreview?: THREE.Group;
  private rallyIndicator?: THREE.Group;
  private rallyIndicatorKey = "";
  private placementValid = false;
  private placementReason = "";
  private hoveredId?: string;
  private lastPointerPosition?: { clientX: number; clientY: number };
  private lastCombatSoundAt = 0;
  private attackLocation?: Vec2;
  private attackAlertTimeout?: number;
  private dragStart?: { x: number; y: number };
  private dragMoved = false;
  private lastFrame = performance.now();
  private fpsSampleStartedAt = performance.now();
  private fpsFrameCount = 0;
  private pausedAnimationTime?: number;
  private hillMarker?: THREE.Group;
  private snapshot: GameSnapshot;
  private fogTexture?: THREE.CanvasTexture;
  private readonly fogCanvas = document.createElement("canvas");
  private readonly minimapContext = minimap.getContext("2d")!;

  constructor(
    private readonly container: HTMLElement,
    private readonly gameConnection: HubConnection,
    initialSnapshot: GameSnapshot,
  ) {
    this.snapshot = initialSnapshot;
    const ownTownCenter = initialSnapshot.buildings.find(
      (building) =>
        building.ownerId === initialSnapshot.you.id &&
        building.buildingType === "town_center",
    );
    if (ownTownCenter) {
      this.cameraTarget.set(ownTownCenter.x, 0, ownTownCenter.z);
    }
    this.scene.background = new THREE.Color("#9eb689");
    this.scene.fog = new THREE.Fog("#aebc9f", 75, 150);
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 1.75));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.domElement.id = "game-canvas";
    this.container.append(this.renderer.domElement);

    const hemisphere = new THREE.HemisphereLight(0xfff5d5, 0x40523a, 2.1);
    this.scene.add(hemisphere);
    const sun = new THREE.DirectionalLight(0xffefcb, 2.2);
    sun.position.set(35, 60, 15);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.left = -55;
    sun.shadow.camera.right = 55;
    sun.shadow.camera.top = 55;
    sun.shadow.camera.bottom = -55;
    this.scene.add(sun);

    const groundMaterial = new THREE.MeshStandardMaterial({
      color: 0x789b62,
      roughness: 1,
    });
    this.ground = new THREE.Mesh(
      new THREE.PlaneGeometry(initialSnapshot.mapSize, initialSnapshot.mapSize),
      groundMaterial,
    );
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.position.set(
      initialSnapshot.mapSize / 2,
      -0.02,
      initialSnapshot.mapSize / 2,
    );
    this.ground.receiveShadow = true;
    this.ground.userData.ground = true;
    this.scene.add(this.ground);
    this.addTerrainTiles(initialSnapshot.terrainTiles, initialSnapshot.mapSize);

    const grid = new THREE.GridHelper(
      initialSnapshot.mapSize,
      initialSnapshot.mapSize / NAVIGATION_CELL_SIZE,
      0x476844,
      0x668657,
    );
    grid.position.set(
      initialSnapshot.mapSize / 2,
      0.01,
      initialSnapshot.mapSize / 2,
    );
    (grid.material as THREE.Material).transparent = true;
    (grid.material as THREE.Material).opacity = 0.12;
    this.scene.add(grid);
    this.addMapBorder(initialSnapshot.mapSize);
    this.setupFog(initialSnapshot.mapSize);
    this.bindInput();
    attackAlert.addEventListener("click", () => {
      if (!this.attackLocation) return;
      this.cameraTarget.x = this.attackLocation.x;
      this.cameraTarget.z = this.attackLocation.z;
      attackAlert.classList.add("hidden");
      soundDesign.click();
    });
    this.resize();
    new ResizeObserver(() => this.resize()).observe(this.container);
    requestAnimationFrame((time) => this.render(time));
  }

  locateEventUnit(event: GameEvent): void {
    const unit = event.unitId ? this.entityStates.get(event.unitId) : undefined;
    if (unit?.kind === "unit" && unit.ownerId === this.snapshot.you.id) {
      this.selectedIds.clear();
      this.selectedIds.add(unit.id);
      this.refreshSelection();
      this.centerOnSelection();
      this.playSelectionSound();
    } else if (event.x != null && event.z != null) {
      this.cameraTarget.set(event.x, 0, event.z);
      showEvent("That unit is no longer available. Showing its last reported position.", "info", false);
    }
  }

  triggerAttackAlert(event: GameEvent): void {
    if (event.x === undefined || event.z === undefined) return;
    this.attackLocation = { x: event.x, z: event.z };
    attackAlert.classList.remove("hidden");
    this.showOrderMarker(this.attackLocation, 0xff3f36);
    soundDesign.warDeclaration();
    if (this.attackAlertTimeout !== undefined) {
      window.clearTimeout(this.attackAlertTimeout);
    }
    this.attackAlertTimeout = window.setTimeout(
      () => attackAlert.classList.add("hidden"),
      6500,
    );
  }

  selectNextIdleVillager(): void {
    const idleVillagers = this.snapshot.units.filter(
      (unit) =>
        unit.ownerId === this.snapshot.you.id &&
        unit.unitType === "villager" &&
        unit.state === "idle",
    );
    if (idleVillagers.length === 0) return;
    const currentIndex = idleVillagers.findIndex((unit) =>
      this.selectedIds.has(unit.id),
    );
    const next = idleVillagers[(currentIndex + 1) % idleVillagers.length];
    this.selectIds([next.id]);
    this.centerOnSelection();
    soundDesign.select("villager");
  }

  applySnapshot(snapshot: GameSnapshot): void {
    this.snapshotMotion.push(snapshot, performance.now());
    if (snapshot.phase === "finished" && deleteConfirmation.open) deleteConfirmation.close("cancel");
    if (snapshot.you.age > this.snapshot.you.age) soundDesign.ageUp();
    if (snapshot.isPaused && !this.snapshot.isPaused) {
      this.pausedAnimationTime = performance.now();
    } else if (!snapshot.isPaused) {
      this.pausedAnimationTime = undefined;
    }
    this.snapshot = snapshot;
    if (this.hillMarker) this.hillMarker.visible = !!snapshot.hill;
    if (snapshot.hill) {
      if (!this.hillMarker) {
        this.hillMarker = new THREE.Group();
        const ring = new THREE.Mesh(new THREE.RingGeometry(7.75, 8, 64), new THREE.MeshBasicMaterial({ color: 0xe5be65, side: THREE.DoubleSide, depthTest: false }));
        ring.rotation.x = -Math.PI / 2;
        ring.position.y = 0.12;
        const monument = new THREE.Mesh(new THREE.CylinderGeometry(.35, .8, 3, 6), new THREE.MeshStandardMaterial({ color: 0xe5be65 }));
        monument.position.y = 1.5;
        this.hillMarker.add(ring, monument);
        this.hillMarker.position.set(60, 0, 60);
        this.scene.add(this.hillMarker);
      }
      const color = snapshot.players.find(p => p.id === snapshot.hill?.ownerId)?.color ?? "#e5be65";
      for (const mesh of this.hillMarker.children as THREE.Mesh[]) (mesh.material as THREE.MeshBasicMaterial).color.set(color);
    }
    this.visibleDamagedBuildings.clear();
    const visibleCells = new Set(snapshot.visibleCells);
    for (const building of snapshot.buildings) {
      if (building.ownerId === snapshot.you.id || snapshot.isSpectator || snapshot.phase === "finished" ||
          visibleCells.has(`${Math.floor(building.x / VISION_CELL_SIZE)},${Math.floor(building.z / VISION_CELL_SIZE)}`)) {
        this.visibleDamagedBuildings.add(building.id);
      }
    }
    for (const unit of snapshot.units) {
      const previous = this.lastUnitStates.get(unit.id);
      if (
        unit.ownerId === snapshot.you.id &&
        !snapshot.combatEvents && unit.state === "attacking" &&
        previous !== "attacking" &&
        performance.now() - this.lastCombatSoundAt > 450
      ) {
        soundDesign.combat(unit.unitType === "archer");
        this.lastCombatSoundAt = performance.now();
      }
      this.lastUnitStates.set(unit.id, unit.state);
    }
    for (const event of snapshot.destructionEvents ?? []) {
      this.destructionEffects.add(event, this.entityObjects.get(event.entityId));
    }
    const incoming = new Set<string>();
    const states: RenderState[] = [
      ...snapshot.resources,
      ...snapshot.buildings,
      ...snapshot.units,
    ];
    for (const state of states) {
      incoming.add(state.id);
      this.entityStates.set(state.id, state);
      let object = this.entityObjects.get(state.id);
      if (!object) {
        object = this.createObject(state);
        this.entityObjects.set(state.id, object);
        this.scene.add(object);
      }
      this.updateObject(object, state);
    }
    for (const [id, object] of this.entityObjects) {
      if (!incoming.has(id)) {
        const rememberedState = this.entityStates.get(id);
        const cell = rememberedState
          ? `${Math.floor(rememberedState.x / VISION_CELL_SIZE)},${Math.floor(rememberedState.z / VISION_CELL_SIZE)}`
          : "";
        const rememberLastKnownState =
          snapshot.phase !== "finished" &&
          rememberedState !== undefined &&
          (rememberedState.kind === "resource" ||
            (rememberedState.kind === "building" &&
              rememberedState.ownerId !== snapshot.you.id)) &&
          !snapshot.visibleCells.includes(cell);
        if (rememberLastKnownState) {
          this.buildingDamage.get(id)?.update(this.pausedAnimationTime ?? performance.now(), false);
          continue;
        }
        this.buildingDamage.get(id)?.dispose();
        this.buildingDamage.delete(id);
        this.scene.remove(object);
        this.entityObjects.delete(id);
        this.entityStates.delete(id);
        this.selectedIds.delete(id);
      }
    }
    for (const [number, ids] of this.controlGroups) {
      const remaining = ids.filter((id) => {
        const state = this.entityStates.get(id);
        return (
          state &&
          state.kind !== "resource" &&
          state.ownerId === snapshot.you.id
        );
      });
      if (remaining.length > 0) this.controlGroups.set(number, remaining);
      else this.controlGroups.delete(number);
    }
    for (const cell of snapshot.exploredCells) this.exploredCells.add(cell);
    this.updateWallConnections();
    this.spawnRangedProjectiles(snapshot);
    this.drawFog();
    this.drawMinimap();
    this.updateSelectionHud();
    this.updateControlHotbar();
    this.updateRallyIndicator();
    if (this.lastPointerPosition) {
      this.updatePointerFeedback(this.lastPointerPosition);
    }
  }

  private addTerrainTiles(
    tiles: TerrainTileState[],
    mapSize: number,
  ): void {
    const loader = new THREE.TextureLoader();
    const base = "/assets/kenney-medieval-rts/Default%20size/Tile";
    loader.load(`${base}/medievalTile_57.png`, (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.magFilter = THREE.NearestFilter;
      texture.minFilter = THREE.NearestFilter;
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(
        mapSize / TERRAIN_TILE_SIZE,
        mapSize / TERRAIN_TILE_SIZE,
      );
      const material = this.ground.material as THREE.MeshStandardMaterial;
      material.map = texture;
      // Preserve the game's muted frontier palette while retaining the
      // hand-painted variation from the Kenney grass tile.
      material.color.setHex(0x8fa488);
      material.needsUpdate = true;
    });

    const geometry = new THREE.PlaneGeometry(
      TERRAIN_TILE_SIZE,
      TERRAIN_TILE_SIZE,
    );
    const byVariant = new Map<number, TerrainTileState[]>();
    for (const tile of tiles) {
      const collection = byVariant.get(tile.variant) ?? [];
      collection.push(tile);
      byVariant.set(tile.variant, collection);
    }
    for (const [variant, matchingTiles] of byVariant) {
      const filename = `medievalTile_${String(variant).padStart(2, "0")}.png`;
      loader.load(`${base}/${filename}`, (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.magFilter = THREE.NearestFilter;
        texture.minFilter = THREE.NearestFilter;
        const isRoad = matchingTiles[0]?.kind === "road";
        const material = new THREE.MeshStandardMaterial({
          map: texture,
          transparent: isRoad,
          alphaTest: isRoad ? 0.08 : 0,
          depthWrite: !isRoad,
          roughness: 1,
          side: THREE.DoubleSide,
        });
        for (const tile of matchingTiles) {
          const mesh = new THREE.Mesh(geometry, material);
          mesh.rotation.x = -Math.PI / 2;
          mesh.position.set(tile.x, isRoad ? 0.018 : 0.012, tile.z);
          mesh.receiveShadow = true;
          mesh.renderOrder = isRoad ? 2 : 1;
          mesh.userData.ground = true;
          this.scene.add(mesh);
        }
      });
    }
  }

  private createObject(state: RenderState): THREE.Object3D {
    if (state.kind === "resource") return this.createResource(state);
    const group = new THREE.Group();
    group.userData.entityId = state.id;
    group.userData.clickable = true;
    const isFriendly = state.ownerId === this.snapshot.you.id;
    const teamColor = new THREE.Color(
      ownerColor(this.snapshot, state.ownerId),
    );
    const actorVisual = new THREE.Group();
    actorVisual.name = "actor-visual";
    const visualContainer = state.kind === "unit" ? actorVisual : group;
    if (state.kind === "unit") group.add(actorVisual);
    if (state.kind === "unit" && state.unitType === "bombard_cannon") {
      visualContainer.add(createBombardCannon(teamColor));
    } else if (state.kind === "unit" && state.unitType === "onager") {
      visualContainer.add(createOnager(teamColor));
    } else if (state.kind === "unit") {
      visualContainer.add(createSoldier(state.unitType, teamColor));
    } else {
      const buildingVisual = this.createStructureVisual(state.buildingType, teamColor, state.maxHp > BUILDING_DEFS[state.buildingType].hp);
      group.userData.stoneFortification = state.maxHp > BUILDING_DEFS[state.buildingType].hp;
      buildingVisual.name = "building-visual";
      group.add(buildingVisual);
    }
    if (state.kind === "unit") {
      const isBombard = state.unitType === "bombard_cannon" || state.unitType === "onager";
      const hitbox = new THREE.Mesh(
        new THREE.BoxGeometry(
          isBombard ? 2.5 : 1.65,
          isBombard ? 2.4 : 3.35,
          isBombard ? 2.5 : 1.65,
        ),
        new THREE.MeshBasicMaterial({
          transparent: true,
          opacity: 0,
          depthWrite: false,
          colorWrite: false,
        }),
      );
      hitbox.name = "unit-hitbox";
      hitbox.position.y = isBombard ? 1.2 : 1.85;
      hitbox.userData.entityId = state.id;
      hitbox.userData.clickable = true;
      group.add(hitbox);
      const squiggle = this.createActionSquiggle();
      squiggle.name = "action-squiggle";
      squiggle.visible = false;
      group.add(squiggle);
      const roleBadge = this.createIconBadge(
        unitRoleCode(state.unitType),
        `#${teamColor.getHexString()}`,
        46,
      );
      roleBadge.name = "unit-role-badge";
      roleBadge.position.set(-0.78, 3.45, 0);
      roleBadge.scale.set(1.08, 1.08, 1);
      roleBadge.raycast = () => {};
      group.add(roleBadge);
    } else {
      const constructionBadge = this.createIconBadge("🔨");
      constructionBadge.name = "construction-badge";
      constructionBadge.position.y =
        BUILDING_DEFS[state.buildingType].size * 1.36;
      constructionBadge.userData.entityId = state.id;
      constructionBadge.userData.clickable = true;
      constructionBadge.visible = !state.completed;
      group.add(constructionBadge);
      const repairBadge = this.createIconBadge("🔧", "#e7b95b");
      repairBadge.name = "repair-badge";
      repairBadge.position.y =
        BUILDING_DEFS[state.buildingType].size * 1.36;
      repairBadge.userData.entityId = state.id;
      repairBadge.userData.clickable = true;
      repairBadge.visible =
        state.completed &&
        state.hp < state.maxHp &&
        state.ownerId === this.snapshot.you.id;
      group.add(repairBadge);
      if (state.buildingType === "farm") {
        const depletedBadge = this.createIconBadge("🌱", "#d7b56d");
        depletedBadge.name = "farm-depleted-badge";
        depletedBadge.position.y =
          BUILDING_DEFS[state.buildingType].size * 1.36;
        depletedBadge.userData.entityId = state.id;
        depletedBadge.userData.clickable = true;
        depletedBadge.visible =
          state.completed && (state.foodRemaining ?? 0) <= 0;
        group.add(depletedBadge);
      }
      if (BUILDING_DEFS[state.buildingType].defensiveRange > 0) {
        const range = createDefensiveRange(BUILDING_DEFS[state.buildingType].defensiveRange);
        range.visible = state.completed && isFriendly;
        group.add(range);
      }
    }
    const markerRadius =
      state.kind === "unit"
        ? state.unitType === "bombard_cannon" || state.unitType === "onager"
          ? 1.05
          : 0.72
        : BUILDING_DEFS[state.buildingType].size * 0.52;
    const isPalisadeMarker = state.kind === "building";
    const markerWidth = state.kind === "building" ? buildingHalf(state.buildingType).x * 2 : markerRadius * 2;
    const markerDepth = state.kind === "building" ? buildingHalf(state.buildingType).z * 2 : markerRadius * 2;
    const marker = new THREE.Mesh(
      isPalisadeMarker
        ? this.createRectRingGeometry(
            markerWidth,
            markerDepth,
            0.12,
          )
        : new THREE.RingGeometry(markerRadius * 0.78, markerRadius, 32),
      new THREE.MeshBasicMaterial({
        color: teamColor,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 1,
        depthTest: false,
      }),
    );
    marker.name = "team-marker";
    marker.rotation.x = -Math.PI / 2;
    marker.position.y = 0.035;
    marker.renderOrder = 5;
    group.add(marker);
    const teamWash = new THREE.Mesh(
      isPalisadeMarker
        ? new THREE.PlaneGeometry(
            markerWidth * 0.82,
            markerDepth * 0.82,
          )
        : new THREE.CircleGeometry(markerRadius * 0.74, 32),
      new THREE.MeshBasicMaterial({
        color: teamColor,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: isFriendly ? 0.08 : 0.13,
        depthTest: false,
      }),
    );
    teamWash.name = "team-wash";
    teamWash.rotation.x = -Math.PI / 2;
    teamWash.position.y = 0.03;
    teamWash.renderOrder = 4;
    group.add(teamWash);
    const selection = new THREE.Mesh(
      isPalisadeMarker
        ? this.createRectRingGeometry(
            markerWidth + 0.28,
            markerDepth + 0.28,
            0.11,
          )
        : new THREE.RingGeometry(markerRadius * 1.02, markerRadius * 1.12, 36),
      new THREE.MeshBasicMaterial({
        color: isFriendly ? 0xffe28a : 0xff8a80,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.95,
      }),
    );
    selection.name = "selection-ring";
    selection.rotation.x = -Math.PI / 2;
    selection.position.y = 0.05;
    selection.visible = false;
    group.add(selection);
    const hover = new THREE.Mesh(
      isPalisadeMarker
        ? this.createRectRingGeometry(
            markerWidth + 0.52,
            markerDepth + 0.52,
            0.13,
          )
        : new THREE.RingGeometry(markerRadius * 1.15, markerRadius * 1.34, 36),
      new THREE.MeshBasicMaterial({
        color: 0xff4f45,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.95,
      }),
    );
    hover.name = "hover-ring";
    hover.rotation.x = -Math.PI / 2;
    hover.position.y = 0.06;
    hover.visible = false;
    group.add(hover);
    return group;
  }

  private createRectRingGeometry(
    width: number,
    depth: number,
    thickness: number,
  ): THREE.ShapeGeometry {
    const halfWidth = width / 2;
    const halfDepth = depth / 2;
    const innerWidth = Math.max(0.02, halfWidth - thickness);
    const innerDepth = Math.max(0.02, halfDepth - thickness);
    const shape = new THREE.Shape();
    shape.moveTo(-halfWidth, -halfDepth);
    shape.lineTo(halfWidth, -halfDepth);
    shape.lineTo(halfWidth, halfDepth);
    shape.lineTo(-halfWidth, halfDepth);
    shape.closePath();
    const hole = new THREE.Path();
    hole.moveTo(-innerWidth, -innerDepth);
    hole.lineTo(-innerWidth, innerDepth);
    hole.lineTo(innerWidth, innerDepth);
    hole.lineTo(innerWidth, -innerDepth);
    hole.closePath();
    shape.holes.push(hole);
    return new THREE.ShapeGeometry(shape);
  }


  private createStructureVisual(type: BuildingType, teamColor: THREE.Color, stone = this.snapshot.you.age >= 3): THREE.Group {
    const visual = new THREE.Group();
    const half = buildingHalf(type);
    if (stone && (type === "wall" || type === "gate")) visual.add(createStoneFortification(type, teamColor));
    else if (type === "wall") visual.add(this.createWallVisual(teamColor));
    else if (type === "gate") visual.add(this.createGateVisual(teamColor));
    else if (type === "farm") {
      const farm = this.createFarmVisual(teamColor);
      const bounds = new THREE.Box3().setFromObject(farm).getSize(new THREE.Vector3());
      farm.scale.set(half.x * 2 / bounds.x, 1, half.z * 2 / bounds.z);
      visual.add(farm);
    } else visual.add(createBuildingVisual(type, half.x * 2, half.z * 2, teamColor));
    return visual;
  }

  private createFarmVisual(teamColor: THREE.Color): THREE.Group {
    const group = new THREE.Group();
    const soil = new THREE.Mesh(
      new THREE.BoxGeometry(3.2, 0.14, 3.2),
      new THREE.MeshStandardMaterial({
        color: 0x76502f,
        roughness: 1,
      }),
    );
    soil.position.y = 0.08;
    group.add(soil);

    for (let row = -1; row <= 1; row += 1) {
      const furrow = new THREE.Mesh(
        new THREE.BoxGeometry(0.42, 0.18, 2.65),
        new THREE.MeshStandardMaterial({
          color: row % 2 === 0 ? 0x9a6b3b : 0x865a33,
          roughness: 1,
        }),
      );
      furrow.position.set(row * 0.82, 0.2, 0);
      group.add(furrow);
      for (let crop = -2; crop <= 2; crop += 1) {
        const plant = new THREE.Mesh(
          new THREE.ConeGeometry(0.16, 0.55, 5),
          new THREE.MeshStandardMaterial({
            color: crop % 2 === 0 ? 0x8eb84d : 0xb1c95b,
            roughness: 0.95,
          }),
        );
        plant.name = "farm-crop";
        plant.position.set(row * 0.82, 0.52, crop * 0.52);
        group.add(plant);
      }
    }

    const cornerMaterial = new THREE.MeshStandardMaterial({
      color: teamColor,
      roughness: 0.8,
    });
    for (const [x, z] of [
      [-1.68, -1.68],
      [1.68, -1.68],
      [-1.68, 1.68],
      [1.68, 1.68],
    ] as [number, number][]) {
      const post = new THREE.Mesh(
        new THREE.BoxGeometry(0.14, 0.7, 0.14),
        cornerMaterial,
      );
      post.position.set(x, 0.35, z);
      group.add(post);
    }
    group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    return group;
  }

  private createWallVisual(teamColor: THREE.Color): THREE.Group {
    const group = new THREE.Group();
    group.name = "wall-visual";
    const timber = new THREE.MeshStandardMaterial({
      color: 0x76502f,
      roughness: 1,
    });
    const darkTimber = new THREE.MeshStandardMaterial({
      color: 0x4d3423,
      roughness: 1,
    });
    const teamMaterial = new THREE.MeshStandardMaterial({
      color: teamColor,
      roughness: 0.75,
    });
    for (let index = -2; index <= 2; index += 1) {
      const material = index === -2 || index === 2 ? teamMaterial : timber;
      const post = new THREE.Mesh(
        new THREE.CylinderGeometry(0.13, 0.16, 1.75, 6),
        material,
      );
      post.position.set(index * 0.28, 0.83, 0);
      post.castShadow = true;
      group.add(post);
      const point = new THREE.Mesh(
        new THREE.ConeGeometry(0.135, 0.38, 6),
        material,
      );
      point.position.set(index * 0.28, 1.88, 0);
      point.castShadow = true;
      group.add(point);
    }
    for (const height of [0.58, 1.15]) {
      const rail = new THREE.Mesh(
        new THREE.BoxGeometry(1.4, 0.14, 0.18),
        darkTimber,
      );
      rail.position.set(0, height, -0.11);
      rail.castShadow = true;
      group.add(rail);
    }
    const connectors = new THREE.Group();
    connectors.name = "wall-connectors";
    group.add(connectors);
    return group;
  }

  private createGateVisual(teamColor: THREE.Color): THREE.Group {
    const group = new THREE.Group();
    group.name = "gate-visual";
    const timber = new THREE.MeshStandardMaterial({
      color: 0x76502f,
      roughness: 1,
    });
    const teamMaterial = new THREE.MeshStandardMaterial({
      color: teamColor,
      roughness: 0.78,
    });
    for (const side of [-1, 1]) {
      const post = new THREE.Mesh(
        new THREE.BoxGeometry(0.34, 2.7, 0.42),
        teamMaterial,
      );
      post.position.set(side * 1.45, 1.35, 0);
      post.castShadow = true;
      group.add(post);
      const door = new THREE.Group();
      door.name = side < 0 ? "gate-left" : "gate-right";
      door.position.set(side * 1.35, 0, 0);
      for (let index = 0; index < 4; index += 1) {
        const plank = new THREE.Mesh(
          new THREE.BoxGeometry(0.34, 2.15, 0.22),
          timber,
        );
        plank.position.set(-side * (0.2 + index * 0.34), 1.08, 0);
        plank.castShadow = true;
        door.add(plank);
      }
      group.add(door);
    }
    const lintel = new THREE.Mesh(
      new THREE.BoxGeometry(3.35, 0.3, 0.5),
      teamMaterial,
    );
    lintel.position.y = 2.55;
    lintel.castShadow = true;
    group.add(lintel);
    for (const side of [-1, 1]) {
      for (const height of [0.58, 1.15]) {
        const rail = new THREE.Mesh(
          new THREE.BoxGeometry(0.95, 0.14, 0.18),
          timber,
        );
        rail.position.set(side * 1.92, height, -0.11);
        rail.castShadow = true;
        group.add(rail);
      }
    }
    return group;
  }

  private updateWallConnections(): void {
    const walls = [...this.entityStates.values()].filter(
      (state): state is BuildingState =>
        state.kind === "building" && state.buildingType === "wall",
    );
    for (const wall of walls) {
      const object = this.entityObjects.get(wall.id);
      const connectorGroup = object?.getObjectByName("wall-connectors");
      if (!(connectorGroup instanceof THREE.Group)) continue;
      const branchSigns = new Set<number>();
      for (const candidate of walls) {
        if (
          candidate.id === wall.id ||
          candidate.ownerId !== wall.ownerId ||
          Math.hypot(candidate.x - wall.x, candidate.z - wall.z) >
            NAVIGATION_CELL_SIZE * 1.12
        ) {
          continue;
        }
        const deltaX = candidate.x - wall.x;
        const deltaZ = candidate.z - wall.z;
        const localX =
          Math.cos(wall.facing) * deltaX -
          Math.sin(wall.facing) * deltaZ;
        const localZ =
          Math.sin(wall.facing) * deltaX +
          Math.cos(wall.facing) * deltaZ;
        if (Math.abs(localZ) > Math.abs(localX) * 0.6) {
          branchSigns.add(Math.sign(localZ) || 1);
        }
      }
      const stone = wall.maxHp > BUILDING_DEFS.wall.hp;
      const signature = `${stone}:` + [...branchSigns].sort().join(",");
      if (connectorGroup.userData.signature === signature) continue;
      connectorGroup.userData.signature = signature;
      while (connectorGroup.children.length > 0) {
        const child = connectorGroup.children[0];
        connectorGroup.remove(child);
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          const materials = Array.isArray(child.material)
            ? child.material
            : [child.material];
          for (const material of materials) material.dispose();
        }
      }
      const timber = new THREE.MeshStandardMaterial({
        color: 0x4d3423,
        roughness: 1,
      });
      const team = new THREE.MeshStandardMaterial({
        color: ownerColor(this.snapshot, wall.ownerId),
        roughness: 0.75,
      });
      for (const sign of branchSigns) {
        if (stone) {
          const masonry = new THREE.MeshStandardMaterial({color:0x91958e,roughness:1});
          const section = new THREE.Mesh(new THREE.BoxGeometry(.48,2.1,1.5),masonry);
          section.position.set(0,1.05,sign*.75);section.castShadow=section.receiveShadow=true;
          connectorGroup.add(section);
          for(const z of [.35,.9,1.4]) {
            const merlon = new THREE.Mesh(new THREE.BoxGeometry(.55,.35,.28),masonry);
            merlon.position.set(0,2.25,sign*z);merlon.castShadow=true;connectorGroup.add(merlon);
          }
          continue;
        }
        for (const height of [0.58, 1.15]) {
          const rail = new THREE.Mesh(
            new THREE.BoxGeometry(0.18, 0.14, 1.5),
            timber,
          );
          rail.position.set(0, height, sign * 0.75);
          rail.castShadow = true;
          connectorGroup.add(rail);
        }
        const post = new THREE.Mesh(
          new THREE.CylinderGeometry(0.13, 0.16, 1.75, 6),
          team,
        );
        post.position.set(0, 0.83, sign * 1.38);
        post.castShadow = true;
        connectorGroup.add(post);
        const point = new THREE.Mesh(
          new THREE.ConeGeometry(0.135, 0.38, 6),
          team,
        );
        point.position.set(0, 1.88, sign * 1.38);
        point.castShadow = true;
        connectorGroup.add(point);
      }
      if (branchSigns.size === 0 || stone) {
        timber.dispose();
        team.dispose();
      }
    }
  }

  private createActionSquiggle(): THREE.Sprite {
    const canvas = document.createElement("canvas");
    canvas.width = 128;
    canvas.height = 128;
    const context = canvas.getContext("2d")!;
    context.strokeStyle = "#ffffff";
    context.fillStyle = "#ffffff";
    context.lineCap = "round";
    context.lineJoin = "round";
    context.lineWidth = 10;
    context.beginPath();
    context.moveTo(19, 84);
    context.bezierCurveTo(35, 44, 48, 105, 66, 61);
    context.bezierCurveTo(80, 30, 91, 85, 109, 39);
    context.stroke();
    for (const [x, y, size] of [
      [25, 33, 5],
      [103, 91, 6],
      [74, 20, 4],
    ] as [number, number, number][]) {
      context.beginPath();
      context.arc(x, y, size, 0, Math.PI * 2);
      context.fill();
    }
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({
      map: texture,
      color: 0xf2cf69,
      transparent: true,
      opacity: 0.9,
      depthTest: false,
      depthWrite: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.position.set(0.9, 2.85, 0);
    sprite.scale.set(1.8, 1.8, 1);
    sprite.renderOrder = 16;
    return sprite;
  }

  private createIconBadge(
    icon: string,
    strokeColor = "#71d6de",
    fontSize = 58,
  ): THREE.Sprite {
    const canvas = document.createElement("canvas");
    canvas.width = 128;
    canvas.height = 128;
    const context = canvas.getContext("2d")!;
    context.fillStyle = "rgba(14, 20, 16, 0.9)";
    context.beginPath();
    context.arc(64, 64, 52, 0, Math.PI * 2);
    context.fill();
    context.strokeStyle = strokeColor;
    context.lineWidth = 7;
    context.stroke();
    context.fillStyle = "#fff1d2";
    context.font = `800 ${fontSize}px Inter, "Segoe UI Emoji", sans-serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(icon, 64, 68);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }),
    );
    sprite.scale.set(1.85, 1.85, 1);
    sprite.renderOrder = 17;
    return sprite;
  }

  private createResource(state: ResourceNodeState): THREE.Object3D {
    const group = new THREE.Group();
    group.userData.entityId = state.id;
    group.userData.clickable = true;
    if (state.resourceType === "wood" && state.id.startsWith("forest-")) {
      const random = seededVisual(state.id);
      const height = 3.5 + random() * 1.4;
      const trunk = new THREE.Mesh(
        new THREE.CylinderGeometry(0.18, 0.28, 1.7, 5),
        new THREE.MeshStandardMaterial({ color: 0x725238, roughness: 1 }),
      );
      trunk.position.y = 0.85;
      const crown = new THREE.Mesh(
        new THREE.ConeGeometry(1.45 + random() * 0.25, height, 7),
        new THREE.MeshStandardMaterial({
          color: new THREE.Color().setHSL(0.29 + random() * 0.055, 0.32, 0.22 + random() * 0.09),
          roughness: 1,
        }),
      );
      crown.position.y = 1.1 + height / 2;
      crown.rotation.y = random() * Math.PI;
      for (const mesh of [trunk, crown]) {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.userData.entityId = state.id;
        mesh.userData.clickable = true;
        group.add(mesh);
      }
      return group;
    }
    const resourceColors: Record<ResourceKind, number> = {
      food: 0xd8685b,
      wood: 0x5f9a51,
      gold: 0xf0c94f,
      stone: 0xb1b5b2,
    };
    const random = seededVisual(state.id);
    const depositScale = Math.max(0.7, Math.min(1.2, Math.cbrt(state.maxAmount / 1000)));
    const count = state.resourceType === "wood" ? 8 : Math.max(3, Math.min(10, Math.round(state.maxAmount / 220)));
    for (let index = 0; index < count; index += 1) {
      const angle = (index / count) * Math.PI * 2 + random() * 0.6;
      const radius = state.resourceType === "wood"
        ? 0.6 + random() * 1.35
        : (0.25 + random() * 0.45) * depositScale;
      let mesh: THREE.Mesh;
      if (state.resourceType === "wood") {
        const tree = new THREE.Group();
        const trunk = new THREE.Mesh(
          new THREE.CylinderGeometry(0.16, 0.22, 1.25, 6),
          new THREE.MeshStandardMaterial({ color: 0x725238 }),
        );
        trunk.position.y = 0.62;
        const crown = new THREE.Mesh(
          new THREE.ConeGeometry(0.8, 2.1, 7),
          new THREE.MeshStandardMaterial({
            color: index % 2 ? 0x355d35 : 0x426f3d,
            roughness: 1,
          }),
        );
        crown.position.y = 1.75;
        tree.add(trunk, crown);
        tree.position.set(Math.cos(angle) * radius, 0, Math.sin(angle) * radius);
        tree.traverse((child) => {
          child.userData.entityId = state.id;
          child.userData.clickable = true;
          if (child instanceof THREE.Mesh) child.castShadow = true;
        });
        group.add(tree);
        continue;
      }
      const colors: Record<Exclude<ResourceKind, "wood">, number[]> = {
        food: [0x8b2340, 0x6e8f37],
        gold: [0xd7ad2f, 0xf0ca50],
        stone: [0x747b80, 0x9a9c98],
      };
      const palette = colors[state.resourceType];
      mesh = new THREE.Mesh(
        state.resourceType === "food"
          ? new THREE.IcosahedronGeometry(0.34 + random() * 0.14, 0)
          : new THREE.DodecahedronGeometry(0.55 + random() * 0.28, 0),
        new THREE.MeshStandardMaterial({
          color: palette[index % palette.length],
          roughness: 0.9,
          metalness: state.resourceType === "gold" ? 0.25 : 0,
        }),
      );
      mesh.position.set(
        Math.cos(angle) * radius,
        (state.resourceType === "food" ? 0.35 : 0.48) * depositScale,
        Math.sin(angle) * radius,
      );
      mesh.scale.setScalar(depositScale);
      mesh.rotation.set(random(), random(), random());
      mesh.castShadow = true;
      mesh.userData.entityId = state.id;
      mesh.userData.clickable = true;
      group.add(mesh);
    }
    const label = this.createWorldLabel(
      RESOURCE_LABELS[state.resourceType].toUpperCase(),
      resourceColors[state.resourceType],
    );
    label.position.y = state.resourceType === "wood" ? 3.6 : 2.35;
    label.userData.entityId = state.id;
    label.userData.clickable = true;
    group.add(label);
    return group;
  }

  private createWorldLabel(text: string, color: number): THREE.Sprite {
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 64;
    const context = canvas.getContext("2d")!;
    context.fillStyle = "rgba(12, 17, 14, 0.88)";
    context.beginPath();
    context.roundRect(8, 8, 240, 48, 10);
    context.fill();
    context.strokeStyle = `#${color.toString(16).padStart(6, "0")}`;
    context.lineWidth = 4;
    context.stroke();
    context.fillStyle = "#fff4d7";
    context.font = "700 25px Inter, sans-serif";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(text, 128, 33);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
      }),
    );
    sprite.scale.set(4.5, 1.125, 1);
    sprite.renderOrder = 10;
    return sprite;
  }

  private updateObject(object: THREE.Object3D, state: RenderState): void {
    if (state.kind !== "unit" || !object.userData.hasPosition) {
      object.position.x = state.x;
      object.position.z = state.z;
      if (state.kind === "unit") object.rotation.y = state.facing;
    }
    object.userData.hasPosition = true;
    const selectionRing = object.getObjectByName("selection-ring");
    if (selectionRing) {
      selectionRing.visible = this.selectedIds.has(state.id);
    }
    if (state.kind === "building") {
      const stone = (state.buildingType === "wall" || state.buildingType === "gate") && state.maxHp > BUILDING_DEFS[state.buildingType].hp;
      if (stone !== !!object.userData.stoneFortification) {
        this.buildingDamage.get(state.id)?.dispose();
        this.buildingDamage.delete(state.id);
        const previous = object.getObjectByName("building-visual");
        previous?.removeFromParent();
        const geometries = new Set<THREE.BufferGeometry>(), materials = new Set<THREE.Material>();
        previous?.traverse(child => {
          if (child instanceof THREE.Mesh) {
            if (!child.userData.sharedGeometry) geometries.add(child.geometry);
            for (const material of Array.isArray(child.material) ? child.material : [child.material]) materials.add(material);
          }
        });
        geometries.forEach(geometry => geometry.dispose());materials.forEach(material => material.dispose());
        const visual = this.createStructureVisual(state.buildingType, new THREE.Color(ownerColor(this.snapshot,state.ownerId)),stone);
        visual.name="building-visual";object.add(visual);object.userData.stoneFortification=stone;
      }
      const range = object.getObjectByName("defensive-range");
      if (range) range.visible = state.completed && (state.ownerId === this.snapshot.you.id || this.selectedIds.has(state.id));
      if (state.buildingType === "wall" || state.buildingType === "gate") {
        object.rotation.y = state.facing;
      }
      if (state.buildingType === "gate") {
        const left = object.getObjectByName("gate-left");
        const right = object.getObjectByName("gate-right");
        if (left) left.rotation.y = state.gateOpen ? -Math.PI / 2 : 0;
        if (right) right.rotation.y = state.gateOpen ? Math.PI / 2 : 0;
      }
      // Construction grows upward; its occupied tiles never shrink.
      object.scale.setScalar(1);
      object.getObjectByName("building-visual")?.scale.set(1, state.completed ? 1 : 0.25 + state.progress * 0.75, 1);
      object.traverse((child) => {
        if (child instanceof THREE.Sprite) {
          child.material.opacity = state.completed ? 1 : 0.58;
        }
      });
      const constructionBadge = object.getObjectByName("construction-badge");
      if (constructionBadge) constructionBadge.visible = !state.completed;
      const repairBadge = object.getObjectByName("repair-badge");
      if (repairBadge) {
        repairBadge.visible =
          state.completed &&
          state.hp < state.maxHp &&
          state.ownerId === this.snapshot.you.id;
      }
      const depletedFarm =
        state.buildingType === "farm" &&
        state.completed &&
        (state.foodRemaining ?? 0) <= 0;
      object.traverse((child) => {
        if (child.name === "farm-crop") child.visible = !depletedFarm;
      });
      const depletedBadge = object.getObjectByName("farm-depleted-badge");
      if (depletedBadge) depletedBadge.visible = depletedFarm;
      const damageStage = buildingDamageStage(state.hp, state.maxHp, state.completed);
      if (damageStage > 0) {
        let damage = this.buildingDamage.get(state.id);
        if (!damage) {
          const visual = object.getObjectByName("building-visual");
          if (visual) {
            damage = new BuildingDamage(visual);
            this.buildingDamage.set(state.id, damage);
          }
        }
        damage?.setHealth(state.hp, state.maxHp, state.completed);
      } else {
        this.buildingDamage.get(state.id)?.dispose();
        this.buildingDamage.delete(state.id);
      }
    }
    if (state.kind === "resource") {
      object.scale.setScalar(Math.max(0.35, state.amount / state.maxAmount));
    }
  }

  private bindInput(): void {
    const canvas = this.renderer.domElement;
    element("global-production").onclick = event => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>("button[data-production-building]");
      if (!button) return;
      this.selectIds([button.dataset.productionBuilding!]);
      this.centerOnSelection();
      soundDesign.select();
    };
    commandPanel.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return;
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
        "button[data-action]",
      );
      if (!button || button.disabled) return;
      event.preventDefault();
      this.handleCommandAction(button.dataset.action!, event.shiftKey);
    });
    selectionInfo.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return;
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
        "button[data-action]",
      );
      if (!button || button.disabled) return;
      event.preventDefault();
      this.handleCommandAction(button.dataset.action!, event.shiftKey);
    });
    controlHotbar.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return;
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
        "button[data-group]",
      );
      if (!button) return;
      event.preventDefault();
      const number = Number(button.dataset.group);
      if (event.ctrlKey) this.assignControlGroup(number);
      else this.recallControlGroup(number);
    });
    canvas.addEventListener("contextmenu", (event) => event.preventDefault());
    canvas.addEventListener("dblclick", (event) => {
      if (
        event.button !== 0 ||
        this.placementType ||
        this.rallyPlacementBuildingId
      ) {
        return;
      }
      event.preventDefault();
      this.selectVisibleMatchingUnits(event, event.shiftKey);
    });
    canvas.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return;
      this.dragStart = { x: event.clientX, y: event.clientY };
      this.dragAdditive = event.shiftKey;
      this.dragMoved = false;
      selectionBox.classList.add("hidden");
      if (this.placementType === "wall" && event.shiftKey) {
        const point = this.groundPoint(event);
        if (point) {
          this.wallDragStart = this.snapWallPoint(point);
          this.wallDragEnd = { x: point.x, z: point.z };
          this.updateWallLinePreview(point);
          canvas.setPointerCapture(event.pointerId);
        }
      }
    });
    canvas.addEventListener("pointermove", (event) => {
      this.updatePointerFeedback(event);
      if (this.wallDragStart) {
        const point = this.groundPoint(event);
        if (point) this.updateWallLinePreview(point);
      } else if (this.placementType) {
        this.updatePlacementPreview(event);
      }
      if (this.rallyPlacementBuildingId) this.updateRallyPreview(event);
      if (!this.dragStart) return;
      const dx = event.clientX - this.dragStart.x;
      const dy = event.clientY - this.dragStart.y;
      if (Math.hypot(dx, dy) > 5) this.dragMoved = true;
      if (!this.dragMoved || this.placementType) return;
      selectionBox.classList.remove("hidden");
      selectionBox.style.left = `${Math.min(this.dragStart.x, event.clientX)}px`;
      selectionBox.style.top = `${Math.min(this.dragStart.y, event.clientY)}px`;
      selectionBox.style.width = `${Math.abs(dx)}px`;
      selectionBox.style.height = `${Math.abs(dy)}px`;
    });
    canvas.addEventListener("pointerleave", () => {
      worldTooltip.classList.add("hidden");
      combatCursor.classList.add("hidden");
      workCursor.classList.add("hidden");
      garrisonCursor.classList.add("hidden");
      this.setHoveredTarget(undefined);
      canvas.classList.remove(
        "gather-cursor",
        "attack-cursor",
        "work-cursor",
        "garrison-cursor",
      );
    });
    canvas.addEventListener("pointerup", (event) => {
      if (event.button !== 0 || !this.dragStart) return;
      if (this.wallDragStart) {
        const point = this.groundPoint(event);
        if (point) {
          this.updateWallLinePreview(point);
          void this.placeWallLine();
        }
        this.wallDragStart = undefined;
        this.wallDragEnd = undefined;
        if (canvas.hasPointerCapture(event.pointerId)) {
          canvas.releasePointerCapture(event.pointerId);
        }
      } else if (this.placementType) {
        const point = this.groundPoint(event);
        if (point) void this.placeBuilding(point, event.shiftKey);
      } else if (this.rallyPlacementBuildingId) {
        const point = this.groundPoint(event);
        if (point) void this.placeRallyPoint(point, this.contextTarget(event));
      } else if (this.garrisonMode) {
        const target = this.contextTarget(event);
        if (target?.kind === "building" && target.ownerId === this.snapshot.you.id &&
            target.completed && target.garrisonCapacity > target.garrisonedCount) {
          const unitIds = [...this.selectedIds].filter(id => this.entityStates.get(id)?.kind === "unit");
          void this.sendCommand({ type: "garrison", unitIds, targetId: target.id, queue: event.shiftKey }, () => {
            this.showOrderMarker(target, 0x86c7ff);
            this.showOrderAcknowledgement(event.shiftKey ? "Garrison queued" : "Garrison");
            soundDesign.garrison();
          });
          this.cancelPlacement(false);
        } else showEvent("Choose a friendly building with garrison space.", "warning");
      } else if (this.siegeTargetMode) {
        const point = this.groundPoint(event);
        const target = this.contextTarget(event);
        const unitIds = [...this.selectedIds].filter(id => { const unit = this.entityStates.get(id); return unit?.kind === "unit" && unit.unitType === "onager"; });
        if (this.siegeTargetMode === "trees" && (target?.kind !== "resource" || target.resourceType !== "wood")) {
          showEvent("Choose a tree to bombard.", "warning");
        } else if (point) {
          const command: CommandPayload = this.siegeTargetMode === "trees" && target
            ? { type: "attack", unitIds, targetId: target.id, queue: event.shiftKey }
            : { type: "attack_ground", unitIds, target: { x: point.x, z: point.z }, queue: event.shiftKey };
          void this.sendCommand(command, () => { soundDesign.attackOrder(); this.showOrderMarker(point, 0xef665c); });
          this.cancelPlacement(false);
        }
      } else if (this.attackMoveMode) {
        const point = this.groundPoint(event);
        if (point) {
          const unitIds = [...this.selectedIds].filter(id => this.entityStates.get(id)?.kind === "unit");
          void this.sendCommand({ type: "attack_move", unitIds, target: { x: point.x, z: point.z }, queue: event.shiftKey }, () => {
            soundDesign.attackOrder();
            this.showOrderMarker(point, 0xef665c);
          });
        }
        this.attackMoveMode = false;
        placementHint.classList.add("hidden");
      } else if (this.dragMoved) {
        this.selectRectangle(this.dragStart, {
          x: event.clientX,
          y: event.clientY,
        }, event.shiftKey || this.dragAdditive);
      } else {
        this.selectAt(event, event.shiftKey || this.dragAdditive);
      }
      this.dragStart = undefined;
      selectionBox.classList.add("hidden");
    });
    canvas.addEventListener("pointerdown", (event) => {
      if (event.button !== 2) return;
      if (this.placementType || this.rallyPlacementBuildingId || this.attackMoveMode || this.garrisonMode || this.siegeTargetMode) {
        event.preventDefault();
        this.cancelPlacement();
        this.dragStart = undefined;
        selectionBox.classList.add("hidden");
        return;
      }
      this.issueContextCommand(event);
    });
    canvas.addEventListener(
      "wheel",
      (event) => {
        event.preventDefault();
        this.cameraDistance = THREE.MathUtils.clamp(
          this.cameraDistance + event.deltaY * 0.025,
          20,
          72,
        );
      },
      { passive: false },
    );
    window.addEventListener("keydown", (event) => {
      if (deleteConfirmation.open) return;
      if (
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement
      ) {
        return;
      }
      this.keys.add(event.key.toLowerCase());
      if (event.key === "Shift" && !event.repeat) this.refreshSelection();
      if (event.key === "Escape") this.cancelPlacement();
      if (event.key === " ") {
        event.preventDefault();
        this.centerOnSelection();
      }
      if (event.key === "Delete" && !event.repeat) {
        event.preventDefault();
        this.handleCommandAction("delete-selected");
        return;
      }
      const number = Number(event.key);
      if (number >= 1 && number <= 9) {
        event.preventDefault();
        if (event.ctrlKey) {
          this.assignControlGroup(number);
        } else {
          this.recallControlGroup(number);
        }
      }
      if (
        !event.repeat &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.metaKey
      ) {
        const hotkey = event.key.toUpperCase();
        const button = commandPanel.querySelector<HTMLButtonElement>(
          `button[data-hotkey="${hotkey}"]:not(:disabled)`,
        );
        if (button) {
          event.preventDefault();
          this.handleCommandAction(button.dataset.action!, event.shiftKey);
        }
      }
    });
    window.addEventListener("keyup", (event) => {
      this.keys.delete(event.key.toLowerCase());
      if (event.key === "Shift") this.refreshSelection();
    });
    window.addEventListener("pointermove", (event) => {
      this.updateEdgePan(event);
    });
    window.addEventListener("blur", () => {
      this.edgePanInput.set(0, 0);
      this.keys.clear();
      this.refreshSelection();
    });
    minimap.addEventListener("pointerdown", (event) => {
      soundDesign.click();
      const rect = minimap.getBoundingClientRect();
      const screenX = ((event.clientX - rect.left) / rect.width) * minimap.width;
      const screenY = ((event.clientY - rect.top) / rect.height) * minimap.height;
      const difference =
        ((screenX - minimap.width / 2) * 2 * this.snapshot.mapSize) /
        minimap.width;
      const sum =
        (screenY * 2 * this.snapshot.mapSize) / minimap.height;
      this.cameraTarget.x = THREE.MathUtils.clamp(
        (sum + difference) / 2,
        0,
        this.snapshot.mapSize,
      );
      this.cameraTarget.z = THREE.MathUtils.clamp(
        (sum - difference) / 2,
        0,
        this.snapshot.mapSize,
      );
    });
  }

  private updateEdgePan(event: PointerEvent): void {
    if (deleteConfirmation.open || gameScreen.classList.contains("hidden")) {
      this.edgePanInput.set(0, 0);
      return;
    }
    const canvasBounds = this.renderer.domElement.getBoundingClientRect();
    const hud = document.querySelector<HTMLElement>(".bottom-hud");
    const hudTop = hud?.getBoundingClientRect().top ?? canvasBounds.bottom;
    const hotbarTop =
      controlHotbar.childElementCount > 0
        ? controlHotbar.getBoundingClientRect().top
        : hudTop;
    const bottomPanLine = Math.min(hudTop, hotbarTop);
    const edgeZone = Math.min(
      34,
      Math.max(18, canvasBounds.width * 0.025),
    );
    const withinHorizontalBounds =
      event.clientX >= canvasBounds.left &&
      event.clientX <= canvasBounds.right;
    const withinBattlefieldHeight =
      event.clientY >= canvasBounds.top &&
      event.clientY <= hudTop;
    this.edgePanInput.x =
      withinBattlefieldHeight && event.clientX <= canvasBounds.left + edgeZone
        ? -1
        : withinBattlefieldHeight &&
            event.clientX >= canvasBounds.right - edgeZone
          ? 1
          : 0;
    this.edgePanInput.y =
      withinHorizontalBounds &&
      event.clientY <= canvasBounds.top + edgeZone
        ? 1
        : withinHorizontalBounds &&
            event.clientY >= bottomPanLine - edgeZone &&
            event.clientY <= bottomPanLine + 6
          ? -1
          : 0;
  }

  private selectAt(event: PointerEvent, additive: boolean): void {
    const entityId = this.selectionTargetId(event);
    if (!additive) this.selectedIds.clear();
    if (entityId) {
      const state = this.entityStates.get(entityId);
      const isEnemyBuilding =
        state?.kind === "building" &&
        state.ownerId !== this.snapshot.you.id;
      if (isEnemyBuilding) {
        this.selectedIds.clear();
        this.selectedIds.add(entityId);
      } else if (
        state &&
        state.kind !== "resource" &&
        state.ownerId === this.snapshot.you.id
      ) {
        if (additive && this.selectedIds.has(entityId)) {
          this.selectedIds.delete(entityId);
        } else {
          this.selectedIds.add(entityId);
        }
      }
    }
    if (this.selectedIds.size > 0) this.playSelectionSound();
    this.refreshSelection();
  }

  private selectVisibleMatchingUnits(
    event: { clientX: number; clientY: number },
    additive: boolean,
  ): void {
    const entityId = this.selectionTargetId(event);
    if (!entityId) return;
    const clicked = this.entityStates.get(entityId);
    if (
      clicked?.kind !== "unit" ||
      clicked.ownerId !== this.snapshot.you.id
    ) {
      return;
    }

    if (!additive) this.selectedIds.clear();
    const idleVillagersOnly =
      clicked.unitType === "villager" && clicked.state === "idle";
    for (const [id, state] of this.entityStates) {
      if (
        state.kind !== "unit" ||
        state.ownerId !== this.snapshot.you.id ||
        state.unitType !== clicked.unitType ||
        (idleVillagersOnly && state.state !== "idle") ||
        !this.isInCameraView(state)
      ) {
        continue;
      }
      this.selectedIds.add(id);
    }
    if (this.selectedIds.size > 0) this.playSelectionSound();
    this.refreshSelection();
  }

  private selectionTargetId(event: {
    clientX: number;
    clientY: number;
  }): string | undefined {
    const ids = this.raycast(event)
      .map((hit) => this.findEntityId(hit.object))
      .filter((id): id is string => Boolean(id));
    const directFriendlyUnit = ids.find((id) => {
      const state = this.entityStates.get(id);
      return (
        state?.kind === "unit" &&
        state.ownerId === this.snapshot.you.id
      );
    });
    if (directFriendlyUnit) return directFriendlyUnit;

    // Large composite buildings can cover a unit visually even though the
    // player is clicking its badge/body. Give a tight screen-space unit target
    // priority over the building behind it.
    const nearbyFriendlyUnit = this.nearestScreenTarget(
      event,
      (state) =>
        state.kind === "unit" &&
        state.ownerId === this.snapshot.you.id,
      34,
    );
    return nearbyFriendlyUnit?.id ?? ids[0];
  }

  private isInCameraView(state: UnitState): boolean {
    const projected = new THREE.Vector3(state.x, 0.8, state.z).project(
      this.camera,
    );
    return (
      projected.z >= -1 &&
      projected.z <= 1 &&
      projected.x >= -1 &&
      projected.x <= 1 &&
      projected.y >= -1 &&
      projected.y <= 1
    );
  }

  private selectRectangle(
    start: { x: number; y: number },
    end: { x: number; y: number },
    additive = false,
  ): void {
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);
    if (!additive) this.selectedIds.clear();
    for (const [id, state] of this.entityStates) {
      if (
        state.kind !== "unit" ||
        state.ownerId !== this.snapshot.you.id
      ) {
        continue;
      }
      const projected = new THREE.Vector3(state.x, 0.8, state.z).project(
        this.camera,
      );
      const x = ((projected.x + 1) / 2) * innerWidth;
      const y = ((-projected.y + 1) / 2) * innerHeight;
      if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
        this.selectedIds.add(id);
      }
    }
    if (this.selectedIds.size > 0) this.playSelectionSound();
    this.refreshSelection();
  }

  private issueContextCommand(event: PointerEvent): void {
    const units = [...this.selectedIds].filter(
      (id) => this.entityStates.get(id)?.kind === "unit",
    );
    if (units.length === 0) {
      const producers = [...this.selectedIds].map(id => this.entityStates.get(id)).filter(
        (state): state is BuildingState => state?.kind === "building" &&
          state.ownerId === this.snapshot.you.id && state.completed && BUILDING_DEFS[state.buildingType].trains.length > 0,
      );
      if (producers.length) {
        const point = this.groundPoint(event);
        if (point) for (const building of producers) void this.setRallyPoint(building.id, point, this.contextTarget(event));
        return;
      }
    }
    const firingBuilding = [...this.selectedIds]
      .map((id) => this.entityStates.get(id))
      .find(
        (state): state is BuildingState =>
          state?.kind === "building" &&
          state.ownerId === this.snapshot.you.id &&
          state.completed &&
          BUILDING_DEFS[state.buildingType].defensiveRange > 0 &&
          (state.garrisonedCount > 0 ||
            state.buildingType === "tower" ||
            state.buildingType === "castle"),
      );
    if (units.length === 0 && !firingBuilding) {
      showEvent("Select units first, then right-click to issue an order.", "warning");
      return;
    }
    const target = this.contextTarget(event);
    let command: CommandPayload | undefined;
    let markerPosition: Vec2 | undefined;
    let markerColor = 0x9bd38b;
    let acknowledgement = "";
    let resourceKind: ResourceKind | undefined;
    if (units.length > 0 && target?.kind === "resource" && units.some(id => {
      const unit = this.entityStates.get(id); return unit?.kind === "unit" && unit.unitType === "villager";
    })) {
      command = { type: "gather", unitIds: units, targetId: target.id };
      markerPosition = { x: target.x, z: target.z };
      markerColor = 0xf3d78d;
      resourceKind = target.resourceType;
      acknowledgement = `Gather ${RESOURCE_LABELS[target.resourceType].toLowerCase()}`;
    } else if (
      units.length > 0 &&
      target?.kind === "building" &&
      target.ownerId === this.snapshot.you.id &&
      target.buildingType === "farm" &&
      target.completed
    ) {
      const depleted = (target.foodRemaining ?? 0) <= 0;
      command = depleted
        ? {
            type: "reseed_farm",
            buildingId: target.id,
            unitIds: units,
          }
        : { type: "work_farm", unitIds: units, targetId: target.id };
      markerPosition = { x: target.x, z: target.z };
      markerColor = 0xb8d865;
      resourceKind = "food";
      acknowledgement = depleted ? "Farm reseeded and assigned" : "Work farm";
    } else if (
      units.length > 0 &&
      target?.kind === "building" &&
      target.ownerId === this.snapshot.you.id &&
      !target.completed
    ) {
      command = {
        type: "assist_build",
        unitIds: units,
        targetId: target.id,
      };
      markerPosition = { x: target.x, z: target.z };
      markerColor = 0x71d6de;
      acknowledgement = `Help build ${buildingLabel(target)}`;
    } else if (target?.kind === "building" && this.canDepositAt(target, units)) {
      command = { type: "deposit", unitIds: units, targetId: target.id };
      markerPosition = target;
      markerColor = 0xf3d78d;
      acknowledgement = "Deposit resources, then resume gathering";
    } else if (
      units.length > 0 &&
      target?.kind === "building" &&
      target.ownerId === this.snapshot.you.id &&
      target.completed &&
      target.hp < target.maxHp &&
      units.some((id) => {
        const state = this.entityStates.get(id);
        return state?.kind === "unit" && state.unitType === "villager";
      })
    ) {
      command = {
        type: "repair",
        unitIds: units,
        targetId: target.id,
      };
      markerPosition = { x: target.x, z: target.z };
      markerColor = 0xe7b95b;
      acknowledgement = `Repair ${buildingLabel(target)}`;
    } else if (
      target &&
      target.kind !== "resource" &&
      target.ownerId !== this.snapshot.you.id
    ) {
      command =
        units.length > 0
          ? { type: "attack", unitIds: units, targetId: target.id }
          : {
              type: "building_attack",
              buildingId: firingBuilding!.id,
              targetId: target.id,
            };
      markerPosition = { x: target.x, z: target.z };
      markerColor = 0xef665c;
      acknowledgement = firingBuilding ? "Focus fire" : "Attack target";
    } else {
      if (units.length === 0) {
        showEvent(
          "Defensive buildings can focus an enemy within range; select units to move.",
          "info",
        );
        return;
      }
      const point = this.groundPoint(event);
      if (point) {
        command = {
          type: "move",
          unitIds: units,
          target: { x: point.x, z: point.z },
        };
        markerPosition = { x: point.x, z: point.z };
        acknowledgement = "Move";
      }
    }
    if (!command) return;
    command.queue = event.shiftKey;
    void this.sendCommand(command, () => {
      if (!markerPosition) return;
      this.showOrderMarker(markerPosition, markerColor);
      if (command.type === "reseed_farm" || command.type === "work_farm") soundDesign.farm();
      else if (resourceKind) soundDesign.gather(resourceKind);
      else if (command.type === "assist_build" || command.type === "repair") {
        if (command.type === "repair") soundDesign.repair();
        else soundDesign.build();
      }
      else if (
        command.type === "attack" ||
        command.type === "building_attack"
      ) soundDesign.attackOrder();
      else {
        const mover = this.entityStates.get(units[0]);
        soundDesign.move(mover?.kind === "unit" ? mover.unitType : undefined);
      }
      this.showOrderAcknowledgement(
        event.shiftKey ? `${acknowledgement} queued` : acknowledgement,
      );
    });
  }

  private async placeBuilding(
    point: THREE.Vector3,
    queue = false,
  ): Promise<void> {
    if (!this.placementType) return;
    this.updatePlacementAtPoint(point);
    if (!this.placementValid) {
      showEvent(this.placementReason || "Choose another building site.", "warning");
      return;
    }
    const villagers = [...this.selectedIds].filter((id) => {
      const state = this.entityStates.get(id);
      return state?.kind === "unit" && state.unitType === "villager";
    });
    if (villagers.length === 0) {
      showEvent("Select at least one villager.", "warning");
      this.cancelPlacement();
      return;
    }
    const buildingType = this.placementType;
    const placement = this.placementGhost?.position ?? point;
    const result = await this.sendCommand({
      type: "build",
      unitIds: villagers,
      buildingType,
      target: { x: placement.x, z: placement.z },
      queue,
    });
    if (result.ok) {
      soundDesign.build();
      this.showOrderMarker({ x: placement.x, z: placement.z }, 0xf3d78d);
      this.showOrderAcknowledgement(
        `${BUILDING_DEFS[buildingType].label} foundation ${queue ? "queued" : "placed"}`,
      );
      if (buildingType === "wall" || queue) {
        placementHint.textContent =
          buildingType === "wall"
            ? "Click for one segment · Shift-drag for a wall line · Esc finishes"
            : `Shift-click to queue another ${BUILDING_DEFS[buildingType].label} · right-click finishes`;
      } else {
        this.cancelPlacement(false);
      }
    }
  }

  private snapGridPoint(point: { x: number; z: number }): Vec2 {
    return {
      x:
        Math.round(point.x / NAVIGATION_CELL_SIZE) *
        NAVIGATION_CELL_SIZE,
      z:
        Math.round(point.z / NAVIGATION_CELL_SIZE) *
        NAVIGATION_CELL_SIZE,
    };
  }

  private snapWallPoint(point: { x: number; z: number }): Vec2 {
    return this.snapGridPoint({ x: THREE.MathUtils.clamp(point.x, 0, this.snapshot.mapSize),
      z: THREE.MathUtils.clamp(point.z, 0, this.snapshot.mapSize) });
  }

  private getWallLine(start: Vec2, end: Vec2): {
    points: Vec2[];
    facing: number;
  } {
    const snappedStart = this.snapGridPoint(start);
    const snappedEnd = this.snapGridPoint(end);
    const startX = Math.round(snappedStart.x / NAVIGATION_CELL_SIZE);
    const startZ = Math.round(snappedStart.z / NAVIGATION_CELL_SIZE);
    const endX = Math.round(snappedEnd.x / NAVIGATION_CELL_SIZE);
    const endZ = Math.round(snappedEnd.z / NAVIGATION_CELL_SIZE);
    const deltaX = endX - startX;
    const deltaZ = endZ - startZ;
    const horizontal = Math.abs(deltaX) >= Math.abs(deltaZ);
    const alignedEndX = horizontal ? endX : startX;
    const alignedEndZ = horizontal ? startZ : endZ;
    const steps = Math.min(
      Math.floor(this.snapshot.mapSize / NAVIGATION_CELL_SIZE),
      horizontal
        ? Math.abs(alignedEndX - startX)
        : Math.abs(alignedEndZ - startZ),
    );
    const points: Vec2[] = [];
    for (let index = 0; index <= steps; index++) {
      const progress = steps === 0 ? 0 : index / steps;
      const cellX = Math.round(
        startX + (alignedEndX - startX) * progress,
      );
      const cellZ = Math.round(
        startZ + (alignedEndZ - startZ) * progress,
      );
      const candidate = {
        x: cellX * NAVIGATION_CELL_SIZE,
        z: cellZ * NAVIGATION_CELL_SIZE,
      };
      const previous = points.at(-1);
      if (!previous || previous.x !== candidate.x || previous.z !== candidate.z) {
        points.push(candidate);
      }
    }
    const dx = points.at(-1)!.x - points[0].x;
    const dz = points.at(-1)!.z - points[0].z;
    return {
      points,
      facing: Math.abs(dx) + Math.abs(dz) < 0.001 ? 0 : -Math.atan2(dz, dx),
    };
  }

  private clearWallLinePreview(): void {
    this.wallPreviewKey = "";
    for (const ghost of this.wallLineGhosts) {
      this.scene.remove(ghost);
      this.disposeGroup(ghost);
    }
    this.wallLineGhosts = [];
    this.wallLinePoints = [];
    this.wallLineValid = false;
    this.wallLineReason = "";
    if (this.placementGhost) this.placementGhost.visible = true;
  }

  private updateWallLinePreview(point: THREE.Vector3): void {
    if (!this.wallDragStart || this.placementType !== "wall") return;
    const end = { x: point.x, z: point.z };
    const snapped = this.snapWallPoint(point);
    const key = `${this.snapshot.tick}:${this.wallDragStart.x},${this.wallDragStart.z}:${snapped.x},${snapped.z}`;
    if (this.wallPreviewKey === key) return;
    this.wallDragEnd = end;
    this.clearWallLinePreview();
    this.wallPreviewKey = key;
    if (this.placementGhost) this.placementGhost.visible = false;

    const definition = BUILDING_DEFS.wall;
    const line = this.getWallLine(this.wallDragStart, end);
    this.wallDragEnd = line.points.at(-1);
    line.points = line.points.filter(point => ![...this.entityStates.values()].some(state =>
      state.kind === "building" && state.ownerId === this.snapshot.you.id &&
      (state.buildingType === "wall" || state.buildingType === "gate") &&
      palisadeContains(state, point)));
    const blocked = (candidate: Vec2) =>
      candidate.x < 0 || candidate.z < 0 || candidate.x > this.snapshot.mapSize || candidate.z > this.snapshot.mapSize ||
      [...this.entityStates.values()].some(state => state.kind === "building"
        ? buildingOverlapAt(state, "wall", candidate, line.facing)
        : state.kind === "resource" && buildingBlocksResource("wall", candidate, line.facing, state)) ||
      this.terrainBlocksPlacement(candidate.x, candidate.z, .75);
    const clearPoints = line.points.filter(candidate => !blocked(candidate));
    this.wallLinePoints = clearPoints;
    const skipped = line.points.length - clearPoints.length;
    const totalCost = { wood: (definition.cost.wood ?? 0) * clearPoints.length };
    this.wallLineReason = !clearPoints.length ? "No clear wall cells in this line."
      : !hasResources(this.snapshot.you.resources, totalCost)
        ? `Need ${formatCost(totalCost)} for ${clearPoints.length} clear segments.` : "";
    this.wallLineValid = this.wallLineReason === "";

    for (const candidate of line.points) {
      const color = blocked(candidate) || !this.wallLineValid ? 0xef665c : 0x75d782;
      const ghost = this.createPlacementGhost("wall");
      ghost.visible = true;
      ghost.position.set(candidate.x, 0, candidate.z);
      ghost.rotation.y = line.facing;
      const label = ghost.getObjectByName("placement-label");
      if (label) label.visible = false;
      ghost.traverse((object) => {
        if (
          object instanceof THREE.Mesh &&
          !Array.isArray(object.material) &&
          "color" in object.material
        ) {
          (object.material as THREE.MeshBasicMaterial).color.setHex(color);
        } else if (object instanceof THREE.Sprite) {
          object.material.color.setHex(this.wallLineValid ? 0x9cf3ae : 0xff7d73);
        }
      });
      this.wallLineGhosts.push(ghost);
      this.scene.add(ghost);
    }
    placementHint.textContent = this.wallLineValid
      ? `${clearPoints.length} wall segments · ${skipped} blocked cells skipped · ${formatCost(totalCost)} · release to place`
      : `${this.wallLineReason} · release to cancel`;
    placementHint.classList.toggle("invalid", !this.wallLineValid);
  }

  private async placeWallLine(): Promise<void> {
    const start = this.wallDragStart;
    const end = this.wallDragEnd;
    const points = [...this.wallLinePoints];
    const valid = this.wallLineValid;
    const reason = this.wallLineReason;
    const villagers = [...this.selectedIds].filter((id) => {
      const state = this.entityStates.get(id);
      return state?.kind === "unit" && state.unitType === "villager";
    });
    this.clearWallLinePreview();
    if (this.placementGhost) this.placementGhost.visible = false;
    if (!start || !end || !valid) {
      if (reason) showEvent(reason, "warning");
      return;
    }
    const result = await this.sendCommand({
      type: "build_wall_line",
      unitIds: villagers,
      target: start,
      endTarget: end,
    });
    if (!result.ok) return;
    soundDesign.build();
    const marker = points[Math.floor(points.length / 2)] ?? start;
    this.showOrderMarker(marker, 0xf3d78d);
    this.showOrderAcknowledgement(
      `${result.code ?? points.length} wall segments queued`,
    );
    placementHint.textContent =
      "Click for one segment · Shift-drag for a wall line · Esc finishes";
    placementHint.classList.remove("invalid");
  }

  private beginPlacement(type: BuildingType): void {
    if (this.placementGhost || this.rallyPreview || this.wallLineGhosts.length > 0) {
      this.cancelPlacement();
    }
    this.placementType = type;
    this.placementGhost = this.createPlacementGhost(type);
    this.scene.add(this.placementGhost);
    soundDesign.select(type);
    placementHint.textContent =
      type === "wall"
        ? "Click for one segment · hold Shift and drag for a wall line · Esc finishes"
        : `Move over the map to place ${BUILDING_DEFS[type].label}`;
    placementHint.classList.remove("hidden");
    this.renderer.domElement.classList.add("placing");
  }

  private beginRallyPlacement(buildingId: string): void {
    const building = this.entityStates.get(buildingId);
    if (
      !building ||
      building.kind !== "building" ||
      building.ownerId !== this.snapshot.you.id ||
      !building.completed ||
      BUILDING_DEFS[building.buildingType].trains.length === 0
    ) {
      showEvent("Select a completed unit-producing building.", "warning");
      return;
    }
    if (this.placementGhost || this.rallyPreview) this.cancelPlacement();
    this.rallyPlacementBuildingId = buildingId;
    this.rallyPreview = this.createRallyFlag(
      new THREE.Color(this.snapshot.you.color).getHex(),
      true,
    );
    this.rallyPreview.visible = false;
    this.scene.add(this.rallyPreview);
    placementHint.textContent = `Click the map to place ${BUILDING_DEFS[building.buildingType].label} rally flag · Esc cancels`;
    placementHint.classList.remove("hidden", "invalid");
    this.renderer.domElement.classList.add("placing");
    soundDesign.select();
  }

  private rallyActivity(target?: RenderState): string | undefined {
    if (target?.kind === "resource" && target.amount > 0) return `Gather ${RESOURCE_LABELS[target.resourceType].toLowerCase()}`;
    if (target?.kind !== "building" || target.ownerId !== this.snapshot.you.id) return;
    if (!target.completed) return "Help build";
    if (target.buildingType === "farm" && (target.foodRemaining ?? 0) > 0) return "Collect food";
    if (target.hp < target.maxHp) return "Repair";
  }

  private canDepositAt(target: BuildingState, ids: string[]): boolean {
    return target.ownerId === this.snapshot.you.id && target.completed &&
      (target.buildingType === "town_center" || target.buildingType === "camp") &&
      ids.some(id => {
        const unit = this.entityStates.get(id);
        return unit?.kind === "unit" && unit.unitType === "villager" && unit.carriedAmount > 0;
      });
  }

  private async placeRallyPoint(point: THREE.Vector3, target?: RenderState): Promise<void> {
    const buildingId = this.rallyPlacementBuildingId;
    if (!buildingId) return;
    if (await this.setRallyPoint(buildingId, point, target)) this.cancelPlacement(false);
  }

  private async setRallyPoint(buildingId: string, point: Vec2, target?: RenderState): Promise<boolean> {
    const activity = this.rallyActivity(target);
    const destination = activity && target ? { x: target.x, z: target.z } : { x: point.x, z: point.z };
    const result = await this.sendCommand({
      type: "set_rally_point",
      buildingId,
      target: destination,
      targetId: activity ? target?.id : undefined,
    }, () => {
      this.showOrderMarker(destination, 0xf3d78d);
      this.showOrderAcknowledgement(activity ? `Rally: ${activity} · New villagers start working` : "Rally: new units move here");
      soundDesign.rally();
    });
    if (!result.ok) return false;
    const building = this.entityStates.get(buildingId);
    if (building?.kind === "building") {
      building.rallyPoint = destination;
      building.rallyTargetId = activity ? target?.id : undefined;
      building.rallyActivity = activity;
    }
    this.updateRallyIndicator();
    return true;
  }

  private cancelPlacement(playSound = true): void {
    this.siegeTargetMode = undefined;
    this.attackMoveMode = false;
    this.garrisonMode = false;
    const wasPlacing = Boolean(
      this.placementType ||
        this.placementGhost ||
        this.wallLineGhosts.length > 0 ||
        this.rallyPlacementBuildingId ||
        this.rallyPreview,
    );
    this.clearWallLinePreview();
    if (this.placementGhost) {
      this.scene.remove(this.placementGhost);
      this.disposeGroup(this.placementGhost);
    }
    if (this.rallyPreview) {
      this.scene.remove(this.rallyPreview);
      this.disposeGroup(this.rallyPreview);
    }
    this.placementType = undefined;
    this.placementGhost = undefined;
    this.wallDragStart = undefined;
    this.wallDragEnd = undefined;
    this.rallyPlacementBuildingId = undefined;
    this.rallyPreview = undefined;
    this.placementValid = false;
    this.placementReason = "";
    placementHint.classList.add("hidden");
    this.renderer.domElement.classList.remove("placing");
    if (wasPlacing && playSound) soundDesign.cancel();
  }

  private updatePointerFeedback(event: {
    clientX: number;
    clientY: number;
  }): void {
    this.lastPointerPosition = {
      clientX: event.clientX,
      clientY: event.clientY,
    };
    if (this.placementType || this.rallyPlacementBuildingId) {
      worldTooltip.classList.add("hidden");
      return;
    }
    const target = this.contextTarget(event);
    const hasVillager = [...this.selectedIds].some((id) => {
      const state = this.entityStates.get(id);
      return state?.kind === "unit" && state.unitType === "villager";
    });
    const hasUnit = [...this.selectedIds].some(
      (id) => this.entityStates.get(id)?.kind === "unit",
    );
    const hasGarrisonableUnit = [...this.selectedIds].some((id) => {
      const state = this.entityStates.get(id);
      return (
        state?.kind === "unit" &&
        ["villager", "spearman", "archer", "royal_guard"].includes(
          state.unitType,
        )
      );
    });
    const hasFiringBuilding = [...this.selectedIds].some((id) => {
      const state = this.entityStates.get(id);
      return (
        state?.kind === "building" &&
        state.ownerId === this.snapshot.you.id &&
        state.completed &&
        (state.buildingType === "tower" || state.buildingType === "castle")
      );
    });
    const canAttack =
      Boolean(
        target &&
          target.kind !== "resource" &&
          target.ownerId !== this.snapshot.you.id &&
          (hasUnit || hasFiringBuilding),
      );
    const canAssist =
      Boolean(
        target?.kind === "building" &&
          target.ownerId === this.snapshot.you.id &&
          !target.completed &&
          hasVillager,
      );
    const canDeposit = target?.kind === "building" && this.canDepositAt(target, [...this.selectedIds]);
    const canRepair = !this.garrisonMode && !canDeposit &&
      Boolean(
        target?.kind === "building" &&
          target.ownerId === this.snapshot.you.id &&
          target.completed &&
          target.hp < target.maxHp &&
          hasVillager,
      );
    const canFarm =
      target?.kind === "building" &&
      target.ownerId === this.snapshot.you.id &&
      target.buildingType === "farm" &&
      target.completed &&
      hasVillager;
    const canGarrison =
      this.garrisonMode &&
      target?.kind === "building" &&
      target.ownerId === this.snapshot.you.id &&
      target.completed &&
      target.garrisonCapacity > target.garrisonedCount &&
      hasGarrisonableUnit;
    this.setHoveredTarget(canAttack ? target?.id : undefined);
    this.renderer.domElement.classList.toggle(
      "gather-cursor",
      (target?.kind === "resource" && hasVillager) || canFarm,
    );
    this.renderer.domElement.classList.toggle(
      "attack-cursor",
      canAttack,
    );
    this.renderer.domElement.classList.toggle(
      "work-cursor",
      canAssist || canRepair || canDeposit,
    );
    this.renderer.domElement.classList.toggle(
      "garrison-cursor",
      canGarrison,
    );
    combatCursor.classList.toggle("hidden", !canAttack);
    workCursor.classList.toggle("hidden", !(canAssist || canRepair));
    garrisonCursor.classList.toggle("hidden", !canGarrison);
    if (canAttack) {
      combatCursor.style.left = `${event.clientX + 8}px`;
      combatCursor.style.top = `${event.clientY + 8}px`;
    }
    if (canAssist || canRepair) {
      workCursor.style.left = `${event.clientX + 8}px`;
      workCursor.style.top = `${event.clientY + 8}px`;
    }
    if (canGarrison) {
      garrisonCursor.style.left = `${event.clientX + 8}px`;
      garrisonCursor.style.top = `${event.clientY + 8}px`;
    }
    const rallySelected = !hasUnit && [...this.selectedIds].some(id => {
      const state = this.entityStates.get(id);
      return state?.kind === "building" && state.ownerId === this.snapshot.you.id &&
        state.completed && BUILDING_DEFS[state.buildingType].trains.length > 0;
    });
    if (canGarrison || canDeposit || rallySelected) {
      const activity = this.rallyActivity(target);
      worldTooltip.innerHTML = `<strong>${canGarrison ? "Garrison" : canDeposit ? "Deposit resources" : "Set rally point"}</strong>
        <small>${canGarrison ? "LEFT-CLICK TO GARRISON · SHIFT QUEUES" : canDeposit ? "RIGHT-CLICK TO DEPOSIT · THEN RESUME GATHERING" : activity ? `RIGHT-CLICK: ${escapeHtml(activity.toUpperCase())} · NEW VILLAGERS START WORKING` : "RIGHT-CLICK: NEW UNITS MOVE HERE"}</small>`;
      worldTooltip.style.left = `${event.clientX + 20}px`;
      worldTooltip.style.top = `${event.clientY + 20}px`;
      worldTooltip.classList.remove("hidden");
    } else if (target?.kind === "resource") {
      const canClearTrees = target.resourceType === "wood" && this.snapshot.you.age >= 4 &&
        [...this.selectedIds].some((id) => { const unit = this.entityStates.get(id); return unit?.kind === "unit" && unit.unitType === "onager"; });
      const instruction = canClearTrees ? "PRESS T, THEN CLICK TO BOMBARD TREES" : hasVillager
        ? "RIGHT-CLICK TO GATHER"
        : "SELECT A VILLAGER, THEN RIGHT-CLICK";
      worldTooltip.innerHTML = `
        <strong>${resourceGlyph(target.resourceType)} ${RESOURCE_LABELS[target.resourceType]}</strong>
        <span>${Math.ceil(target.amount)} remaining</span>
        <small>${instruction}</small>`;
      worldTooltip.style.left = `${event.clientX + 16}px`;
      worldTooltip.style.top = `${event.clientY + 16}px`;
      worldTooltip.classList.remove("hidden");
    } else if (
      target?.kind === "building" &&
      target.ownerId === this.snapshot.you.id &&
      target.buildingType === "farm" &&
      target.completed
    ) {
      const depleted = (target.foodRemaining ?? 0) <= 0;
      worldTooltip.innerHTML = `
        <strong>🌾 Farm</strong>
        <span>${Math.ceil(target.foodRemaining ?? 0)} food remaining</span>
        <small>${
          depleted
            ? hasVillager
              ? "RIGHT-CLICK TO RESEED · 60 WOOD"
              : "SELECT VILLAGERS TO RESEED"
            : hasVillager
              ? "RIGHT-CLICK TO FARM"
              : "SELECT VILLAGERS TO FARM"
        }</small>`;
      worldTooltip.style.left = `${event.clientX + 16}px`;
      worldTooltip.style.top = `${event.clientY + 16}px`;
      worldTooltip.classList.remove("hidden");
    } else if (
      target?.kind === "building" &&
      target.ownerId === this.snapshot.you.id &&
      !target.completed
    ) {
      worldTooltip.innerHTML = `
        <strong>🔨 ${buildingLabel(target)}</strong>
        <span>${Math.round(target.progress * 100)}% constructed</span>
        <small>${hasVillager ? "RIGHT-CLICK TO HELP BUILD" : "SELECT VILLAGERS TO HELP"}</small>`;
      worldTooltip.style.left = `${event.clientX + 46}px`;
      worldTooltip.style.top = `${event.clientY + 16}px`;
      worldTooltip.classList.remove("hidden");
    } else if (
      target?.kind === "building" &&
      target.ownerId === this.snapshot.you.id &&
      target.completed &&
      target.hp < target.maxHp
    ) {
      worldTooltip.innerHTML = `
        <strong>🔧 ${buildingLabel(target)}</strong>
        <span>${Math.ceil(target.hp)} / ${target.maxHp} health</span>
        <small>${hasVillager ? "RIGHT-CLICK TO REPAIR" : "SELECT VILLAGERS TO REPAIR"}</small>`;
      worldTooltip.style.left = `${event.clientX + 46}px`;
      worldTooltip.style.top = `${event.clientY + 16}px`;
      worldTooltip.classList.remove("hidden");
    } else if (
      target?.kind === "building" &&
      target.ownerId === this.snapshot.you.id &&
      target.completed &&
      target.garrisonCapacity > 0
    ) {
      worldTooltip.innerHTML = `
        <strong>♜ ${buildingLabel(target)}</strong>
        <span>${target.garrisonedCount} / ${target.garrisonCapacity} garrisoned</span>
        <small>${
          hasGarrisonableUnit
            ? target.garrisonedCount < target.garrisonCapacity
              ? "G THEN LEFT-CLICK TO GARRISON"
              : "GARRISON FULL"
            : "SELECT VILLAGERS OR FOOT SOLDIERS"
        }</small>`;
      worldTooltip.style.left = `${event.clientX + 46}px`;
      worldTooltip.style.top = `${event.clientY + 16}px`;
      worldTooltip.classList.remove("hidden");
    } else if (
      target &&
      target.ownerId !== this.snapshot.you.id
    ) {
      worldTooltip.innerHTML = `
        <strong>⚔ Enemy ${target.kind === "unit" ? UNIT_DEFS[target.unitType].label : buildingLabel(target)}</strong>
        <span>${Math.ceil(target.hp)} / ${target.maxHp} health</span>
        ${target.kind === "unit" ? `<span class="tooltip-combat">${escapeHtml(unitCombatTooltip(target.unitType))}</span>` : ""}
        <small>${hasUnit ? "RIGHT-CLICK TO ATTACK" : "SELECT COMBAT UNITS TO ATTACK"}</small>`;
      worldTooltip.style.left = `${event.clientX + 46}px`;
      worldTooltip.style.top = `${event.clientY + 16}px`;
      worldTooltip.classList.remove("hidden");
    } else if (target?.kind === "unit") {
      worldTooltip.innerHTML = `
        <strong>${unitRoleCode(target.unitType)} · ${UNIT_DEFS[target.unitType].label}</strong>
        <span>${Math.ceil(target.hp)} / ${target.maxHp} health · ${target.state}</span>
        <span class="tooltip-combat">${escapeHtml(unitCombatTooltip(target.unitType))}</span>
        <small>${escapeHtml(UNIT_DEFS[target.unitType].lore)}</small>`;
      worldTooltip.style.left = `${event.clientX + 16}px`;
      worldTooltip.style.top = `${event.clientY + 16}px`;
      worldTooltip.classList.remove("hidden");
    } else {
      worldTooltip.classList.add("hidden");
    }
  }

  private createRallyFlag(color: number, preview = false): THREE.Group {
    const group = new THREE.Group();
    group.renderOrder = 25;

    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.85, 1.05, 28),
      new THREE.MeshBasicMaterial({
        color,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: preview ? 0.62 : 0.92,
        depthTest: false,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.08;

    const pole = new THREE.Mesh(
      new THREE.CylinderGeometry(0.055, 0.075, 2.7, 8),
      new THREE.MeshBasicMaterial({
        color: 0xe8dcc0,
        transparent: true,
        opacity: preview ? 0.65 : 1,
        depthTest: false,
      }),
    );
    pole.position.y = 1.35;

    const banner = new THREE.Mesh(
      new THREE.PlaneGeometry(1.15, 0.72),
      new THREE.MeshBasicMaterial({
        color,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: preview ? 0.56 : 0.95,
        depthTest: false,
      }),
    );
    banner.position.set(0.62, 2.15, 0);

    const tip = new THREE.Mesh(
      new THREE.ConeGeometry(0.13, 0.32, 6),
      new THREE.MeshBasicMaterial({
        color: 0xf3d78d,
        transparent: true,
        opacity: preview ? 0.65 : 1,
        depthTest: false,
      }),
    );
    tip.position.y = 2.86;

    group.add(ring, pole, banner, tip);
    return group;
  }

  private updateRallyPreview(event: PointerEvent): void {
    if (!this.rallyPreview) return;
    const point = this.groundPoint(event);
    if (!point) return;
    this.rallyPreview.visible = true;
    this.rallyPreview.position.set(point.x, 0, point.z);
    const activity = this.rallyActivity(this.contextTarget(event));
    placementHint.textContent = activity
      ? `Click: ${activity} · New villagers start working · Esc cancels`
      : "Click to place rally flag · New units will move here · Esc cancels";
  }

  private updateRallyIndicator(): void {
    this.updateOrderFlags();
    const key = [...this.selectedIds].map(id => {
      const state = this.entityStates.get(id);
      return state?.kind === "building" ? `${id}:${state.rallyPoint?.x}:${state.rallyPoint?.z}:${state.rallyTargetId}:${state.rallyActivity}` : "";
    }).join("|");
    if (key === this.rallyIndicatorKey) return;
    this.rallyIndicatorKey = key;
    if (this.rallyIndicator) {
      this.rallyIndicator.traverse(object => { if (object instanceof THREE.Sprite) object.material.map?.dispose(); });
      this.scene.remove(this.rallyIndicator);
      this.disposeGroup(this.rallyIndicator);
      this.rallyIndicator = undefined;
    }
    const building = [...this.selectedIds]
      .map((id) => this.entityStates.get(id))
      .find(
        (state): state is BuildingState =>
          Boolean(
            state &&
              state.kind === "building" &&
              state.ownerId === this.snapshot.you.id &&
              state.rallyPoint &&
              BUILDING_DEFS[state.buildingType].trains.length > 0,
          ),
      );
    if (!building?.rallyPoint) return;

    const color = new THREE.Color(this.snapshot.you.color).getHex();
    const indicator = this.createRallyFlag(color);
    if (building.rallyActivity) {
      const target = building.rallyTargetId ? this.entityStates.get(building.rallyTargetId) : undefined;
      const icon = target?.kind === "resource" ? resourceGlyph(target.resourceType) : building.rallyActivity === "farm" ? "🌾" : "🔨";
      const badge = this.createIconBadge(icon, "#f3d78d");
      badge.position.y = 3;
      indicator.add(badge);
    }
    indicator.position.set(building.rallyPoint.x, 0, building.rallyPoint.z);
    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(
          building.x - building.rallyPoint.x,
          0.12,
          building.z - building.rallyPoint.z,
        ),
        new THREE.Vector3(0, 0.12, 0),
      ]),
      new THREE.LineDashedMaterial({
        color,
        dashSize: 0.7,
        gapSize: 0.45,
        transparent: true,
        opacity: 0.68,
        depthTest: false,
      }),
    );
    line.computeLineDistances();
    indicator.add(line);
    this.rallyIndicator = indicator;
    this.scene.add(indicator);
  }

  private updateOrderFlags(): void {
    const keep = new Set<string>();
    for (const id of this.selectedIds) {
      const unit = this.entityStates.get(id);
      if (unit?.kind !== "unit" || unit.ownerId !== this.snapshot.you.id) continue;
      for (const [index, order] of (unit.orders ?? []).entries()) {
        const key = `${id}:${index}`;
        keep.add(key);
        let flag = this.orderFlags.get(key);
        if (!flag) {
          flag = this.createRallyFlag(index === 0 ? 0x9bd38b : 0xf3d78d);
          const badge = this.createIconBadge(String(index + 1), "#f3d78d");
          badge.position.y = 3;
          flag.add(badge);
          this.orderFlags.set(key, flag);
          this.scene.add(flag);
        }
        flag.position.set(order.x, 0, order.z);
      }
    }
    for (const [key, flag] of this.orderFlags) if (!keep.has(key)) {
      flag.traverse(object => { if (object instanceof THREE.Sprite) object.material.map?.dispose(); });
      this.scene.remove(flag);
      this.disposeGroup(flag);
      this.orderFlags.delete(key);
    }
  }

  private disposeGroup(group: THREE.Group): void {
    group.traverse((object) => {
      if (object instanceof THREE.Mesh || object instanceof THREE.Line) {
        if (!object.userData.sharedGeometry) object.geometry.dispose();
        if (Array.isArray(object.material)) {
          object.material.forEach((material) => material.dispose());
        } else {
          object.material.dispose();
        }
      } else if (object instanceof THREE.Sprite) {
        object.material.dispose();
      }
    });
  }

  private createPlacementGhost(type: BuildingType): THREE.Group {
    const definition = BUILDING_DEFS[type];
    const group = new THREE.Group();
    group.visible = false;
    group.renderOrder = 20;
    const footprintWidth = buildingHalf(type).x * 2;
    const footprintDepth = buildingHalf(type).z * 2;

    const foundation = new THREE.Mesh(
      new THREE.PlaneGeometry(footprintWidth, footprintDepth),
      new THREE.MeshBasicMaterial({
        color: 0x75d782,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.18,
        depthWrite: false,
      }),
    );
    foundation.rotation.x = -Math.PI / 2;
    foundation.position.y = 0.085;
    foundation.name = "placement-foundation";

    const model = this.createStructureVisual(type, new THREE.Color(0x75d782));
    const modelHeight = new THREE.Box3().setFromObject(model).max.y;
    const ghostMaterial = new THREE.MeshBasicMaterial({
      color: 0x75d782, transparent: true, opacity: 0.45, depthWrite: false,
    });
    model.traverse((child) => {
      if (child instanceof THREE.Mesh) child.material = ghostMaterial;
      child.raycast = () => {};
    });
    group.add(model);

    const blueprintLabel = this.createWorldLabel(
      `${definition.label.toUpperCase()} · CLICK TO PLACE`,
      0x75d782,
    );
    blueprintLabel.name = "placement-label";
    blueprintLabel.position.y = modelHeight + 0.65;
    blueprintLabel.scale.set(6.2, 1.55, 1);
    blueprintLabel.renderOrder = 24;
    group.add(blueprintLabel);

    const footprint = new THREE.Mesh(
      this.createRectRingGeometry(footprintWidth, footprintDepth, 0.1),
      new THREE.MeshBasicMaterial({
        color: 0x92e09a,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.9,
        depthTest: false,
      }),
    );
    footprint.rotation.x = -Math.PI / 2;
    footprint.position.y = 0.1;
    footprint.name = "placement-footprint";
    const gridMaterial = new THREE.MeshBasicMaterial({ color: 0x9cf3ae, transparent: true, opacity: .55, depthTest: false });
    for (let x = -footprintWidth / 2 + NAVIGATION_CELL_SIZE; x < footprintWidth / 2; x += NAVIGATION_CELL_SIZE) {
      const line = new THREE.Mesh(new THREE.BoxGeometry(.035, .025, footprintDepth), gridMaterial);
      line.position.set(x, .12, 0); group.add(line);
    }
    for (let z = -footprintDepth / 2 + NAVIGATION_CELL_SIZE; z < footprintDepth / 2; z += NAVIGATION_CELL_SIZE) {
      const line = new THREE.Mesh(new THREE.BoxGeometry(footprintWidth, .025, .035), gridMaterial);
      line.position.set(0, .12, z); group.add(line);
    }
    group.add(foundation, footprint);
    return group;
  }

  private updatePlacementPreview(event: PointerEvent): void {
    const point = this.groundPoint(event);
    if (!point) return;
    this.updatePlacementAtPoint(point);
  }

  private updatePlacementAtPoint(point: THREE.Vector3): void {
    if (!this.placementType || !this.placementGhost) return;
    const definition = BUILDING_DEFS[this.placementType];
    let placementPoint = snapBuildingPoint(point, this.placementType);
    const gateAnchor =
      this.placementType === "gate"
        ? [...this.entityStates.values()]
            .filter(
              (state): state is BuildingState =>
                state.kind === "building" &&
                state.ownerId === this.snapshot.you.id &&
                state.buildingType === "wall" &&
                Math.hypot(state.x - point.x, state.z - point.z) <=
                  definition.size,
            )
            .sort(
              (left, right) =>
                Math.hypot(left.x - point.x, left.z - point.z) -
                Math.hypot(right.x - point.x, right.z - point.z),
            )[0]
        : undefined;
    if (gateAnchor) {
      placementPoint = { x: gateAnchor.x, z: gateAnchor.z };
    }
    this.placementGhost.visible = true;
    this.placementGhost.position.set(placementPoint.x, 0, placementPoint.z);
    if (this.placementType === "gate") {
      const nearestPalisade = [...this.entityStates.values()]
        .filter(
          (state): state is BuildingState =>
            state.kind === "building" &&
            state.ownerId === this.snapshot.you.id &&
            (state.buildingType === "wall" ||
              state.buildingType === "gate") &&
            Math.hypot(
              state.x - placementPoint.x,
              state.z - placementPoint.z,
            ) <= 7,
        )
        .sort(
          (left, right) =>
            Math.hypot(
              left.x - placementPoint.x,
              left.z - placementPoint.z,
            ) -
            Math.hypot(
              right.x - placementPoint.x,
              right.z - placementPoint.z,
            ),
        )[0];
      this.placementGhost.rotation.y =
        gateAnchor?.facing ?? nearestPalisade?.facing ?? 0;
    } else if (this.placementType !== "wall") {
      this.placementGhost.rotation.y = 0;
    }
    const villagers = [...this.selectedIds].filter((id) => {
      const state = this.entityStates.get(id);
      return state?.kind === "unit" && state.unitType === "villager";
    });
    const half = buildingHalf(this.placementType, this.placementGhost.rotation.y);
    const replaceableWalls = [...this.entityStates.values()].filter(
      (state): state is BuildingState =>
        this.placementType === "gate" &&
        state.kind === "building" &&
        state.ownerId === this.snapshot.you.id &&
        state.buildingType === "wall" &&
        Math.hypot(state.x - placementPoint.x, state.z - placementPoint.z) <
          (BUILDING_DEFS.wall.size + definition.size) * 0.55,
    );
    const replaceableWallIds = new Set(
      replaceableWalls.map((state) => state.id),
    );
    const buildingOverlap = [...this.entityStates.values()].some((state) => {
      if (state.kind !== "building" || replaceableWallIds.has(state.id)) {
        return false;
      }
      return buildingOverlapAt(state, this.placementType!, placementPoint, this.placementGhost!.rotation.y);
    });
    const resourceOverlap = [...this.entityStates.values()].some(state =>
      state.kind === "resource" && buildingBlocksResource(this.placementType!, placementPoint,
        this.placementGhost!.rotation.y, state));
    const waterOverlap = this.terrainBlocksPlacement(
      placementPoint.x,
      placementPoint.z,
      half.x,
      half.z,
    );
    const edgeX = this.placementType === "wall" ? 0 : half.x;
    const edgeZ = this.placementType === "wall" ? 0 : half.z;
    this.placementReason =
      villagers.length === 0
        ? "Select at least one villager."
        : this.snapshot.you.age < definition.age
          ? `Requires ${AGE_NAMES[definition.age]}.`
          : !hasResources(this.snapshot.you.resources, definition.cost)
            ? `Need ${formatCost(definition.cost)}.`
            : placementPoint.x < edgeX || placementPoint.z < edgeZ ||
                placementPoint.x > this.snapshot.mapSize - edgeX ||
                placementPoint.z > this.snapshot.mapSize - edgeZ
              ? "Too close to the map border."
              : buildingOverlap
                ? "Overlaps another building."
                : resourceOverlap
                  ? "Blocks a resource deposit."
                  : waterOverlap
                    ? "Cannot build in deep water."
                    : "";
    this.placementValid = this.placementReason === "";
    const color = this.placementValid ? 0x75d782 : 0xef665c;
    this.placementGhost.traverse((object) => {
      if (
        object instanceof THREE.Mesh &&
        !Array.isArray(object.material) &&
        "color" in object.material
      ) {
        (object.material as THREE.MeshBasicMaterial).color.setHex(color);
      } else if (object instanceof THREE.Sprite) {
        object.material.color.setHex(this.placementValid ? 0x9cf3ae : 0xff7d73);
      }
    });
    placementHint.textContent = this.placementValid
      ? replaceableWalls.length > 0
        ? `Click to replace ${replaceableWalls.length} wall ${replaceableWalls.length === 1 ? "segment" : "segments"} with ${definition.label} · ${formatCost(definition.cost)} · Esc cancels`
        : `Click to place ${definition.label} · ${formatCost(definition.cost)} · Esc cancels`
      : `${this.placementReason} · Esc cancels`;
    placementHint.classList.toggle("invalid", !this.placementValid);
    if (this.placementValid && this.placementType === "wall") {
      placementHint.textContent =
        `Click for one segment · Shift-drag for a line · ${formatCost(definition.cost)} · Esc finishes`;
    }
  }

  private terrainBlocksPlacement(
    x: number,
    z: number,
    clearance: number,
    clearanceZ = clearance,
  ): boolean {
    return this.snapshot.terrainTiles.some(
      (tile) =>
        tile.kind === "water" &&
        Math.abs(x - tile.x) < TERRAIN_TILE_SIZE / 2 + clearance &&
        Math.abs(z - tile.z) < TERRAIN_TILE_SIZE / 2 + clearanceZ,
    );
  }

  private showOrderMarker(position: Vec2, color: number): void {
    const material = new THREE.MeshBasicMaterial({
      color,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.95,
      depthTest: false,
    });
    const marker = new THREE.Mesh(
      new THREE.RingGeometry(0.55, 0.72, 32),
      material,
    );
    marker.rotation.x = -Math.PI / 2;
    marker.position.set(position.x, 0.14, position.z);
    marker.renderOrder = 12;
    this.scene.add(marker);
    this.orderMarkers.push({ mesh: marker, startedAt: performance.now() });
  }

  private showOrderAcknowledgement(message: string): void {
    showEvent(`Order: ${message}.`, "info");
  }

  private updateSelectionHud(): void {
    const selected = [...this.selectedIds]
      .map((id) => this.entityStates.get(id))
      .filter((state): state is UnitState | BuildingState =>
        Boolean(state && state.kind !== "resource"),
      );
    if (selected.length === 0) {
      selectionInfo.innerHTML = `
        <div class="empty-selection">
          <strong>No selection</strong>
          <span>Click or drag over your forces.</span>
          <small>WASD pan · Wheel zoom · Space centres · Ctrl+1–9 groups</small>
        </div>`;
      commandPanel.innerHTML = `
        <div class="command-primer">
          <span>RIGHT CLICK</span><strong>Move · Gather · Attack</strong>
        </div>`;
      return;
    }
    const first = selected[0];
    if (selected.length > 1) {
      const counts = new Map<string, number>();
      selected.forEach((state) => {
        const label =
          state.kind === "unit"
            ? UNIT_DEFS[state.unitType].label
            : buildingLabel(state);
        counts.set(label, (counts.get(label) ?? 0) + 1);
      });
      selectionInfo.innerHTML = `
        <small>FORMATION</small>
        <strong>${selected.length} selected</strong>
        <span>${[...counts].map(([label, count]) => `${count} ${label}`).join(" · ")}</span>`;
    } else {
      const label =
        first.kind === "unit"
          ? UNIT_DEFS[first.unitType].label
          : buildingLabel(first);
      const description =
        first.kind === "unit"
          ? UNIT_DEFS[first.unitType].description
          : BUILDING_DEFS[first.buildingType].description;
      const isEnemy = first.ownerId !== this.snapshot.you.id;
      const owner = this.snapshot.players.find(
        (player) => player.id === first.ownerId,
      );
      selectionInfo.innerHTML = `
        <small>${isEnemy ? `ENEMY ${first.kind === "unit" ? "UNIT" : "STRUCTURE"} · ${escapeHtml(owner?.name ?? "Rival")}` : first.kind === "unit" ? `${first.state.toUpperCase()} · ${first.stance.toUpperCase()}` : first.completed ? "STRUCTURE" : `BUILDING ${Math.round(first.progress * 100)}%`}</small>
        <strong>${label}</strong>
        <div class="health-bar${isEnemy ? " enemy" : ""}"><i style="width:${(first.hp / first.maxHp) * 100}%"></i></div>
        <span>${Math.ceil(first.hp)} / ${first.maxHp} · ${description}${first.kind === "unit" && first.carriedAmount > 0 ? ` · Carrying ${Math.ceil(first.carriedAmount)}/15 ${first.carriedResource}` : ""}</span>
        ${first.kind === "unit" ? renderUnitCombatProfile(first.unitType) : ""}
        ${first.kind === "building" && first.rallyPoint ? `<div class="worker-tip"><b>RALLY</b> ${escapeHtml(this.rallyActivity(first.rallyTargetId ? this.entityStates.get(first.rallyTargetId) : undefined) ?? "Move to flag")} · Right-click ground or a work site to change.</div>` : ""}
        ${
          first.kind === "building" && first.buildingType === "farm"
            ? (first.foodRemaining ?? 0) > 0
              ? `<div class="worker-tip"><b>FOOD</b> ${Math.ceil(first.foodRemaining ?? 0)} remaining · Villagers auto-reseed for 60 wood.</div>`
              : `<div class="worker-tip"><b>WAITING TO RESEED</b> Villagers will resume automatically when you have ${formatCost(BUILDING_DEFS.farm.cost)}. Right-click or press Q to reseed now.</div>`
            : ""
        }
        ${
          first.kind === "unit" && first.queuedTasks > 0
            ? `<div class="worker-tip"><b>ORDER QUEUE</b> ${first.queuedTasks} ${first.queuedTasks === 1 ? "task" : "tasks"} waiting · Shift + right-click adds more.</div>`
            : ""
        }
        ${first.kind === "building" ? `<div class="structure-facts">
          ${first.garrisonCapacity > 0 ? `<span><b>Garrison</b> ${first.garrisonedCount} / ${first.garrisonCapacity}</span>` : ""}
          ${BUILDING_DEFS[first.buildingType].defensiveRange > 0 ? `<span><b>Range</b> ${BUILDING_DEFS[first.buildingType].defensiveRange} tiles</span><span><b>Fire</b> ${first.buildingType === "town_center" && first.garrisonedCount === 0 ? "Needs garrison" : "Automatic"}</span>` : ""}
          ${BUILDING_DEFS[first.buildingType].populationCap > 0 ? `<span><b>Population</b> +${BUILDING_DEFS[first.buildingType].populationCap}</span>` : ""}
        </div>` : ""}`;
    }
    const selectedUnits = selected.filter(
      (state): state is UnitState => state.kind === "unit",
    );
    if (selectedUnits.length > 0) {
      selectionInfo.insertAdjacentHTML(
        "beforeend",
        `<div class="stance-controls compact" aria-label="Unit stance">
          <span>STANCE</span>
          ${(["passive", "defensive", "aggressive"] as UnitStance[])
            .map((stance) => {
              const active = selectedUnits.every((unit) => unit.stance === stance);
              const detail = {
                passive: "Explicit orders only",
                defensive: "Retaliate when attacked",
                aggressive: "Engage enemies in sight",
              }[stance];
              const icon = stance === "passive" ? "○" : stance === "defensive" ? "◈" : "⚔";
              return `<button type="button" data-action="stance:${stance}" class="${active ? "active" : ""}" title="${detail}">${icon}<span>${stance}</span></button>`;
            })
            .join("")}
        </div>`,
      );
    }
    const deletableSelection = selected.filter(
      (state) =>
        state.ownerId === this.snapshot.you.id &&
        !(state.kind === "building" && !state.completed),
    );
    if (deletableSelection.length > 0) {
      selectionInfo.insertAdjacentHTML(
        "beforeend",
        `<button type="button" class="selection-delete" data-action="delete-selected" title="Permanently delete the selected ${deletableSelection.length === 1 ? "object" : "objects"} (Delete)">Delete${deletableSelection.length > 1 ? ` ${deletableSelection.length}` : ""}</button>`,
      );
    }
    this.renderCommands(selected);
  }

  private renderCommands(selected: (UnitState | BuildingState)[]): void {
    const first = selected[0];
    if (first.ownerId !== this.snapshot.you.id) {
      commandPanel.innerHTML = `
        <div class="command-primer enemy-intel">
          <span>ENEMY INTELLIGENCE</span>
          <strong>Keep this structure in sight to track its health.</strong>
        </div>`;
      return;
    }
    const buttons: string[] = [];
    const hasVillager = selected.some(
      (state) => state.kind === "unit" && state.unitType === "villager",
    );
    if (!hasVillager) this.workerBuildMenu = undefined;
    if (first.kind === "building" && !first.completed) {
      buttons.push(
        actionButton(
          "×",
          "Cancel Construction",
          "Remove blueprint and refund unused materials",
          `cancel-construction:${first.id}`,
          false,
          "X",
          "wide",
        ),
      );
    }
    if (hasVillager) {
      if (!this.workerBuildMenu) {
        buttons.push(
          actionButton(
            "⌂",
            "Economic Buildings",
            "Homes, farms, and trade",
            "build-menu:eco",
            false,
            "Q",
            "build-category",
          ),
          actionButton(
            "⚔",
            "Military Buildings",
            "Production and defences",
            "build-menu:military",
            false,
            "E",
            "build-category",
          ),
        );
      } else {
        const types: BuildingType[] =
          this.workerBuildMenu === "eco"
            ? ["house", "farm", "camp", "market", "town_center"]
            : [
                "barracks",
                "archery_range",
                "stable",
                "wall",
                "gate",
                "tower",
                "castle",
              ];
        const hotkeys = ["Q", "E", "R", "T", "Y", "U", "I"];
        buttons.push(
          actionButton(
            "←",
            "Build Categories",
            "Back to ECO / MILITARY",
            "build-menu:root",
            false,
            "",
            "build-category-back",
          ),
        );
        for (const [index, type] of types.entries()) {
          const definition = BUILDING_DEFS[type];
          const requiredAge = type === "town_center" ? 3 : definition.age;
          const locked = this.snapshot.you.age < requiredAge;
          buttons.push(
            actionButton(
              locked ? "🔒" : buildingGlyph(type),
              definition.label,
              locked ? `Age ${requiredAge}` : formatCost(definition.cost),
              `build:${type}`,
              locked,
              hotkeys[index],
              "",
              buildingHelp(type),
            ),
          );
        }
      }
    }
    if (first.kind === "building" && first.completed) {
      if (
        first.buildingType === "farm" &&
        (first.foodRemaining ?? 0) <= 0
      ) {
        buttons.push(
          actionButton(
            "🌱",
            "Reseed Farm",
            formatCost(BUILDING_DEFS.farm.cost),
            `reseed:${first.id}`,
            !hasResources(
              this.snapshot.you.resources,
              BUILDING_DEFS.farm.cost,
            ),
            "Q",
            "wide",
          ),
        );
      }
      for (const [index, unitType] of BUILDING_DEFS[first.buildingType].trains.entries()) {
        const definition = UNIT_DEFS[unitType];
        const locked = this.snapshot.you.age < definition.age;
        buttons.push(
          actionButton(
            locked ? "🔒" : unitGlyph(unitType),
            this.keys.has("shift") ? `5× ${definition.label}` : `Train ${definition.label}`,
            locked ? `Age ${definition.age}` : formatCost(definition.cost),
            `train:${first.id}:${unitType}`,
            locked,
            ["Q", "E", "T"][index] ?? "",
            "",
            unitCombatTooltip(unitType),
          ),
        );
      }
      if (first.buildingType === "town_center" && this.snapshot.you.age >= 4) {
        const cost = this.snapshot.you.spiesCost ?? 200;
        buttons.push(actionButton("◉", this.snapshot.you.hasSpies ? "Spies active" : "Spies",
          this.snapshot.you.hasSpies ? "Permanent shared enemy vision" : `${cost.toLocaleString()} gold · Shared enemy vision`,
          `spies:${first.id}:${cost}`, Boolean(this.snapshot.you.hasSpies) || this.snapshot.you.resources.gold < cost, "Y", "",
          "200 gold + 1,000 per surviving enemy villager, including garrisons. Permanently see enemy units, buildings and their current vision."));
      }
      if (first.buildingType === "town_center" && this.snapshot.you.age < 4) {
        const nextAge = this.snapshot.you.age + 1;
        buttons.push(
          actionButton(
            "♛",
            `Advance to ${AGE_NAMES[nextAge]}`,
            formatCost(AGE_COSTS[nextAge]),
            `age:${first.id}`,
            false,
            "T",
            "wide",
          ),
        );
      }
      if (BUILDING_DEFS[first.buildingType].trains.length > 0) {
        buttons.push(
          actionButton(
            "⚑",
            first.rallyPoint ? "Move Rally Flag" : "Set Rally Flag",
            "New units automatically move here",
            `rally:${first.id}`,
            false,
            "R",
          ),
        );
      }
      if (first.garrisonCapacity > 0 && first.garrisonedCount > 0) {
        buttons.push(
          actionButton(
            "↗",
            "Eject All",
            `${first.garrisonedCount} garrisoned`,
            `ungarrison:${first.id}`,
            false,
            "G",
          ),
        );
        for (const unit of first.garrisonedUnits) {
          buttons.push(
            actionButton(
              unitGlyph(unit.unitType),
              `Eject ${UNIT_DEFS[unit.unitType].label}`,
              `${Math.ceil(unit.hp)} / ${unit.maxHp} health`,
              `ungarrison:${first.id}:${unit.id}`,
              false,
            ),
          );
        }
      }
      if (first.buildingType === "market") {
        const tradeResources = ["food", "wood", "stone"] as const;
        const sellKeys = ["Q", "W", "E"];
        const buyKeys = ["Z", "X", "C"];
        for (const [index, resource] of tradeResources.entries()) {
          buttons.push(
            actionButton(
              resourceGlyph(resource),
              `Sell 100 ${RESOURCE_LABELS[resource]}`,
              "Receive 70 gold · Shift-click trades 500",
              `trade:${first.id}:sell:${resource}`,
              this.snapshot.you.resources[resource] < 100,
              sellKeys[index],
            ),
          );
        }
        for (const [index, resource] of tradeResources.entries()) {
          buttons.push(
            actionButton(
              "◆",
              `Buy 100 ${RESOURCE_LABELS[resource]}`,
              "Costs 130 gold · Shift-click trades 500",
              `trade:${first.id}:buy:${resource}`,
              this.snapshot.you.resources.gold < 130,
              buyKeys[index],
            ),
          );
        }
      }
    }
    if (
      !this.workerBuildMenu && selected.some(
        (state) => state.kind === "unit",
      )
    ) {
      buttons.push(
        actionButton("⚔", "Attack Move", "Engage along a route; click a destination", "attack-move", false, "F"),
        actionButton("■", "Stop", "Cancel current orders", "stop", false, "X"),
      );
      if (selected.some(state => state.kind === "unit" && ["villager", "spearman", "archer", "royal_guard"].includes(state.unitType))) {
        buttons.push(actionButton("♜", "Garrison", "Click a friendly building · Shift queues", "garrison-mode", false, "G"));
      }
      const scouts = selected.filter((state): state is UnitState => state.kind === "unit" && state.unitType === "scout");
      if (scouts.length) buttons.push(actionButton("⚑", scouts.every(unit => unit.autoScouting) ? "Stop scouting" : "Auto scout", "Explore unexplored terrain · Any new order cancels", "auto-scout", false, "Z"));
    }
    if (selected.some(state => state.kind === "unit" && state.unitType === "onager")) {
      buttons.push(actionButton("☄", "Attack Ground", "Fire one area shot at a ground position", "attack-ground", false, "B"));
      if (this.snapshot.you.age >= 4) buttons.push(actionButton("♠", "Clear Trees", "Bombard trees with an Imperial onager", "clear-trees", false, "T"));
    }
    const commandHtml =
      `<div class="action-grid${hasVillager ? " worker-actions" : ""}">${buttons.join("")}</div>` +
      (first.kind === "building" && first.queue.length
        ? `<div class="production-queue">${buildingQueueMarkup(first)}</div>`
        : "");
    reconcileMarkup(commandPanel, commandHtml);
  }

  private handleCommandAction(action: string, bulk = false): void {
    if (action.startsWith("spies:")) {
      const [, buildingId, cost] = action.split(":");
      void this.sendCommand({ type: "research_spies", buildingId, expectedCost: Number(cost) }).then(result => {
        if (result.ok) { soundDesign.select(); this.showOrderAcknowledgement("Spies researched · Enemy vision shared"); }
      });
      return;
    }
    if (action === "garrison-mode") {
      this.cancelPlacement(false);
      this.garrisonMode = true;
      placementHint.textContent = "Garrison: click a friendly building · Shift queues · Right-click cancels";
      placementHint.classList.remove("hidden", "invalid");
      return;
    }
    if (action === "auto-scout") {
      const scouts = [...this.selectedIds].map(id => this.entityStates.get(id)).filter(
        (state): state is UnitState => state?.kind === "unit" && state.unitType === "scout",
      );
      if (!scouts.length) return;
      const stop = scouts.every(unit => unit.autoScouting);
      void this.sendCommand({ type: stop ? "stop" : "auto_scout", unitIds: scouts.map(unit => unit.id) }, () => {
        this.showOrderAcknowledgement(stop ? "Auto scout stopped" : "Auto scout: exploring the map");
        soundDesign.move("scout");
      });
      return;
    }
    if (action === "attack-ground" || action === "clear-trees") {
      this.cancelPlacement(false);
      this.siegeTargetMode = action === "attack-ground" ? "ground" : "trees";
      placementHint.textContent = `${action === "attack-ground" ? "Attack ground: click an area" : "Clear trees: click a tree"} · Shift queues · Right-click cancels`;
      placementHint.classList.remove("hidden", "invalid");
      return;
    }
    if (action === "attack-move") {
      this.cancelPlacement(false);
      this.attackMoveMode = true;
      placementHint.textContent = "Attack move: click a destination · Shift queues · Right-click cancels";
      placementHint.classList.remove("hidden", "invalid");
      return;
    }
    if (action.startsWith("build-menu:")) {
      const menu = action.slice("build-menu:".length);
      this.workerBuildMenu =
        menu === "eco" || menu === "military" ? menu : undefined;
      soundDesign.select();
      this.refreshSelection();
      return;
    }
    if (action.startsWith("stance:")) {
      const stance = action.slice(7) as UnitStance;
      const unitIds = [...this.selectedIds].filter(
        (id) => this.entityStates.get(id)?.kind === "unit",
      );
      if (unitIds.length === 0) return;
      void this.sendCommand({
        type: "set_stance",
        unitIds,
        stance,
      }).then((result) => {
        if (!result.ok) return;
        soundDesign.stance();
        this.showOrderAcknowledgement(
          `${stance[0].toUpperCase()}${stance.slice(1)} stance`,
        );
      });
      return;
    }
    if (action.startsWith("build:")) {
      this.workerBuildMenu = undefined;
      this.beginPlacement(action.slice(6) as BuildingType);
      return;
    }
    if (action.startsWith("train:")) {
      const [, buildingId, unitType] = action.split(":");
      const anchor = this.entityStates.get(buildingId);
      const buildingIds = [...this.selectedIds].filter(id => {
        const building = this.entityStates.get(id);
        return anchor?.kind === "building" && building?.kind === "building" &&
          building.ownerId === this.snapshot.you.id && building.completed && building.buildingType === anchor.buildingType;
      });
      void Promise.all(Array.from({ length: bulk ? 5 : 1 }, () => this.sendCommand({
        type: "train",
        buildingId,
        buildingIds,
        unitType: unitType as UnitType,
      }, undefined, false))).then((results) => {
        const failure = results.find(result => !result.ok);
        if (failure) showEvent(failure.error ?? "The server rejected that order.", "warning");
        const queued = results.filter(result => result.ok).length;
        if (queued) {
          soundDesign.train(unitType as UnitType);
          this.showOrderAcknowledgement(
            `${queued}× ${UNIT_DEFS[unitType as UnitType].label} queued`,
          );
        }
      });
      return;
    }
    if (action.startsWith("ungarrison:")) {
      const [, buildingId, unitId] = action.split(":");
      void this.sendCommand({
        type: "ungarrison",
        buildingId,
        unitIds: unitId ? [unitId] : [],
      }).then((result) => {
        if (!result.ok) return;
        soundDesign.ungarrison();
        this.showOrderAcknowledgement(unitId ? "Unit ejected" : "Garrison emptied");
      });
      return;
    }
    if (action.startsWith("age:")) {
      void this.sendCommand({
        type: "advance_age",
        buildingId: action.slice(4),
      }).then((result) => {
        if (result.ok) {
          soundDesign.click();
          this.showOrderAcknowledgement("Age advancement started");
        }
      });
      return;
    }
    if (action.startsWith("cancel-queue:")) {
      const [, buildingId, queueItemId] = action.split(":");
      const building = this.entityStates.get(buildingId);
      const item = building?.kind === "building" ? building.queue.find(item => item.id === queueItemId) : undefined;
      const ids = bulk && item && building?.kind === "building"
        ? building.queue.filter(candidate => candidate.key === item.key).slice(-5).map(candidate => candidate.id)
        : [queueItemId];
      void Promise.all(ids.map(id => this.sendCommand({
        type: "cancel_queue_item",
        buildingId,
        queueItemId: id,
      }))).then((results) => {
        const cancelled = results.filter(result => result.ok).length;
        if (!cancelled) return;
        soundDesign.cancel();
        this.showOrderAcknowledgement(`${cancelled} production ${cancelled === 1 ? "order" : "orders"} cancelled and refunded`);
      });
      return;
    }
    if (action.startsWith("rally:")) {
      this.beginRallyPlacement(action.slice(6));
      return;
    }
    if (action.startsWith("trade:")) {
      const [, buildingId, direction, resourceType] = action.split(":") as [
        string,
        string,
        "buy" | "sell",
        Exclude<ResourceKind, "gold">,
      ];
      void this.sendCommand({
        type: "trade",
        buildingId,
        direction,
        resourceType,
        quantity: bulk ? 5 : 1,
      }).then((result) => {
        if (!result.ok) return;
        soundDesign.trade(direction);
        this.showOrderAcknowledgement(
          `${direction === "sell" ? "Sold" : "Bought"} ${bulk ? 500 : 100} ${RESOURCE_LABELS[resourceType].toLowerCase()}`,
        );
      });
      return;
    }
    if (action.startsWith("reseed:")) {
      void this.sendCommand({
        type: "reseed_farm",
        buildingId: action.slice(7),
      }).then((result) => {
        if (!result.ok) return;
        soundDesign.build();
        this.showOrderAcknowledgement("Farm reseeded");
      });
      return;
    }
    if (action.startsWith("cancel-construction:")) {
      void this.sendCommand({
        type: "cancel_construction",
        buildingId: action.slice("cancel-construction:".length),
      }).then((result) => {
        if (!result.ok) return;
        soundDesign.cancel();
        this.showOrderAcknowledgement("Construction cancelled");
      });
      return;
    }
    if (action === "stop") {
      const unitIds = [...this.selectedIds].filter(
        (id) => this.entityStates.get(id)?.kind === "unit",
      );
      if (unitIds.length > 0) {
        void this.sendCommand({ type: "stop", unitIds });
      }
      return;
    }
    if (action === "delete-selected") {
      const entityIds = [...this.selectedIds].filter((id) => {
        const state = this.entityStates.get(id);
        return (
          state &&
          state.kind !== "resource" &&
          state.ownerId === this.snapshot.you.id
        );
      });
      if (entityIds.length === 0) return;
      const first = this.entityStates.get(entityIds[0]);
      const selectedLabel =
        entityIds.length > 1 ? `these ${entityIds.length} selected objects`
          : first?.kind === "unit" ? UNIT_DEFS[first.unitType].label
          : first?.kind === "building" ? buildingLabel(first)
          : "this object";
      this.keys.clear();
      this.edgePanInput.set(0, 0);
      void requestDeleteConfirmation(deleteConfirmation, selectedLabel).then(async confirmed => {
        if (!confirmed) return;
        const result = await this.sendCommand({
          type: "delete_entities",
          entityIds,
        });
        if (!result.ok) return;
        for (const id of entityIds) this.selectedIds.delete(id);
        this.refreshSelection();
        soundDesign.death();
        this.showOrderAcknowledgement(
          `${entityIds.length} selected ${entityIds.length === 1 ? "entity" : "entities"} deleted`,
        );
      });
    }
  }

  private async sendCommand(command: CommandPayload, onSent?: () => void, showErrors = true): Promise<OperationResult> {
    if (matchUnavailable) { setGameMenuOpen(true); return { ok: false, error: "This match has ended." }; }
    if (this.snapshot.isSpectator) return { ok: false, error: "Spectators cannot issue orders." };
    if (
      !connectionReady ||
      this.gameConnection.state !== HubConnectionState.Connected
    ) {
      const result = {
        ok: false,
        error: "Reconnecting to the match. Your army is holding position.",
      };
      if (showErrors) showEvent(result.error, "info");
      return result;
    }
    if (this.snapshot.isPaused) {
      const result = {
        ok: false,
        error: "The match is paused. Press P or Resume before issuing orders.",
      };
      if (showErrors) showEvent(result.error, "info");
      return result;
    }
    try {
      const pending = this.gameConnection.invoke<OperationResult>(
        "Command",
        command,
      );
      onSent?.();
      const result = await pending;
      if (!result.ok && result.error === "Match not found.") {
        markMatchUnavailable();
        return result;
      }
      if (!result.ok && showErrors) {
        showEvent(result.error ?? "The server rejected that order.", "warning");
      }
      return result;
    } catch (error) {
      const result = { ok: false, error: String(error) };
      if (showErrors) showEvent(`Order failed: ${result.error}`, "warning");
      return result;
    }
  }

  private refreshSelection(): void {
    for (const [id, object] of this.entityObjects) {
      const ring = object.getObjectByName("selection-ring");
      if (ring) ring.visible = this.selectedIds.has(id);
      const defensiveRange = object.getObjectByName("defensive-range");
      const state = this.entityStates.get(id);
      if (defensiveRange && state?.kind === "building") defensiveRange.visible = state.completed &&
        (state.ownerId === this.snapshot.you.id || this.selectedIds.has(id));
    }
    this.updateSelectionHud();
    this.updateControlHotbar();
    this.updateRallyIndicator();
  }

  private assignControlGroup(number: number): void {
    const ids = [...this.selectedIds].filter((id) => {
      const state = this.entityStates.get(id);
      return (
        state &&
        state.kind !== "resource" &&
        state.ownerId === this.snapshot.you.id
      );
    });
    if (ids.length === 0) {
      this.controlGroups.delete(number);
      showEvent(`Control group ${number} cleared.`, "info");
    } else {
      this.controlGroups.set(number, ids);
      showEvent(
        `Control group ${number} assigned to ${ids.length} ${ids.length === 1 ? "entity" : "entities"}.`,
        "info",
      );
    }
    this.updateControlHotbar();
  }

  private recallControlGroup(number: number): void {
    const ids = this.controlGroups.get(number) ?? [];
    if (ids.length === 0) return;
    this.selectIds(ids);
    this.centerOnSelection();
    soundDesign.select();
  }

  private updateControlHotbar(): void {
    for (let number = 1; number <= 9; number += 1) {
      const button = controlHotbar.querySelector<HTMLButtonElement>(
        `[data-group="${number}"]`,
      );
      if (!button) continue;
      const states = (this.controlGroups.get(number) ?? [])
        .map((id) => this.entityStates.get(id))
        .filter(
          (state): state is UnitState | BuildingState =>
            Boolean(state && state.kind !== "resource"),
        );
      const icon = button.querySelector<HTMLElement>("span")!;
      const summary = button.querySelector<HTMLElement>("small")!;
      button.classList.toggle("empty", states.length === 0);
      const isActive =
        states.length > 0 &&
        states.length === this.selectedIds.size &&
        states.every((state) => this.selectedIds.has(state.id));
      button.classList.toggle("active", isActive);
      if (states.length === 0) {
        icon.textContent = "—";
        summary.textContent = "EMPTY";
        button.title = `Ctrl+${number} assigns the current selection`;
        continue;
      }
      const codes = new Set(states.map(controlGroupCode));
      icon.textContent = codes.size === 1 ? [...codes][0] : "◆";
      const units = states.filter((state) => state.kind === "unit").length;
      const buildings = states.length - units;
      summary.textContent =
        units === states.length
          ? `${states.length} UNIT${states.length === 1 ? "" : "S"}`
          : buildings === states.length
            ? `${states.length} BLDG${states.length === 1 ? "" : "S"}`
            : `${states.length} MIXED`;
      const counts = new Map<string, number>();
      for (const state of states) {
        const label =
          state.kind === "unit"
            ? UNIT_DEFS[state.unitType].label
            : buildingLabel(state);
        counts.set(label, (counts.get(label) ?? 0) + 1);
      }
      button.title = `Group ${number}: ${[...counts]
        .map(([label, count]) => `${count} ${label}`)
        .join(", ")}. Click to recall; Ctrl+click to replace.`;
    }
  }

  private playSelectionSound(): void {
    const state = this.entityStates.get([...this.selectedIds][0]);
    soundDesign.select(state?.kind === "unit" ? state.unitType : state?.kind === "building" ? state.buildingType : undefined);
  }

  private selectIds(ids: string[]): void {
    this.selectedIds.clear();
    ids.forEach((id) => {
      const state = this.entityStates.get(id);
      if (
        state &&
        state.kind !== "resource" &&
        state.ownerId === this.snapshot.you.id
      ) {
        this.selectedIds.add(id);
      }
    });
    this.refreshSelection();
  }

  private centerOnSelection(): void {
    const states = [...this.selectedIds]
      .map((id) => this.entityStates.get(id))
      .filter(Boolean) as RenderState[];
    if (states.length === 0) return;
    this.cameraTarget.x =
      states.reduce((sum, state) => sum + state.x, 0) / states.length;
    this.cameraTarget.z =
      states.reduce((sum, state) => sum + state.z, 0) / states.length;
  }

  private raycast(event: {
    clientX: number;
    clientY: number;
  }): THREE.Intersection[] {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    return this.raycaster.intersectObjects(this.scene.children, true);
  }

  private contextTarget(event: {
    clientX: number;
    clientY: number;
  }): RenderState | undefined {
    const directTargets: RenderState[] = [];
    const seen = new Set<string>();
    for (const intersection of this.raycast(event)) {
      const id = this.findEntityId(intersection.object);
      if (!id || seen.has(id)) continue;
      const state = this.entityStates.get(id);
      if (!state) continue;
      seen.add(id);
      directTargets.push(state);
    }

    const directEnemyUnit = directTargets.find(
      (state) =>
        state.kind === "unit" &&
        state.ownerId !== this.snapshot.you.id,
    );
    if (directEnemyUnit) return directEnemyUnit;

    const hasVillager = [...this.selectedIds].some((id) => {
      const state = this.entityStates.get(id);
      return state?.kind === "unit" && state.unitType === "villager";
    });
    const hasMilitary = [...this.selectedIds].some((id) => {
      const state = this.entityStates.get(id);
      return state?.kind === "unit" && state.unitType !== "villager";
    });

    if (hasMilitary) {
      const nearbyEnemyUnit = this.nearestScreenTarget(
        event,
        (state) =>
          state.kind === "unit" &&
          state.ownerId !== this.snapshot.you.id,
        46,
      );
      if (nearbyEnemyUnit) return nearbyEnemyUnit;
    }

    if (hasVillager) {
      const canWork = (state: RenderState) =>
        state.kind === "resource" ||
        (state.kind === "building" &&
          state.ownerId === this.snapshot.you.id &&
          (!state.completed ||
            state.buildingType === "farm" ||
            state.hp < state.maxHp));
      const directWorkTarget = directTargets.find(canWork);
      if (directWorkTarget) return directWorkTarget;
      const nearbyWorkTarget = this.nearestScreenTarget(
        event,
        canWork,
        52,
      );
      if (nearbyWorkTarget) return nearbyWorkTarget;
    }

    const directEnemyBuilding = directTargets.find(
      (state) =>
        state.kind === "building" &&
        state.ownerId !== this.snapshot.you.id,
    );
    if (directEnemyBuilding) return directEnemyBuilding;

    const hasSelectedUnit = [...this.selectedIds].some(
      (id) => this.entityStates.get(id)?.kind === "unit",
    );
    if (hasSelectedUnit) {
      const rect = this.renderer.domElement.getBoundingClientRect();
      let nearest:
        | { state: UnitState | BuildingState; distance: number }
        | undefined;
      for (const state of this.entityStates.values()) {
        if (
          state.kind === "resource" ||
          state.ownerId === this.snapshot.you.id
        ) {
          continue;
        }
        const height =
          state.kind === "unit"
            ? 1.8
            : BUILDING_DEFS[state.buildingType].size * 0.65;
        const projected = new THREE.Vector3(state.x, height, state.z).project(
          this.camera,
        );
        if (projected.z < -1 || projected.z > 1) continue;
        const screenX = rect.left + ((projected.x + 1) / 2) * rect.width;
        const screenY = rect.top + ((1 - projected.y) / 2) * rect.height;
        const distance = Math.hypot(
          event.clientX - screenX,
          event.clientY - screenY,
        );
        const snapRadius =
          state.kind === "unit"
            ? 46
            : 48 + BUILDING_DEFS[state.buildingType].size * 4;
        if (distance > snapRadius || (nearest && distance >= nearest.distance)) {
          continue;
        }
        nearest = { state, distance };
      }
      if (nearest) return nearest.state;
    }

    return directTargets[0];
  }

  private nearestScreenTarget(
    event: { clientX: number; clientY: number },
    predicate: (state: RenderState) => boolean,
    snapRadius: number,
  ): RenderState | undefined {
    const rect = this.renderer.domElement.getBoundingClientRect();
    let nearest: { state: RenderState; distance: number } | undefined;
    for (const state of this.entityStates.values()) {
      if (!predicate(state)) continue;
      const height =
        state.kind === "unit"
          ? 1.8
          : state.kind === "building"
            ? BUILDING_DEFS[state.buildingType].size * 0.65
            : 1.2;
      const projected = new THREE.Vector3(state.x, height, state.z).project(
        this.camera,
      );
      if (projected.z < -1 || projected.z > 1) continue;
      const screenX = rect.left + ((projected.x + 1) / 2) * rect.width;
      const screenY = rect.top + ((1 - projected.y) / 2) * rect.height;
      const distance = Math.hypot(
        event.clientX - screenX,
        event.clientY - screenY,
      );
      if (distance > snapRadius || (nearest && distance >= nearest.distance)) {
        continue;
      }
      nearest = { state, distance };
    }
    return nearest?.state;
  }

  private setHoveredTarget(id: string | undefined): void {
    if (this.hoveredId === id) return;
    if (this.hoveredId) {
      const previous = this.entityObjects
        .get(this.hoveredId)
        ?.getObjectByName("hover-ring");
      if (previous) previous.visible = false;
    }
    this.hoveredId = id;
    if (!id) return;
    const next = this.entityObjects.get(id)?.getObjectByName("hover-ring");
    if (next) next.visible = true;
  }

  private groundPoint(event: PointerEvent): THREE.Vector3 | undefined {
    return this.raycast(event).find(
      (intersection) =>
        intersection.object === this.ground ||
        intersection.object.userData.ground,
    )?.point;
  }

  private findEntityId(object: THREE.Object3D): string | undefined {
    let current: THREE.Object3D | null = object;
    while (current) {
      if (current.userData.entityId) return current.userData.entityId as string;
      current = current.parent;
    }
    return undefined;
  }

  private setupFog(mapSize: number): void {
    this.fogCanvas.width = 512;
    this.fogCanvas.height = 512;
    this.fogTexture = new THREE.CanvasTexture(this.fogCanvas);
    this.fogTexture.minFilter = THREE.LinearFilter;
    const material = new THREE.MeshBasicMaterial({
      map: this.fogTexture,
      transparent: true,
      depthWrite: false,
    });
    const fogPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(mapSize, mapSize),
      material,
    );
    fogPlane.rotation.x = -Math.PI / 2;
    fogPlane.position.set(mapSize / 2, 0.08, mapSize / 2);
    fogPlane.renderOrder = 8;
    fogPlane.userData.ground = true;
    this.scene.add(fogPlane);
  }

  private drawFog(): void {
    const context = this.fogCanvas.getContext("2d")!;
    const scale = this.fogCanvas.width / this.snapshot.mapSize;
    context.clearRect(0, 0, this.fogCanvas.width, this.fogCanvas.height);
    if (this.snapshot.phase === "finished") {
      this.fogTexture!.needsUpdate = true;
      return;
    }
    context.fillStyle = "rgba(7, 10, 8, 0.90)";
    context.fillRect(0, 0, this.fogCanvas.width, this.fogCanvas.height);
    context.fillStyle = "rgba(7, 10, 8, 0.48)";
    for (const cell of this.exploredCells) {
      const [x, z] = cell.split(",").map(Number);
      context.fillRect(
        x * VISION_CELL_SIZE * scale - 2,
        z * VISION_CELL_SIZE * scale - 2,
        VISION_CELL_SIZE * scale + 4,
        VISION_CELL_SIZE * scale + 4,
      );
    }
    context.globalCompositeOperation = "destination-out";
    for (const cell of this.snapshot.visibleCells) {
      const [x, z] = cell.split(",").map(Number);
      context.fillRect(
        x * VISION_CELL_SIZE * scale - 3,
        z * VISION_CELL_SIZE * scale - 3,
        VISION_CELL_SIZE * scale + 6,
        VISION_CELL_SIZE * scale + 6,
      );
    }
    context.globalCompositeOperation = "source-over";
    this.fogTexture!.needsUpdate = true;
  }

  private drawMinimap(): void {
    const context = this.minimapContext;
    const size = minimap.width;
    const mapSize = this.snapshot.mapSize;
    const project = (x: number, z: number) => ({
      x: size / 2 + ((x - z) / mapSize) * (size / 2),
      y: ((x + z) / mapSize) * (size / 2),
    });
    const fillWorldTile = (
      x: number,
      z: number,
      width: number,
      color: string,
    ) => {
      const corners = [
        project(x, z),
        project(x + width, z),
        project(x + width, z + width),
        project(x, z + width),
      ];
      context.fillStyle = color;
      context.beginPath();
      context.moveTo(corners[0].x, corners[0].y);
      for (const corner of corners.slice(1)) context.lineTo(corner.x, corner.y);
      context.closePath();
      context.fill();
    };
    const drawMarker = (
      x: number,
      z: number,
      radius: number,
      color: string,
    ) => {
      const point = project(x, z);
      context.fillStyle = color;
      context.beginPath();
      context.arc(point.x, point.y, radius, 0, Math.PI * 2);
      context.fill();
    };

    context.fillStyle = "#11151b";
    context.fillRect(0, 0, size, size);
    const mapCorners = [
      project(0, 0),
      project(mapSize, 0),
      project(mapSize, mapSize),
      project(0, mapSize),
    ];
    context.fillStyle = "#20262d";
    context.beginPath();
    context.moveTo(mapCorners[0].x, mapCorners[0].y);
    for (const corner of mapCorners.slice(1)) context.lineTo(corner.x, corner.y);
    context.closePath();
    context.fill();

    for (const cell of this.exploredCells) {
      const [x, z] = cell.split(",").map(Number);
      fillWorldTile(
        x * VISION_CELL_SIZE,
        z * VISION_CELL_SIZE,
        VISION_CELL_SIZE,
        "#53694d",
      );
    }
    for (const tile of this.snapshot.terrainTiles) {
      const cell = `${Math.floor(tile.x / TERRAIN_TILE_SIZE)},${Math.floor(tile.z / TERRAIN_TILE_SIZE)}`;
      if (!this.exploredCells.has(cell)) continue;
      fillWorldTile(
        tile.x - TERRAIN_TILE_SIZE / 2,
        tile.z - TERRAIN_TILE_SIZE / 2,
        TERRAIN_TILE_SIZE,
        tile.kind === "water" ? "#69b7d2" : "#bb8a45",
      );
    }
    if (this.snapshot.hill) fillWorldTile(56, 56, 8, "#e5be65");
    const resourceColors: Record<ResourceKind, string> = {
      food: "#bb6d5f",
      wood: "#254e2e",
      gold: "#e6bd43",
      stone: "#a5a7a2",
    };
    const rememberedStates = [...this.entityStates.values()];
    for (const resource of rememberedStates.filter(
      (state): state is ResourceNodeState => state.kind === "resource",
    )) {
      drawMarker(
        resource.x,
        resource.z,
        1.5,
        resourceColors[resource.resourceType],
      );
    }
    for (const building of rememberedStates.filter(
      (state): state is BuildingState => state.kind === "building",
    )) {
      const player = this.snapshot.players.find(
        (candidate) => candidate.id === building.ownerId,
      );
      drawMarker(building.x, building.z, 2.5, player?.color ?? "#fff");
    }
    for (const unit of this.snapshot.units) {
      const player = this.snapshot.players.find(
        (candidate) => candidate.id === unit.ownerId,
      );
      drawMarker(unit.x, unit.z, 1.5, player?.color ?? "#fff");
    }
    const viewWidth = 18 * (this.cameraDistance / 42);
    minimap.dataset.cameraX = this.cameraTarget.x.toFixed(2);
    minimap.dataset.cameraZ = this.cameraTarget.z.toFixed(2);
    context.strokeStyle = "#f7e2a4";
    context.lineWidth = 1;
    const viewCorners = [
      project(
        this.cameraTarget.x - viewWidth / 2,
        this.cameraTarget.z - viewWidth / 2,
      ),
      project(
        this.cameraTarget.x + viewWidth / 2,
        this.cameraTarget.z - viewWidth / 2,
      ),
      project(
        this.cameraTarget.x + viewWidth / 2,
        this.cameraTarget.z + viewWidth / 2,
      ),
      project(
        this.cameraTarget.x - viewWidth / 2,
        this.cameraTarget.z + viewWidth / 2,
      ),
    ];
    context.beginPath();
    context.moveTo(viewCorners[0].x, viewCorners[0].y);
    for (const corner of viewCorners.slice(1)) {
      context.lineTo(corner.x, corner.y);
    }
    context.closePath();
    context.stroke();
  }

  private addMapBorder(mapSize: number): void {
    const borderMaterial = new THREE.MeshStandardMaterial({
      color: 0x3f5b37,
      roughness: 1,
    });
    const geometry = new THREE.BoxGeometry(mapSize + 4, 1.4, 2);
    const positions = [
      [mapSize / 2, 0.4, -1],
      [mapSize / 2, 0.4, mapSize + 1],
    ];
    positions.forEach(([x, y, z]) => {
      const border = new THREE.Mesh(geometry, borderMaterial);
      border.position.set(x, y, z);
      border.receiveShadow = true;
      this.scene.add(border);
    });
    const sideGeometry = new THREE.BoxGeometry(2, 1.4, mapSize + 4);
    [ -1, mapSize + 1 ].forEach((x) => {
      const border = new THREE.Mesh(sideGeometry, borderMaterial);
      border.position.set(x, 0.4, mapSize / 2);
      border.receiveShadow = true;
      this.scene.add(border);
    });
  }

  private resize(): void {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  private animateUnitActions(time: number): void {
    const seconds = time / 1000;
    for (const [id, state] of this.entityStates) {
      const object = this.entityObjects.get(id);
      if (state.kind === "building") {
        const constructionBadge = object?.getObjectByName(
          "construction-badge",
        ) as THREE.Sprite | undefined;
        if (constructionBadge && !state.completed) {
          const pulse = 1.72 + Math.sin(seconds * 4.5) * 0.16;
          constructionBadge.scale.set(pulse, pulse, 1);
          constructionBadge.position.x = Math.sin(seconds * 2.6) * 0.12;
          constructionBadge.material.rotation = Math.sin(seconds * 3.2) * 0.09;
        }
        const repairBadge = object?.getObjectByName(
          "repair-badge",
        ) as THREE.Sprite | undefined;
        if (repairBadge?.visible) {
          const pulse = 1.68 + Math.sin(seconds * 4.8) * 0.14;
          repairBadge.scale.set(pulse, pulse, 1);
          repairBadge.position.x = Math.sin(seconds * 2.9) * 0.12;
          repairBadge.material.rotation = Math.sin(seconds * 3.5) * 0.1;
        }
        continue;
      }
      if (state.kind !== "unit") continue;
      const visual = object?.getObjectByName("actor-visual");
      const squiggle = object?.getObjectByName(
        "action-squiggle",
      ) as THREE.Sprite | undefined;
      if (!visual || !squiggle) continue;
      const actorSprite = visual.children.find(
        (child): child is THREE.Sprite => child instanceof THREE.Sprite,
      );

      const phase =
        [...id.slice(-8)].reduce(
          (sum, character) => sum + character.charCodeAt(0),
          0,
        ) * 0.017;
      visual.position.set(0, 0, 0);
      visual.rotation.set(0, 0, 0);
      visual.scale.setScalar(1);
      if (actorSprite) actorSprite.material.rotation = 0;
      squiggle.visible = false;

      const throwingArm = visual.getObjectByName("onager-arm");
      const action = object?.userData.renderedState ?? state.state;
      animateUnitModel(visual, state.unitType, action, seconds, phase,
        (time - (object?.userData.strikeAt ?? -10000)) / 1000, state.workActivity ?? state.carriedResource);

      if (action === "idle") {
        const breath = 1 + Math.sin(seconds * 2.1 + phase) * 0.018;
        visual.scale.set(breath, 1 / breath, breath);
        continue;
      }

      if (action === "moving") {
        const stride = seconds * 9 + phase;
        const siege = state.unitType === "bombard_cannon" || state.unitType === "onager";
        visual.position.y = Math.abs(Math.sin(stride)) * (siege ? .025 : .06);
        visual.rotation.z = Math.sin(stride) * (siege ? .006 : .025);
        if (actorSprite) actorSprite.material.rotation = Math.sin(stride) * 0.055;
        visual.scale.y = 1;
        continue;
      }

      const workPhase = seconds * 8.5 + phase;
      // Tool/limb poses now communicate work without a large floating squiggle.
      squiggle.visible = false;
      squiggle.position.x = 0.9 + Math.sin(workPhase * 0.7) * 0.18;
      squiggle.position.y = 2.85 + Math.sin(workPhase) * 0.14;
      const pulse = 1.65 + (Math.sin(workPhase * 1.4) + 1) * 0.18;
      squiggle.scale.set(pulse, pulse, 1);
      squiggle.material.rotation = Math.sin(workPhase * 0.55) * 0.18;
      squiggle.material.opacity = 0.68 + Math.sin(workPhase * 1.7) * 0.2;

      if (action === "gathering") {
        visual.rotation.z = Math.sin(workPhase) * 0.035;
        if (actorSprite) actorSprite.material.rotation = Math.sin(workPhase) * 0.14;
        visual.position.y = Math.abs(Math.sin(workPhase)) * 0.07;
        squiggle.material.color.setHex(0xf2cf69);
      } else if (action === "building" || action === "repairing") {
        visual.rotation.z = Math.sin(workPhase * 1.15) * 0.035;
        if (actorSprite) {
          actorSprite.material.rotation = Math.sin(workPhase * 1.15) * 0.18;
        }
        visual.position.x = Math.sin(workPhase * 0.55) * 0.08;
        squiggle.material.color.setHex(
          action === "repairing" ? 0xe7b95b : 0x71d6de,
        );
      } else if (action === "attacking") {
        if (throwingArm) { squiggle.visible = false; continue; }
        const strikeAge = (time - (object?.userData.strikeAt ?? -10000)) / 300;
        const lunge = strikeAge >= 0 && strikeAge <= 1 ? Math.sin(strikeAge * Math.PI) : 0;
        visual.position.z = lunge * 0.38;
        visual.rotation.z = lunge * 0.1;
        if (actorSprite) actorSprite.material.rotation = Math.sin(workPhase) * 0.1;
        visual.scale.set(1 + lunge * 0.08, 1 - lunge * 0.04, 1);
        squiggle.position.x = 1.05;
        squiggle.visible = false;
        squiggle.material.color.setHex(0xef665c);
      } else {
        squiggle.visible = false;
      }
    }
  }

  private spawnRangedProjectiles(snapshot: GameSnapshot): void {
    const now = performance.now();
    for (const shot of snapshot.combatEvents ?? []) {
      if (shot.id <= this.lastCombatEventId) continue;
      this.lastCombatEventId = shot.id;
      const elapsed = Math.max(0, (snapshot.tick - shot.tick) / SIM_TICK_RATE);
      if (elapsed > Math.max(.4, shot.flightSeconds + .2)) continue;
      const strikingActor = this.entityObjects.get(shot.attackerId);
      if (strikingActor) strikingActor.userData.strikeAt = now - elapsed * 1000;
      if (shot.projectile === "melee") {
        const actor = this.entityObjects.get(shot.attackerId);
        if (actor) actor.userData.strikeAt = now;
        if (shot.ownerId === snapshot.you.id && now - this.lastCombatSoundAt > 180) {
          soundDesign.combat(false);
          this.lastCombatSoundAt = now;
        }
        continue;
      }
      const isBombard = shot.projectile === "cannon";
      const isStone = shot.projectile === "stone";
      if (isStone) {
        const actor = this.entityObjects.get(shot.attackerId);
        if (actor) actor.userData.strikeAt = now - elapsed * 1000;
        if (now - this.lastCombatSoundAt > 180) { soundDesign.onager(); this.lastCombatSoundAt = now; }
      } else if (isBombard) soundDesign.cannon();
      else if (now - this.lastCombatSoundAt > 180) {
        soundDesign.combat(true);
        this.lastCombatSoundAt = now;
      }
      const attacker = this.entityStates.get(shot.attackerId);
      const height = attacker?.kind === "building" ? 3.5 : isBombard ? 1.15 : 1.7;
      const start = new THREE.Vector3(shot.from.x, height, shot.from.z);
      const end = new THREE.Vector3(shot.to.x, isStone ? .12 : 1.1, shot.to.z);
      const mesh = new THREE.Mesh(
        isStone ? new THREE.DodecahedronGeometry(.35, 0) : isBombard ? new THREE.SphereGeometry(.24, 8, 6) : new THREE.BoxGeometry(.07, .07, .8),
        new THREE.MeshBasicMaterial({ color: isStone ? 0xa5a494 : isBombard ? 0x292c31 : 0xf1dfae }));
      mesh.position.copy(start);
      mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), end.clone().sub(start).normalize());
      this.scene.add(mesh);
      const pulse = new THREE.Mesh(isStone ? new THREE.RingGeometry((shot.splashRadius ?? 2.5) * .85, shot.splashRadius ?? 2.5, 32) : new THREE.SphereGeometry(isBombard ? .3 : .16, 6, 4),
        new THREE.MeshBasicMaterial({ color: isStone ? 0xd7c39c : 0xffc36e, transparent: true, opacity: .85, side: THREE.DoubleSide, depthWrite: false }));
      pulse.position.copy(start);
      if (isStone) { pulse.position.copy(end); pulse.rotation.x = -Math.PI / 2; pulse.visible = false; }
      this.scene.add(pulse);
      let fragments: THREE.InstancedMesh | undefined;
      if (isStone) {
        fragments = new THREE.InstancedMesh(new THREE.TetrahedronGeometry(.18),
          new THREE.MeshStandardMaterial({ color: 0xb1aa94, roughness: 1, flatShading: true, transparent: true }), 18);
        fragments.position.copy(end);
        fragments.visible = false;
        fragments.frustumCulled = false;
        this.scene.add(fragments);
      }
      this.projectiles.push({ mesh, pulse, fragments, start, end,
        startedAt: now - Math.min(elapsed, Math.max(0, shot.flightSeconds - .08)) * 1000,
        duration: Math.max(.1, shot.flightSeconds), splashRadius: isStone ? shot.splashRadius ?? 2.5 : undefined });
    }
  }

  private animateProjectiles(time: number): void {
    for (let index = this.projectiles.length - 1; index >= 0; index -= 1) {
      const projectile = this.projectiles[index];
      const progress = (time - projectile.startedAt) / (projectile.duration * 1000);
      if (projectile.splashRadius && progress >= 1 && progress < 1 + .7 / projectile.duration) {
        if (projectile.mesh.visible && time - this.lastCombatSoundAt > 180) {
          soundDesign.onager(true); this.lastCombatSoundAt = time;
        }
        projectile.mesh.visible = false;
        const age = (progress - 1) * projectile.duration / .7;
        if (projectile.fragments) {
          const fragments = projectile.fragments;
          fragments.visible = true;
          const pose = new THREE.Object3D();
          for (let part = 0; part < fragments.count; part++) {
            const angle = part * 2.39996;
            const distance = projectile.splashRadius * (.45 + (part % 5) * .1375) * age;
            pose.position.set(Math.cos(angle) * distance, .08 + Math.sin(age * Math.PI) * (.5 + (part % 4) * .3), Math.sin(angle) * distance);
            pose.rotation.set(age * (part % 3 + 3), angle + age * 4, age * 6);
            pose.scale.setScalar((.6 + (part % 3) * .25) * (1 - age * .5));
            pose.updateMatrix();
            fragments.setMatrixAt(part, pose.matrix);
          }
          fragments.instanceMatrix.needsUpdate = true;
          (fragments.material as THREE.MeshStandardMaterial).opacity = Math.min(1, (1 - age) * 3);
        }
        if (projectile.pulse) {
          projectile.pulse.visible = true;
          (projectile.pulse.material as THREE.MeshBasicMaterial).opacity = .7 * (1 - age);
        }
        continue;
      }
      if (progress >= 1) {
        this.scene.remove(projectile.mesh);
        if (projectile.fragments) {
          this.scene.remove(projectile.fragments);
          projectile.fragments.geometry.dispose();
          (projectile.fragments.material as THREE.Material).dispose();
          projectile.fragments.dispose();
        }
        projectile.mesh.geometry.dispose();
        (projectile.mesh.material as THREE.Material).dispose();
        if (projectile.pulse) {
          this.scene.remove(projectile.pulse);
          projectile.pulse.geometry.dispose();
          (projectile.pulse.material as THREE.Material).dispose();
        }
        this.projectiles.splice(index, 1);
        continue;
      }
      const eased = THREE.MathUtils.clamp(progress, 0, 1);
      projectile.mesh.position.lerpVectors(
        projectile.start,
        projectile.end,
        eased,
      );
      projectile.mesh.position.y += Math.sin(Math.PI * eased) * (projectile.splashRadius ? 4 : .7);
      if (projectile.splashRadius) projectile.mesh.rotation.x += .08;
      if (projectile.pulse && !projectile.splashRadius) {
        const pulseProgress = Math.min(1, eased / 0.38);
        projectile.pulse.scale.setScalar(1 + pulseProgress * 2.6);
        (
          projectile.pulse.material as THREE.MeshBasicMaterial
        ).opacity = 0.9 * (1 - pulseProgress);
        projectile.pulse.visible = pulseProgress < 1;
      }
    }
  }

  private interpolateUnits(time: number): void {
    const motion = this.snapshotMotion.sample(time);
    if (motion) for (const [id, to] of motion.to.units) {
      const object = this.entityObjects.get(id);
      if (!object) continue;
      const from = motion.from.units.get(id) ?? to;
      const amount = motion.amount;
      object.position.x = THREE.MathUtils.lerp(from.x, to.x, amount);
      object.position.z = THREE.MathUtils.lerp(from.z, to.z, amount);
      const angle = Math.atan2(Math.sin(to.facing - from.facing), Math.cos(to.facing - from.facing));
      object.rotation.y = from.facing + angle * amount;
      object.userData.renderedState = amount < 1 && (from.x !== to.x || from.z !== to.z)
        ? "moving" : to.state;
    }
  }

  private render(time: number): void {
    this.interpolateUnits(time);
    const delta = Math.min(0.05, (time - this.lastFrame) / 1000);
    this.lastFrame = time;
    this.fpsFrameCount++;
    const fpsWindow = time - this.fpsSampleStartedAt;
    if (fpsWindow >= 750) {
      const fps = Math.round((this.fpsFrameCount * 1000) / fpsWindow);
      const indicator = document.querySelector("#client-fps-indicator");
      if (indicator) {
        indicator.textContent = `${fps} FPS`;
        indicator.classList.toggle("warning", fps < 42);
      }
      this.fpsFrameCount = 0;
      this.fpsSampleStartedAt = time;
    }
    const cameraSpeed = 22 * delta * (this.cameraDistance / 42);
    const forwardInput =
      (this.keys.has("w") || this.keys.has("arrowup") ? 1 : 0) -
      (this.keys.has("s") || this.keys.has("arrowdown") ? 1 : 0) +
      this.edgePanInput.y;
    const rightInput =
      (this.keys.has("d") || this.keys.has("arrowright") ? 1 : 0) -
      (this.keys.has("a") || this.keys.has("arrowleft") ? 1 : 0) +
      this.edgePanInput.x;
    this.cameraMove
      .copy(this.cameraForward)
      .multiplyScalar(forwardInput)
      .addScaledVector(this.cameraRight, rightInput);
    if (this.cameraMove.lengthSq() > 0) {
      this.cameraMove.normalize().multiplyScalar(cameraSpeed);
      this.cameraTarget.add(this.cameraMove);
    }
    this.cameraTarget.x = THREE.MathUtils.clamp(
      this.cameraTarget.x,
      4,
      this.snapshot.mapSize - 4,
    );
    this.cameraTarget.z = THREE.MathUtils.clamp(
      this.cameraTarget.z,
      4,
      this.snapshot.mapSize - 4,
    );
    this.camera.position.set(
      this.cameraTarget.x + this.cameraDistance * 0.7,
      this.cameraDistance,
      this.cameraTarget.z + this.cameraDistance * 0.72,
    );
    this.camera.lookAt(this.cameraTarget);
    this.destructionEffects.update(this.pausedAnimationTime ?? time);
    for (const [id, damage] of this.buildingDamage) {
      damage.update(this.pausedAnimationTime ?? time, this.visibleDamagedBuildings.has(id));
    }
    this.animateUnitActions(this.pausedAnimationTime ?? time);
    this.animateProjectiles(this.pausedAnimationTime ?? time);
    for (let index = this.orderMarkers.length - 1; index >= 0; index -= 1) {
      const marker = this.orderMarkers[index];
      const elapsed = (performance.now() - marker.startedAt) / 1000;
      if (elapsed >= 0.85) {
        this.scene.remove(marker.mesh);
        marker.mesh.geometry.dispose();
        (marker.mesh.material as THREE.Material).dispose();
        this.orderMarkers.splice(index, 1);
        continue;
      }
      const scale = 1 + elapsed * 2.4;
      marker.mesh.scale.setScalar(scale);
      (marker.mesh.material as THREE.MeshBasicMaterial).opacity =
        0.95 * (1 - elapsed / 0.85);
    }
    this.renderer.render(this.scene, this.camera);
    requestAnimationFrame((nextTime) => this.render(nextTime));
  }
}

function unitCombatLines(type: UnitType): {
  attack: string;
  armour: string;
  bonus: string;
} {
  const definition = UNIT_DEFS[type];
  const ranged = definition.rangedAttack > 0;
  const bonuses = Object.entries(definition.attackBonuses)
    .filter((entry): entry is [string, number] => entry[1] !== undefined)
    .map(([category, value]) => `+${value} against ${category}`)
    .join(", ");
  return {
    attack: `${ranged ? definition.rangedAttack : definition.meleeAttack} ${ranged ? "Ranged" : "Melee"}${definition.splashRadius ? ` · ${definition.splashRadius} splash` : ""}`,
    armour: `${definition.meleeArmour} Melee / ${definition.rangedArmour} Ranged`,
    bonus: bonuses || "None",
  };
}

function unitCombatTooltip(type: UnitType): string {
  const definition = UNIT_DEFS[type];
  const combat = unitCombatLines(type);
  return `Attack: ${combat.attack} | Armour: ${combat.armour} | Bonus: ${combat.bonus} | Strong: ${definition.strongAgainst} | Weak: ${definition.weakAgainst}${definition.splashRadius ? `\nRange: ${definition.minimumRange}–${definition.range} · Reload: ${definition.cooldown}s · Blast radius: ${definition.splashRadius}\n${definition.rangedAttack} attack at centre, falling to ${Math.round(definition.rangedAttack / 2)} at edge, before ranged armour. Hits enemies only. Stones land at the original aim point; moving units can dodge.` : ""}`;
}

function renderUnitCombatProfile(type: UnitType): string {
  const definition = UNIT_DEFS[type];
  const combat = unitCombatLines(type);
  return `<div class="combat-profile">
    <div><b>Attack</b><span>${escapeHtml(combat.attack)}</span></div>
    <div><b>Armour</b><span>${escapeHtml(combat.armour)}</span></div>
    <div><b>Bonus</b><span>${escapeHtml(combat.bonus)}</span></div>
    <div class="combat-matchups"><b>Strong</b><span>${escapeHtml(definition.strongAgainst)}</span><b>Weak</b><span>${escapeHtml(definition.weakAgainst)}</span></div>
    <p>${escapeHtml(definition.lore)}</p>
  </div>`;
}

function actionButton(
  icon: string,
  title: string,
  subtitle: string,
  action: string,
  disabled = false,
  hotkey = "",
  className = "",
  tooltip = subtitle,
): string {
  const caption = title.replace(/^Train |^Advance to /, "")
    .replace("Economic Buildings", "Economy").replace("Military Buildings", "Military")
    .replace("Build Categories", "Back").replace(/^(Move|Set) Rally Flag$/, "Rally Point");
  const help = [title, subtitle, tooltip !== subtitle ? tooltip : "", hotkey ? `Shortcut: ${hotkey}` : ""].filter(Boolean).join("\n");
  return `
    <button class="action-button ${className}" data-action="${action}" ${hotkey ? `data-hotkey="${hotkey}"` : ""} ${disabled ? "disabled" : ""} data-help="${escapeHtml(help)}" aria-label="${escapeHtml(help)}">
      ${hotkey ? `<kbd class="action-hotkey">${hotkey}</kbd>` : ""}
      <b aria-hidden="true">${icon}</b><span><strong>${escapeHtml(caption)}</strong><small>${escapeHtml(subtitle)}</small></span>
    </button>`;
}

// Keep live command nodes stable: replacing innerHTML every snapshot restarts hover.
function reconcileMarkup(container: HTMLElement, html: string): void {
  const template = document.createElement("template");
  template.innerHTML = html;
  function sync(parent: Node, desired: Node): void {
    const oldNodes = [...parent.childNodes];
    [...desired.childNodes].forEach((next, index) => {
      const current = oldNodes[index];
      if (!current) { parent.appendChild(next.cloneNode(true)); return; }
      if (current.nodeName !== next.nodeName ||
          (current instanceof HTMLElement && next instanceof HTMLElement &&
           current.dataset.action !== next.dataset.action)) {
        parent.replaceChild(next.cloneNode(true), current); return;
      }
      if (current instanceof Element && next instanceof Element) {
        for (const attr of [...current.attributes])
          if (!next.hasAttribute(attr.name) && attr.name !== "aria-describedby") current.removeAttribute(attr.name);
        for (const attr of [...next.attributes])
          if (current.getAttribute(attr.name) !== attr.value) current.setAttribute(attr.name, attr.value);
        sync(current, next);
      } else if (current.nodeValue !== next.nodeValue) current.nodeValue = next.nodeValue;
    });
    for (const extra of oldNodes.slice(desired.childNodes.length)) parent.removeChild(extra);
  }
  sync(container, template.content);
}

function buildingHelp(type: BuildingType): string {
  const definition = BUILDING_DEFS[type];
  const half = buildingHalf(type);
  const garrison = type === "tower" ? 5 : type === "castle" || type === "town_center" ? 10 : 0;
  return [
    definition.description,
    type === "camp" ? "Villagers carry up to 15 resources before depositing. Place near deposits to shorten walking; it does not generate resources." : "",
    type === "town_center" ? "Accepts all carried resources. Additional Town Centres require Castle Age." : "",
    definition.trains.length ? `Trains: ${definition.trains.map(t => UNIT_DEFS[t].label).join(", ")}.` : "",
    garrison ? `Shelters ${garrison} units; garrisoned units add defensive shots.` : "",
    `${formatCost(definition.cost)} · ${definition.buildTime}s · ${Math.round(half.x * 2 / NAVIGATION_CELL_SIZE)} × ${Math.round(half.z * 2 / NAVIGATION_CELL_SIZE)} tiles`,
  ].filter(Boolean).join("\n");
}

function buildingLabel(state: BuildingState): string {
  if ((state.buildingType === "wall" || state.buildingType === "gate") && state.maxHp > BUILDING_DEFS[state.buildingType].hp)
    return state.buildingType === "wall" ? "Stone Wall" : "Stone Gate";
  return BUILDING_DEFS[state.buildingType].label;
}
function buildingHalf(type: BuildingType, facing = 0): Vec2 {
  if (type === "wall" || type === "gate") return palisadeHalf(type, facing);
  const half = Math.ceil(BUILDING_DEFS[type].size / NAVIGATION_CELL_SIZE) * NAVIGATION_CELL_SIZE / 2;
  return { x: half, z: half };
}

function snapBuildingPoint(at: Vec2, type: BuildingType): Vec2 {
  const cells = Math.round(buildingHalf(type).x * 2 / NAVIGATION_CELL_SIZE);
  const offset = cells % 2 === 0 ? NAVIGATION_CELL_SIZE / 2 : 0;
  const snap = (value: number) => Math.floor((value - offset) / NAVIGATION_CELL_SIZE + .5) * NAVIGATION_CELL_SIZE + offset;
  return { x: snap(at.x), z: snap(at.z) };
}

function buildingOverlapAt(existing: BuildingState, type: BuildingType, at: Vec2, facing: number): boolean {
  const a = buildingHalf(existing.buildingType, existing.facing), b = buildingHalf(type, facing);
  return Math.abs(existing.x - at.x) < a.x + b.x - .02 && Math.abs(existing.z - at.z) < a.z + b.z - .02;
}

function buildingBlocksResource(type: BuildingType, at: Vec2, facing: number, resource: ResourceNodeState): boolean {
  const half = buildingHalf(type, facing);
  const dx = Math.max(0, Math.abs(resource.x - at.x) - half.x);
  const dz = Math.max(0, Math.abs(resource.z - at.z) - half.z);
  const radius = resource.id.startsWith("forest-") ? 1.15 : resource.resourceType === "wood" ? 2.5 : 1.8;
  return dx * dx + dz * dz < radius ** 2;
}

function resourceGlyph(kind: ResourceKind): string {
  return { food: "●", wood: "♠", gold: "◆", stone: "⬟" }[kind];
}

function palisadeHalf(type: string, facing: number): Vec2 {
  return type === "wall" ? { x: .75, z: .75 } :
    { x: Math.abs(Math.cos(facing)) * 1.5 + .75, z: Math.abs(Math.sin(facing)) * 1.5 + .75 };
}

function palisadeContains(state: BuildingState, at: Vec2): boolean {
  const half = palisadeHalf(state.buildingType, state.facing);
  return Math.abs(state.x - at.x) < half.x && Math.abs(state.z - at.z) < half.z;
}

function palisadesOverlap(state: BuildingState, type: string, at: Vec2, facing: number): boolean {
  const a = palisadeHalf(state.buildingType, state.facing);
  const b = palisadeHalf(type, facing);
  return Math.abs(state.x - at.x) < a.x + b.x - .02 && Math.abs(state.z - at.z) < a.z + b.z - .02;
}

function buildingGlyph(type: BuildingType): string {
  return {
    town_center: "♜",
    house: "⌂",
    barracks: "⚔",
    archery_range: "➶",
    stable: "♞",
    tower: "♖",
    farm: "🌾",
    wall: "▥",
    gate: "⌗",
    market: "⚖",
    camp: "⛺",
    castle: "♜",
  }[type];
}

function unitGlyph(type: UnitType): string {
  return {
    villager: "♟",
    scout: "♞",
    spearman: "⚔",
    archer: "➶",
    skirmisher: "◈",
    knight: "♘",
    royal_guard: "♛",
    bombard_cannon: "◉",
    onager: "☄",
  }[type];
}

function unitRoleCode(type: UnitType): string {
  return {
    villager: "V",
    scout: "SC",
    spearman: "SP",
    archer: "AR",
    skirmisher: "SK",
    knight: "K",
    royal_guard: "RG",
    bombard_cannon: "BC",
    onager: "ON",
  }[type];
}

function controlGroupCode(state: UnitState | BuildingState): string {
  if (state.kind === "unit") return unitRoleCode(state.unitType);
  return {
    town_center: "TC",
    house: "H",
    barracks: "B",
    archery_range: "AR",
    stable: "ST",
    tower: "T",
    farm: "F",
    wall: "W",
    gate: "G",
    market: "M",
    camp: "CP",
    castle: "C",
  }[state.buildingType];
}

function seededVisual(text: string): () => number {
  let state = [...text].reduce(
    (sum, char) => (sum * 31 + char.charCodeAt(0)) >>> 0,
    2166136261,
  );
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x1_0000_0000;
  };
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      })[character]!,
  );
}

function element(id: string): HTMLElement {
  return document.getElementById(id)!;
}

function inputElement(id: string): HTMLInputElement {
  return document.getElementById(id) as HTMLInputElement;
}

function buttonElement(id: string): HTMLButtonElement {
  return document.getElementById(id) as HTMLButtonElement;
}
